import type {
  ApplyActionCounts,
  ApplyDiagnostic,
  ApplyReasonCode,
  ApplyResultSummary,
  DeckLayoutCatalog,
  DeckLayoutCatalogEntry,
  DeckLayoutPlaceholder,
  DeckLayoutUsedBySlide,
  PresentationContent,
  PresentationModification,
  SlideLayoutInfo,
  SlideModification,
  SlideRef,
  ShapeContent,
  ShapeModification,
  TableCellProperties,
  TableData,
  TableStyleSettingsData,
  TableStyleUpdate,
  ThemeColors,
  TitlePlaceholderHint,
} from "../../types";
import {
  buildEmptyTableShellRows,
  buildTableTextUpdatesFromRows,
  mergeTableTextUpdates,
  normalizeTableCellData,
} from "../../utils/tableData";
import {
  extractBorderWeight,
  extractColorToken,
  extractExplicitBorderColor,
  pickFirstNonEmptyString,
} from "../../utils/factsetExtractorFormatting";
import {
  hasAnyImagePayload,
  hasInlineImagePayload,
  normalizeBase64Payload,
  parseBase64DataUri,
} from "../../utils/imagePayload";
import { resolveBrandIconDataUri } from "../ai/ubsIconCatalog";
import {
  classifyApplyResult,
  createActionCounts,
  recordError,
  recordSkipped,
} from "../applyResultSummary";
import { guardedPowerPointRun } from "../officeJsGuard";
import {
  DEFAULT_TIMEOUT_MS,
  DEFAULT_APP_ID,
  raiseIntent,
  getBridgeParentOriginOrThrow,
  generateRespondAt,
  waitForIntentResponse,
  postIntentMessage,
  type IntentAppId,
} from "./factsetIntentTransport";
import { parseJsonInWorker } from "./jsonParseWorker";

export { isFactSetIntentBridgeAvailable } from "./factsetIntentTransport";
export {
  HOST_BORDER_WEIGHT_UNSET,
  extractBorderWeight,
  extractColorToken,
  numericRgbToHexColor,
  pickFirstNonEmptyString,
} from "../../utils/factsetExtractorFormatting";

const DEFAULT_IMAGE_HEIGHT = 1080;
const DEFAULT_UNDO_ENTRY_TEXT = "FactSet AI Chat Apply";
const DEFAULT_SLIDE_WIDTH_PT = 960;
const DEFAULT_SLIDE_HEIGHT_PT = 540;
// Keep FactSet shape bounds behavior aligned with Office.js routing.
const FACTSET_SAFE_MARGIN_PT = 20;
const DEFAULT_BATCH_TIMEOUT_MS = 45_000;
const DEFAULT_BATCH_TIMEOUT_GRACE_MS = 90_000;
const SLIDE_IMAGE_BATCH_SIZE = 20;
const POWERPOINT_INTENT_APP_ID: IntentAppId = { appId: "OI Remote Iframe" };

const BATCH_FAILURE_PATTERN = /(failed|error|not found|invalid|mismatch|exception|unable|unsupported)/i;
const BATCH_SUCCESS_PATTERN = /(success|succeeded|applied|completed)/i;
const BATCH_ZERO_FAILURE_PATTERN = /\b(no\s+(failures?|errors?)|0\s+(failures?|errors?))\b/i;
const SLIDE_INFO_CACHE_TTL_MS = 10_000;
// Short TTL so concurrent callers (e.g. the slide-selection poll firing while
// a chat handler is also asking for the active slide) coalesce onto one
// bridge call. Mutation paths still call resetIntentBridgeCache() with
// clearSelectedSlideRef / clearSlideInfos / clearActiveSlideId set, which
// invalidates this cache, so a real selection change after a deck mutation
// is still picked up immediately.
const SELECTED_SLIDE_REF_CACHE_TTL_MS = 500;
const PRESENTATION_CACHE_TTL_MS = 8_000;
const DESIGN_METADATA_CACHE_TTL_MS = 8_000;
const OFFICE_PAGE_SETUP_CACHE_TTL_MS = 60_000;
// Slide dimensions only change when the user resizes the deck (rare,
// out-of-band action). 30s is short enough to pick up a manual resize on
// the next chat / apply cycle but long enough that the resolver short-
// circuits across the chat extract → applyModifications → refiner sequence
// instead of issuing a fresh unfiltered RunOfficeExtractor fallback each
// time. Mutation paths that genuinely require fresh bounds pass
// `clearSlideBounds: true` to `resetIntentBridgeCache()`.
const SLIDE_BOUNDS_CACHE_TTL_MS = 30_000;
const ACTIVE_CONTEXT_INTENT_TIMEOUT_MS = 20_000;
const EXTRACTOR_BOUNDS_INTENT_TIMEOUT_MS = 20_000;
// `GetSlideList` walks every slide on the host process and serializes
// each slide's `tags` dictionary — the per-slide cost grows with both
// slide count and per-slide tag density. Empirically, a 200-slide deck
// with Finster-placed tags spends ~7–9 s host-side on a single call
// (see `debug_log_200_slide.txt`), and concurrent / queued calls can
// push individual responses well beyond the previous 20 s budget. Set
// the cap to 60 s — matching `RunOfficeExtractor` (30 s) with extra
// headroom for the slowest hosts — so large-deck chat-edits don't
// abort with `"Timed out after 20000ms while waiting for ..."`. Small
// decks return in <200 ms; the timeout only matters as an outer bound.
const SLIDE_LIST_INTENT_TIMEOUT_MS = 60_000;
const INTENT_LOG_PREVIEW_MAX_CHARS = 1200;
// P2: above this many bytes in the doubly-encoded extractor `result` string,
// the log-preview path skips the JSON.parse and emits a length-only summary.
// The actual data consumers go through `parseExtractorResultAsync` which
// hands the parse to a Worker; doing the same parse a second time on the
// main thread just to log five stats would defeat the off-thread win.
const LOG_PREVIEW_EXTRACTOR_PARSE_MAX_BYTES = 200_000;

type IntentId =
  | "getSelectedShapes"
  | "getSlideList"
  | "getSlideCount"
  | "getActivePresentationContext"
  | "getSlideImage"
  | "getSlideImages"
  | "deleteSlides"
  | "exportPresentation"
  | "insertSlidesFromBase64"
  | "insertSlidesFromBase64WithSourceFormatting"
  | "writeFileFromBase64"
  | "addImageFromBase64"
  | "runOfficeExtractor"
  | "beginUndo"
  | "endUndo"
  | "performBatch"
  | "addSlide"
  | "addSlideTags"
  | "setSelectedSlides"
  | "activatePresentation"
  | "openPresentationFromBase64"
  | "getPreslinkFilePaths"
  | "fetchPreslinkFiles";

interface IntentDefinition {
  intentName: string;
  contextType: string;
}

const INTENT_DEFS: Record<IntentId, IntentDefinition> = {
  getSelectedShapes: {
    intentName: "factset.Presentation.GetSelectedShapes",
    contextType: "fdc3.nothing",
  },
  getSlideList: {
    intentName: "factset.Presentation.GetSlideList",
    contextType: "factset.presentation.getSlideList",
  },
  getSlideCount: {
    intentName: "factset.Presentation.GetSlideCount",
    contextType: "fdc3.nothing",
  },
  getActivePresentationContext: {
    intentName: "factset.Presentation.GetActivePresentationContext",
    contextType: "fdc3.nothing",
  },
  getSlideImage: {
    intentName: "factset.Presentation.GetSlideImageAsBase64",
    contextType: "factset.presentation.getSlideImage",
  },
  getSlideImages: {
    intentName: "factset.Presentation.GetSlideImagesAsBase64",
    contextType: "factset.presentation.getSlideImages",
  },
  deleteSlides: {
    intentName: "factset.Presentation.DeleteSlides",
    contextType: "factset.presentation.deleteSlides",
  },
  exportPresentation: {
    intentName: "factset.Presentation.ExportPresentation",
    contextType: "factset.presentation.exportPresentation",
  },
  insertSlidesFromBase64: {
    intentName: "factset.Presentation.InsertSlidesFromBase64",
    contextType: "factset.presentation.insertSlidesFromBase64",
  },
  insertSlidesFromBase64WithSourceFormatting: {
    intentName: "factset.Presentation.InsertSlidesFromBase64WithSourceFormatting",
    contextType: "factset.presentation.insertSlidesFromBase64WithSourceFormatting",
  },
  writeFileFromBase64: {
    intentName: "factset.writeFileFromBase64",
    contextType: "factset.writeFileFromBase64",
  },
  addImageFromBase64: {
    intentName: "factset.Presentation.AddImageFromBase64",
    contextType: "factset.presentation.base64Image",
  },
  runOfficeExtractor: {
    intentName: "factset.Presentation.RunOfficeExtractor",
    contextType: "factset.presentation.runOfficeExtractor",
  },
  beginUndo: {
    intentName: "factset.Presentation.BeginUndo",
    contextType: "fdc3.nothing",
  },
  endUndo: {
    intentName: "factset.Presentation.EndUndo",
    contextType: "factset.presentation.endUndo",
  },
  performBatch: {
    intentName: "factset.Presentation.PerformBatchPptOperations",
    contextType: "factset.performBatchPptOperations",
  },
  addSlide: {
    intentName: "factset.Presentation.AddSlide",
    contextType: "factset.presentation.addSlide",
  },
  addSlideTags: {
    intentName: "factset.Presentation.AddSlideTags",
    contextType: "factset.presentation.addSlideTags",
  },
  setSelectedSlides: {
    intentName: "factset.Presentation.SetSelectedSlides",
    contextType: "factset.presentation.setSelectedSlides",
  },
  activatePresentation: {
    intentName: "factset.Presentation.ActivatePresentation",
    contextType: "factset.presentation.activatePresentation",
  },
  openPresentationFromBase64: {
    intentName: "factset.Presentation.OpenPresentationFromBase64",
    contextType: "factset.presentation.openPresentationFromBase64",
  },
  getPreslinkFilePaths: {
    intentName: "factset.Presentation.GetPreslinkFilePaths",
    contextType: "factset.presentation.getPreslinkFilePaths",
  },
  fetchPreslinkFiles: {
    intentName: "factset.Presentation.FetchPreslinkFiles",
    contextType: "factset.presentation.fetchPreslinkFiles",
  },
};

// Intents that accept an optional (or required, for ActivatePresentation)
// `presentationRef` field per the FactSet PowerPoint intents documentation
// (f2_docs/powerpoint/powerpoint-tools.md).
// BeginUndo and SetSelectedSlides intentionally OMITTED: BeginUndo punctuates
// the application-wide undo stack and naming is scoped via EndUndo only;
// SetSelectedSlides simply does not accept the field per the spec.
const PRESENTATION_REF_INTENTS = new Set<IntentId>([
  "getSlideList",
  "getSlideImages",
  "deleteSlides",
  "exportPresentation",
  "insertSlidesFromBase64",
  "insertSlidesFromBase64WithSourceFormatting",
  "addImageFromBase64",
  "runOfficeExtractor",
  "endUndo",
  "performBatch",
  "addSlide",
  "addSlideTags",
  "activatePresentation",
  "getPreslinkFilePaths",
  "fetchPreslinkFiles",
]);

interface SlideInfo {
  slideId: string;
  index: number;
  slideNumber?: number;
  tags?: Record<string, string>;
}

export interface SlideBounds {
  slideWidth: number;
  slideHeight: number;
}

export interface FactSetSlideInfo {
  slideId: string;
  index: number;
  slideNumber?: number;
}

interface SlideInfoLookups {
  byNormalizedId: Map<string, SlideInfo>;
  byIndex: Map<number, SlideInfo>;
}

interface ExtractorDecisionSummary {
  slidesProcessed: number;
  shapesProcessed: number;
}

interface BatchDecisionSummary {
  requestedSlides: number;
  requestedShapes: number;
  preparedSlides: number;
  preparedShapes: number;
  skippedByIssue: number;
  lineFallbacksToRectangle: number;
  droppedInvalidFillColor: number;
  droppedInvalidLineColor: number;
  droppedInvalidTextColor: number;
}

interface IntentParityDecisionSummary {
  capturedAt: number;
  extractor: ExtractorDecisionSummary;
  batch: BatchDecisionSummary;
  rationale: string[];
}

interface ActiveSlideContextInfo {
  id?: string;
  index?: number;
  slideNumber?: number;
  slideWidth?: number;
  slideHeight?: number;
  dimensionSource?: string;
  openPresentationId?: string;
  presentationName?: string;
  presentationFullName?: string;
}

export interface FactSetPresentationRef {
  openPresentationId?: string;
  name?: string;
  fullName?: string;
}

export interface FactSetPreslinkShapeRef {
  id: string;
  slideId: string;
  name?: string;
}

export interface FactSetPreslinkLinkedFilePath {
  filePath: string;
}

export interface FactSetPreslinkFilePathShape {
  shapeId: string;
  files: FactSetPreslinkLinkedFilePath[];
}

export interface FactSetPreslinkFilePathSlide {
  slideId: string;
  shapes: FactSetPreslinkFilePathShape[];
}

export interface GetPreslinkFilePathsResult {
  slides: FactSetPreslinkFilePathSlide[];
}

export interface GetPreslinkFilePathsViaIntentsOptions {
  shapeRefs: FactSetPreslinkShapeRef[];
  presentationRef?: Partial<FactSetPresentationRef> | null;
  log?: Logger;
  timeoutMs?: number;
}

export interface FactSetPreslinkPresentationFile {
  presentationId?: string;
  zipBase64: string;
  fileName?: string;
}

export interface FactSetPreslinkLinkedFile {
  linkKey: string;
  zipBase64: string;
  fileName?: string;
}

export interface FactSetPreslinkShapeLinkKeys {
  shapeId: string;
  linkKeys: string[];
}

export interface FactSetPreslinkFilesSlide {
  slideId: string;
  shapes: FactSetPreslinkShapeLinkKeys[];
}

export interface FetchPreslinkFilesResult {
  presentation: FactSetPreslinkPresentationFile;
  linkedFiles: FactSetPreslinkLinkedFile[];
  slides: FactSetPreslinkFilesSlide[];
}

export interface FetchPreslinkFilesViaIntentsOptions {
  shapeRefs: FactSetPreslinkShapeRef[];
  presentationRef?: Partial<FactSetPresentationRef> | null;
  includePptFile?: boolean;
  log?: Logger;
  timeoutMs?: number;
}

export interface ActivePresentationContextViaIntents {
  presentationRef: FactSetPresentationRef | null;
  selectedSlideRef: SlideRef | null;
}

interface IntentBridgeCacheState {
  slideInfos?: SlideInfo[];
  slideInfosFetchedAt: number;
  activeSlideId?: string;
  activeSlideIdFetchedAt: number;
  selectedSlideRef?: SlideRef;
  selectedSlideRefFetchedAt: number;
  presentation?: PresentationContent;
  presentationFetchedAt: number;
  slideBounds?: SlideBounds;
  slideBoundsFetchedAt: number;
}

const intentBridgeCache: IntentBridgeCacheState = {
  slideInfosFetchedAt: 0,
  activeSlideIdFetchedAt: 0,
  selectedSlideRefFetchedAt: 0,
  presentationFetchedAt: 0,
  slideBoundsFetchedAt: 0,
};

let inFlightSlideInfos: Promise<SlideInfo[]> | null = null;
let inFlightActiveSlideRefFromContext: Promise<ActiveSlideContextInfo | null> | null = null;
let inFlightSelectedSlideRef: Promise<SlideRef> | null = null;
let inFlightPresentation: Promise<PresentationContent> | null = null;
let inFlightDesignMetadata: Promise<FactSetDesignMetadata> | null = null;
let inFlightDeckLayoutCatalog: Promise<DeckLayoutCatalog> | null = null;
let inFlightPromptDesignContext: Promise<PromptDesignContext> | null = null;
let lastSlideBoundsResolutionSource: string | null = null;
let hasLoggedIntentMapping = false;
let cachedDesignMetadata: FactSetDesignMetadata | null = null;
let cachedDesignMetadataFetchedAt = 0;
let cachedDeckLayoutCatalog: DeckLayoutCatalog | null = null;
let cachedDeckLayoutCatalogFetchedAt = 0;
let lastIntentParityDecisionSummary: IntentParityDecisionSummary = {
  capturedAt: 0,
  extractor: {
    slidesProcessed: 0,
    shapesProcessed: 0,
  },
  batch: {
    requestedSlides: 0,
    requestedShapes: 0,
    preparedSlides: 0,
    preparedShapes: 0,
    skippedByIssue: 0,
    lineFallbacksToRectangle: 0,
    droppedInvalidFillColor: 0,
    droppedInvalidLineColor: 0,
    droppedInvalidTextColor: 0,
  },
  rationale: [],
};

function buildIntentParityRationale(summary: IntentParityDecisionSummary): string[] {
  const rationale: string[] = [];
  const batch = summary.batch;

  rationale.push(
    "RunOfficeExtractor output is normalized before prompting to match Office-style context semantics."
  );

  rationale.push(
    "FactSet batch payload applies host-safe normalization (shape IDs, bounds, colors, and fallback styles) before PerformBatchPptOperations."
  );
  if (batch.lineFallbacksToRectangle > 0) {
    rationale.push(
      `Converted ${batch.lineFallbacksToRectangle} line-like create op(s) to rectangle fallback for host compatibility, rendering the line via solid fill so it stays visually thin regardless of host fill defaults.`
    );
  }
  const droppedInvalidColors =
    batch.droppedInvalidFillColor + batch.droppedInvalidLineColor + batch.droppedInvalidTextColor;
  if (droppedInvalidColors > 0) {
    rationale.push(
      `Dropped ${droppedInvalidColors} invalid color token(s) that the host would otherwise ignore and replace with theme defaults.`
    );
  }
  if (batch.skippedByIssue > 0) {
    rationale.push(
      `Skipped ${batch.skippedByIssue} operation(s) during batch preparation with explicit diagnostics instead of applying unsafe or ambiguous mutations.`
    );
  }
  return rationale;
}

function updateIntentParityDecisionSummary(
  updates: Partial<Pick<IntentParityDecisionSummary, "extractor" | "batch">>
): void {
  lastIntentParityDecisionSummary = {
    capturedAt: nowMs(),
    extractor: updates.extractor ?? lastIntentParityDecisionSummary.extractor,
    batch: updates.batch ?? lastIntentParityDecisionSummary.batch,
    rationale: [],
  };
  lastIntentParityDecisionSummary.rationale = buildIntentParityRationale(lastIntentParityDecisionSummary);
}

interface ContextSlideDimensionMetadata {
  width: number;
  height: number;
  source: string;
  capturedAt: number;
}

let lastOfficePageSetupDimensionMetadata: ContextSlideDimensionMetadata | null = null;

interface ExtractorFontLite {
  size?: number;
  name?: string;
  color?: unknown;
  fill?: unknown;
  bold?: boolean;
  italic?: boolean;
}

interface ExtractorRunFormatLite {
  font?: ExtractorFontLite;
}

interface ExtractorRunLite {
  text?: string;
  runFormat?: ExtractorRunFormatLite;
}

interface ExtractorBulletFormatLite {
  visible?: boolean;
  type?: unknown;
  style?: unknown;
}

interface ExtractorParagraphFormatLite {
  alignment?: unknown;
  bulletFormat?: ExtractorBulletFormatLite;
  indentLevel?: unknown;
}

interface ExtractorParagraphLite {
  index?: number;
  text?: string;
  runs?: ExtractorRunLite[];
  paragraphFormat?: ExtractorParagraphFormatLite;
}

interface ExtractorTextFrameLite {
  text?: string;
  paragraphs?: ExtractorParagraphLite[];
  textFrameFormat?: {
    verticalAnchor?: unknown;
  };
}

interface ExtractorPlaceholderFormatLite {
  type?: string;
  name?: string;
  containedType?: string;
  index?: number | string;
}

interface ExtractorShapeLite {
  id?: string;
  shapeId?: string;
  shapeID?: string;
  objectId?: string;
  name?: string;
  shapeName?: string;
  type?: string;
  shapeType?: string;
  geometricShapeType?: string;
  geometricType?: string;
  autoShapeType?: string;
  autoShapeName?: string;
  text?: string;
  paragraphs?: ExtractorParagraphLite[];
  textFrame?: ExtractorTextFrameLite;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  fillColor?: string;
  lineColor?: string;
  fontSize?: number;
  fontFamily?: string;
  textColor?: string;
  bold?: boolean;
  italic?: boolean;
  tableData?: unknown;
  table?: unknown;
  fill?: unknown;
  border?: unknown;
  line?: unknown;
  textFrameFormat?: {
    verticalAnchor?: unknown;
  };
  placeholderFormat?: ExtractorPlaceholderFormatLite;
  // Child GroupItems. Present on host group shapes (type "Group").
  shapes?: ExtractorShapeLite[];
}

interface ExtractorSlideLite {
  slideId?: number | string;
  slideID?: number | string;
  slideNumber?: number | string;
  index?: number | string;
  shapes?: ExtractorShapeLite[];
  layout?: string; // Slide layout name (e.g. "Title", "Title and Content").
  customLayoutName?: string;
  slideMasterName?: string;
  displayMasterShapes?: boolean;
  followMasterBackground?: boolean;
}

interface ExtractorResultLite {
  slideWidth?: number;
  slideHeight?: number;
  pageWidth?: number;
  pageHeight?: number;
  presentation?: {
    width?: number;
    height?: number;
    slideWidth?: number;
    slideHeight?: number;
    pageWidth?: number;
    pageHeight?: number;
    pageSetup?: {
      slideWidth?: number;
      slideHeight?: number;
      pageWidth?: number;
      pageHeight?: number;
    };
  };
  slide?: {
    width?: number;
    height?: number;
    slideWidth?: number;
    slideHeight?: number;
    pageWidth?: number;
    pageHeight?: number;
    pageSetup?: {
      slideWidth?: number;
      slideHeight?: number;
      pageWidth?: number;
      pageHeight?: number;
    };
  };
  pageSetup?: {
    slideWidth?: number;
    slideHeight?: number;
    pageWidth?: number;
    pageHeight?: number;
  };
  // Some FactSet-host responses (e.g. template files where `slides: []`) carry slide
  // dimensions only on the design / slide-master / custom-layouts. Declared loosely so
  // we can probe both `designs[*].{width,height}` and nested `customLayouts[*]` shapes
  // without coupling to a single host schema.
  designs?: ExtractorDesignLite[];
  slides?: ExtractorSlideLite[];
}

interface ExtractorCustomLayoutLite {
  name?: string;
  matchingName?: string;
  width?: number;
  height?: number;
  pageWidth?: number;
  pageHeight?: number;
  slideWidth?: number;
  slideHeight?: number;
  displayMasterShapes?: boolean;
  followMasterBackground?: boolean;
  background?: unknown;
  headersFooters?: unknown;
  parentMaster?: { name?: string } | ExtractorSlideMasterLite;
  shapes?: ExtractorShapeLite[];
}

interface ExtractorSlideMasterLite extends ExtractorCustomLayoutLite {
  name?: string;
  colorScheme?: Record<string, unknown>;
  colorSchema?: Record<string, unknown>;
  fontScheme?: Record<string, unknown>;
  fontSchema?: Record<string, unknown>;
  customLayouts?: ExtractorCustomLayoutLite[];
}

interface ExtractorDesignLite extends ExtractorCustomLayoutLite {
  colorScheme?: Record<string, unknown>;
  colorSchema?: Record<string, unknown>;
  fontScheme?: Record<string, unknown>;
  fontSchema?: Record<string, unknown>;
  customLayouts?: ExtractorCustomLayoutLite[];
  slideMaster?: ExtractorSlideMasterLite;
}

interface FactSetDesignMetadata {
  themeColors: ThemeColors | null;
  layoutInfo: SlideLayoutInfo | null;
}

export interface PromptDesignContext {
  themeColors: ThemeColors | null;
  deckLayoutCatalog: DeckLayoutCatalog | null;
}

export type RunOfficeExtractorSlideRangeFilter = { start: number; end?: number } | { id: string };

export interface RunOfficeExtractorShapeFilter {
  id: string;
  slideId: string;
  name?: string;
}

// =============================================================================
// f2_docs RunOfficeExtractor filter schema
// -----------------------------------------------------------------------------
// Mirrors `f2_docs/powerpoint/office-extractor/filters/README.md` (commit
// 4c122af). The host walks this object section-by-section to decide what to
// include in the response. A `BasicFilter` is `true | false | { include }`;
// container nodes accept either a boolean or an object that further narrows
// their children. Empty `filters: {}` means "extract everything" (recipe 1 in
// the spec) — same as omitting the field. See `extractPresentationContentViaIntents`
// for the wire-level integration and the Wave 2 sites in `perf_improvements.md`
// for the concrete recipes we use.
// =============================================================================

export type RunOfficeExtractorBasicFilter = boolean | { include?: boolean };

export interface RunOfficeExtractorShapeSectionFilter {
  include?: boolean;
  fill?: RunOfficeExtractorBasicFilter;
  border?: RunOfficeExtractorBasicFilter;
  tags?: RunOfficeExtractorBasicFilter;
  glow?: RunOfficeExtractorBasicFilter;
  reflection?: RunOfficeExtractorBasicFilter;
  shadow?: RunOfficeExtractorBasicFilter;
  softEdge?: RunOfficeExtractorBasicFilter;
  callout?: RunOfficeExtractorBasicFilter;
  textEffectFormat?: RunOfficeExtractorBasicFilter;
  pictureFormat?: RunOfficeExtractorBasicFilter;
  chart?: RunOfficeExtractorBasicFilter;
  connectorFormat?: RunOfficeExtractorBasicFilter;
  placeholderFormat?: RunOfficeExtractorBasicFilter;
  textFrame?: boolean | RunOfficeExtractorTextFrameFilter;
  table?: boolean | RunOfficeExtractorTableFilter;
}

export interface RunOfficeExtractorTextFrameFilter {
  include?: boolean;
  text?: RunOfficeExtractorBasicFilter;
  textFrameFormat?: RunOfficeExtractorBasicFilter;
  paragraphs?:
    | boolean
    | {
        include?: boolean;
        text?: RunOfficeExtractorBasicFilter;
        paragraphFormat?: RunOfficeExtractorBasicFilter;
        runs?:
          | boolean
          | {
              include?: boolean;
              text?: RunOfficeExtractorBasicFilter;
              runFormat?: RunOfficeExtractorBasicFilter;
            };
      };
}

export interface RunOfficeExtractorTableFilter {
  include?: boolean;
  range?: {
    startRowIndex?: number;
    endRowIndex?: number;
    startColumnIndex?: number;
    endColumnIndex?: number;
  };
  background?: RunOfficeExtractorBasicFilter;
  cell?: {
    include?: boolean;
    borders?: RunOfficeExtractorBasicFilter;
    shape?: RunOfficeExtractorShapeSectionFilter;
  };
}

export interface RunOfficeExtractorSlidesShapesFilter extends RunOfficeExtractorShapeSectionFilter {
  refs?: Array<{
    slideId: string;
    shapeId: string;
    name?: string;
    childShapeIds?: string[];
  }>;
}

export interface RunOfficeExtractorSlidesFilter {
  include?: boolean;
  ranges?: Array<{ start: number; end?: number }>;
  ids?: string[];
  tags?: RunOfficeExtractorBasicFilter;
  background?: RunOfficeExtractorBasicFilter;
  headersFooters?: RunOfficeExtractorBasicFilter;
  notesPage?: RunOfficeExtractorBasicFilter;
  shapes?: boolean | RunOfficeExtractorSlidesShapesFilter;
}

export interface RunOfficeExtractorDesignsFilter {
  include?: boolean;
  fontSchema?: RunOfficeExtractorBasicFilter;
  colorSchema?: RunOfficeExtractorBasicFilter;
  textStyles?: RunOfficeExtractorBasicFilter;
  masterShapes?:
    | boolean
    | (RunOfficeExtractorShapeSectionFilter & {
        refs?: Array<{ shapeId: string }>;
      });
  customLayouts?:
    | boolean
    | {
        include?: boolean;
        names?: string[];
        background?: RunOfficeExtractorBasicFilter;
        headersFooters?: RunOfficeExtractorBasicFilter;
        shapes?:
          | boolean
          | (RunOfficeExtractorShapeSectionFilter & {
              refs?: Array<{ layoutName: string; shapeId: string }>;
            });
      };
}

export interface RunOfficeExtractorFilters {
  slides?: boolean | RunOfficeExtractorSlidesFilter;
  designs?: boolean | RunOfficeExtractorDesignsFilter;
  notesMaster?: RunOfficeExtractorBasicFilter;
  source?: RunOfficeExtractorBasicFilter;
  extraColors?: RunOfficeExtractorBasicFilter;
  tags?: RunOfficeExtractorBasicFilter;
  builtInDocumentProperties?: RunOfficeExtractorBasicFilter;
  customDocumentProperties?: RunOfficeExtractorBasicFilter;
  pageSetup?: RunOfficeExtractorBasicFilter;
  runtime?: RunOfficeExtractorBasicFilter;
  sections?: RunOfficeExtractorBasicFilter;
  slideShowSettings?: RunOfficeExtractorBasicFilter;
}

// Shape filter that asks the host to emit each shape's identity scalars
// (`id`, `type`, `name`, `slideId`) only, with every optional property leaf
// turned off. Per the f2_docs filter spec, identity scalars are always
// included, so callers can still count `slide.shapes.length` while paying
// almost nothing per shape. Used by sites that only need shape counts (e.g.
// the initial-mount thumbnail seeding) or that read shape identities for
// later scoped fetches.
export const RUN_OFFICE_EXTRACTOR_MINIMUM_SHAPE_FILTER: RunOfficeExtractorShapeSectionFilter = {
  textFrame: false,
  fill: false,
  border: false,
  table: false,
  chart: false,
  glow: false,
  reflection: false,
  shadow: false,
  softEdge: false,
  callout: false,
  textEffectFormat: false,
  pictureFormat: false,
  connectorFormat: false,
  placeholderFormat: false,
  tags: false,
};

// Shape filter for sites that need shape identity + geometry + text content
// but no decorative formatting. Used by the per-slide layout refiner whose
// shape manifest reads `shape.id`, `shape.type`, geometry, and `shape.text`
// only. Keeps `textFrame: true` so text content survives extraction; turns
// off every other optional leaf.
export const RUN_OFFICE_EXTRACTOR_TEXT_ONLY_SHAPE_FILTER: RunOfficeExtractorShapeSectionFilter = {
  textFrame: true,
  fill: false,
  border: false,
  table: false,
  chart: false,
  glow: false,
  reflection: false,
  shadow: false,
  softEdge: false,
  callout: false,
  textEffectFormat: false,
  pictureFormat: false,
  connectorFormat: false,
  placeholderFormat: false,
  tags: false,
};

interface RunOfficeExtractorContextOptions {
  includeSlideRanges?: RunOfficeExtractorSlideRangeFilter[];
  includeShapes?: RunOfficeExtractorShapeFilter[];
  // New f2_docs filter schema. Mutually exclusive with the legacy
  // `includeSlideRanges` / `includeShapes` fields above — supply one
  // representation, not both. When set, the value is forwarded verbatim to the
  // host as `context.filters`; this is the path Wave 2 of the non-AI extractor
  // optimisation plan uses to request narrower payloads (text-only, minimum
  // shape, table-cell-only, etc.).
  filters?: RunOfficeExtractorFilters;
}

export interface ExtractPresentationContentViaIntentsOptions extends RunOfficeExtractorContextOptions {
  forceRefresh?: boolean;
  preferredBounds?: SlideBounds;
  presentationRef?: Partial<FactSetPresentationRef> | null;
}

type Logger = (message: string) => void;

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isIntentTimeoutError(error: unknown): boolean {
  const message = toErrorMessage(error).toLowerCase();
  return message.includes("timed out after") || message.includes("timeout");
}

function nowMs(): number {
  return Date.now();
}

function isFresh(fetchedAt: number, ttlMs: number): boolean {
  return fetchedAt > 0 && nowMs() - fetchedAt < ttlMs;
}

function resetIntentBridgeCache(
  options: {
    clearSlideInfos?: boolean;
    clearActiveSlideId?: boolean;
    clearSelectedSlideRef?: boolean;
    clearPresentation?: boolean;
    clearSlideBounds?: boolean;
  } = {}
): void {
  const clearAll = Object.keys(options).length === 0;
  const clearSlideInfos = clearAll || Boolean(options.clearSlideInfos);
  const clearActiveSlideId = clearAll || Boolean(options.clearActiveSlideId);
  const clearSelectedSlideRef =
    clearAll || Boolean(options.clearSelectedSlideRef) || clearSlideInfos || clearActiveSlideId;
  const clearPresentation = clearAll || Boolean(options.clearPresentation);
  const clearSlideBounds = clearAll || Boolean(options.clearSlideBounds);

  if (clearSlideInfos) {
    intentBridgeCache.slideInfos = undefined;
    intentBridgeCache.slideInfosFetchedAt = 0;
    inFlightSlideInfos = null;
  }
  if (clearActiveSlideId) {
    intentBridgeCache.activeSlideId = undefined;
    intentBridgeCache.activeSlideIdFetchedAt = 0;
    inFlightActiveSlideRefFromContext = null;
  }
  if (clearSelectedSlideRef) {
    intentBridgeCache.selectedSlideRef = undefined;
    intentBridgeCache.selectedSlideRefFetchedAt = 0;
    inFlightSelectedSlideRef = null;
  }
  if (clearPresentation) {
    intentBridgeCache.presentation = undefined;
    intentBridgeCache.presentationFetchedAt = 0;
    inFlightPresentation = null;
    cachedDesignMetadata = null;
    cachedDesignMetadataFetchedAt = 0;
    inFlightDesignMetadata = null;
    cachedDeckLayoutCatalog = null;
    cachedDeckLayoutCatalogFetchedAt = 0;
    inFlightDeckLayoutCatalog = null;
    inFlightPromptDesignContext = null;
  }
  if (clearSlideBounds) {
    intentBridgeCache.slideBounds = undefined;
    intentBridgeCache.slideBoundsFetchedAt = 0;
    lastSlideBoundsResolutionSource = null;
  }
}

/**
 * Invalidate every per-presentation cache that could carry over stale data
 * from a different file. Call this from the document-change handler when a
 * user-initiated active-presentation switch is observed in PowerPoint —
 * unlike `activatePresentationViaIntents` (which we drive ourselves and
 * therefore know to flush on), a manual window switch fires no intent we
 * own, so without this the slide-selection poll can return the OLD file's
 * cached selected slide for up to `SELECTED_SLIDE_REF_CACHE_TTL_MS`. The
 * stale read then `addTab`s a foreign slide id, `reconcileTabsStateWithSlides`
 * drops the bogus tab on the next reconcile pass, and `activeTabId` falls
 * back to `nextTabs[0]` — i.e. slide 1 — which is exactly the "sometimes
 * it's just going to slide 1" symptom on a multi-file switch.
 */
export function invalidatePresentationScopedIntentBridgeCaches(): void {
  resetIntentBridgeCache({
    clearSlideInfos: true,
    clearActiveSlideId: true,
    clearSelectedSlideRef: true,
    clearPresentation: true,
    clearSlideBounds: true,
  });
}

function logMessage(log: Logger | undefined, message: string): void {
  if (log) {
    log(message);
  } else {
    console.log(message);
  }
}

function summarizeSlideIdForLog(slideId: string | undefined): string {
  const normalized = String(slideId ?? "").trim();
  if (!normalized) return "unknown";
  return normalized.length <= 18
    ? normalized
    : `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
}

function formatPointValueForLog(value: unknown): string {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(1) : "na";
}

function formatShapeRectForLog(shape: {
  left?: unknown;
  top?: unknown;
  width?: unknown;
  height?: unknown;
}): string {
  return (
    `left=${formatPointValueForLog(shape.left)},` +
    `top=${formatPointValueForLog(shape.top)},` +
    `width=${formatPointValueForLog(shape.width)},` +
    `height=${formatPointValueForLog(shape.height)}`
  );
}

function logPreparedBatchBoundsSummary(
  operations: PresentationModification,
  bounds: SlideBounds,
  log?: Logger
): void {
  const margin = FACTSET_SAFE_MARGIN_PT;
  const maxRightAllowed = roundToTenth(bounds.slideWidth - margin);
  const maxBottomAllowed = roundToTenth(bounds.slideHeight - margin);
  let observedShapeCount = 0;
  let minLeft = Number.POSITIVE_INFINITY;
  let minTop = Number.POSITIVE_INFINITY;
  let maxRight = Number.NEGATIVE_INFINITY;
  let maxBottom = Number.NEGATIVE_INFINITY;
  const violationLogs: string[] = [];

  for (const slide of operations.slides ?? []) {
    for (const shape of slide.shapes ?? []) {
      const action = shape.action ?? "modify";
      if (action === "delete") continue;
      const left = readFiniteNumber(shape.left);
      const top = readFiniteNumber(shape.top);
      const width = readPositiveNumber(shape.width);
      const height = readPositiveNumber(shape.height);
      if (
        typeof left !== "number" ||
        typeof top !== "number" ||
        typeof width !== "number" ||
        typeof height !== "number"
      ) {
        continue;
      }

      observedShapeCount += 1;
      const right = left + width;
      const bottom = top + height;
      minLeft = Math.min(minLeft, left);
      minTop = Math.min(minTop, top);
      maxRight = Math.max(maxRight, right);
      maxBottom = Math.max(maxBottom, bottom);

      const violations: string[] = [];
      if (left < margin) violations.push(`left<margin(${formatPointValueForLog(left)}<${margin})`);
      if (top < margin) violations.push(`top<margin(${formatPointValueForLog(top)}<${margin})`);
      if (right > maxRightAllowed) {
        violations.push(`right>max(${formatPointValueForLog(right)}>${formatPointValueForLog(maxRightAllowed)})`);
      }
      if (bottom > maxBottomAllowed) {
        violations.push(
          `bottom>max(${formatPointValueForLog(bottom)}>${formatPointValueForLog(maxBottomAllowed)})`
        );
      }

      if (violations.length > 0 && violationLogs.length < 8) {
        violationLogs.push(
          `slide=${slide.index}, shape=${shape.id ?? "unknown"}, rect={${formatShapeRectForLog({
            left,
            top,
            width,
            height,
          })}}, violations=${violations.join(",")}`
        );
      }
    }
  }

  if (observedShapeCount === 0) {
    logMessage(log, "Payload bounds summary: no positioned shapes in prepared operations.");
    return;
  }

  const requiredWidth = roundToTenth(maxRight + margin);
  const requiredHeight = roundToTenth(maxBottom + margin);
  const fitsByEnvelope = requiredWidth <= bounds.slideWidth && requiredHeight <= bounds.slideHeight;
  const minEdgeWithinMargin = minLeft >= margin && minTop >= margin;
  const fitsWithinBounds = fitsByEnvelope && minEdgeWithinMargin;

  logMessage(
    log,
    `Payload bounds summary: observedShapes=${observedShapeCount}, ` +
      `minLeft=${formatPointValueForLog(minLeft)}, minTop=${formatPointValueForLog(minTop)}, ` +
      `maxRight=${formatPointValueForLog(maxRight)}, maxBottom=${formatPointValueForLog(maxBottom)}, ` +
      `required>=${requiredWidth}x${requiredHeight}pt, bounds=${bounds.slideWidth}x${bounds.slideHeight}pt, ` +
      `margin=${margin}, fits=${fitsWithinBounds}`
  );

  if (violationLogs.length > 0) {
    for (const violationLog of violationLogs) {
      logMessage(log, `Payload bounds violation: ${violationLog}`);
    }
  }
}

function summarizeSlideInfosForLog(slideInfos: SlideInfo[], limit = 5): string {
  if (slideInfos.length === 0) return "none";
  const entries = slideInfos
    .slice(0, limit)
    .map((info) => {
      const numberSuffix =
        typeof info.slideNumber === "number" ? `/n${info.slideNumber}` : "";
      return `#${info.index}${numberSuffix}:${summarizeSlideIdForLog(info.slideId)}`;
    });
  return `${entries.join(", ")}${slideInfos.length > limit ? ", ..." : ""}`;
}

function logExtractedPresentationSnapshot(
  presentation: PresentationContent,
  log?: Logger
): void {
  logMessage(
    log,
    `Extracted presentation: slides=${presentation.slides.length}, selected=${presentation.selectedSlideIndex}, ` +
      `size=${formatPointValueForLog(presentation.slideWidth)}x${formatPointValueForLog(presentation.slideHeight)}pt`
  );
  if (presentation.slides.length === 0) return;

  const preview = presentation.slides
    .slice(0, 4)
    .map((slide) => {
      const firstShape = slide.shapes?.[0];
      const firstShapeSummary = firstShape
        ? `${firstShape.type}@(${formatPointValueForLog(firstShape.left)},${formatPointValueForLog(firstShape.top)} ` +
          `${formatPointValueForLog(firstShape.width)}x${formatPointValueForLog(firstShape.height)})`
        : "none";
      return `#${slide.index} ${summarizeSlideIdForLog(slide.id)} shapes=${slide.shapes?.length ?? 0} first=${firstShapeSummary}`;
    })
    .join(" | ");
  logMessage(
    log,
    `Sample slides: ${preview}${presentation.slides.length > 4 ? " | ..." : ""}`
  );
}

function getIntentDef(intentId: IntentId): IntentDefinition {
  return INTENT_DEFS[intentId];
}

function readPositiveNumber(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return undefined;
  return value;
}

function readFiniteNumber(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return value;
}

function isPropertyNotLoadedError(error: unknown): boolean {
  const message = toErrorMessage(error).toLowerCase();
  return (
    message.includes("propertynotloaded") ||
    message.includes("not been loaded") ||
    message.includes("is not available")
  );
}

function getSlideAspectRatio(): number {
  return 16 / 9;
}

function applyBoundsToShape(
  shapeMod: { left?: number; top?: number; width?: number; height?: number },
  _bounds: SlideBounds,
  _log?: Logger,
  _options?: { logClamp?: boolean; minSize?: number }
): { left?: number; top?: number; width?: number; height?: number } {
  const normalizePoint = (value: number | undefined): number | undefined => {
    const finite = readFiniteNumber(value);
    if (finite === undefined) return undefined;
    return Math.round(finite * 10) / 10;
  };
  return {
    left: normalizePoint(shapeMod.left),
    top: normalizePoint(shapeMod.top),
    width: normalizePoint(shapeMod.width),
    height: normalizePoint(shapeMod.height),
  };
}

function applyBoundsToTableLayout(
  tableData: ShapeModification["table"] | undefined,
  bounds: SlideBounds,
  log?: Logger
): ShapeModification["table"] | undefined {
  if (!tableData?.layoutSpec?.targetRegion) return tableData;
  const target = tableData.layoutSpec.targetRegion;
  const boundedRegion = applyBoundsToShape(
    { left: target.left, top: target.top, width: target.width, height: target.height },
    bounds,
    log
  );
  return {
    ...tableData,
    layoutSpec: {
      ...tableData.layoutSpec,
      targetRegion: {
        left: boundedRegion.left ?? target.left,
        top: boundedRegion.top ?? target.top,
        width: boundedRegion.width ?? target.width,
        height: boundedRegion.height ?? target.height,
      },
    },
  };
}

function normalizeCreateTableForBatch(
  shape: ShapeModification
): ShapeModification["table"] | undefined {
  const tableData = shape.table;
  if (!tableData || shape.action !== "create") {
    return tableData;
  }
  if (tableData.action && tableData.action !== "create" && tableData.action !== "replace") {
    return tableData;
  }
  if (!tableData.rows || tableData.rows.length === 0) {
    return tableData;
  }

  const explicitTextUpdates =
    tableData.textUpdates && tableData.textUpdates.length > 0
      ? tableData.textUpdates
      : shape.cellUpdates && shape.cellUpdates.length > 0
        ? shape.cellUpdates
        : undefined;

  return {
    ...tableData,
    rows: buildEmptyTableShellRows(tableData.rows),
    textUpdates: mergeTableTextUpdates(
      buildTableTextUpdatesFromRows(tableData.rows),
      explicitTextUpdates
    ),
  };
}

function readOptionalBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function normalizeUnitIntervalValue(value: unknown): number | undefined {
  const normalized = readFiniteNumber(value);
  if (normalized === undefined) return undefined;
  if (normalized < 0 || normalized > 1) return undefined;
  return normalized;
}

function normalizeTableHorizontalAlignmentForBatch(
  value: unknown
): TableCellProperties["horizontalAlignment"] | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized === "left") return "left";
  if (normalized === "center" || normalized === "centre" || normalized === "middle") return "center";
  if (normalized === "right") return "right";
  if (normalized === "justify") return "justify";
  if (normalized === "distributed") return "distributed";
  return undefined;
}

function normalizeTableVerticalAlignmentForBatch(
  value: unknown
): TableCellProperties["verticalAlignment"] | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized === "top") return "top";
  if (normalized === "middle" || normalized === "center" || normalized === "centre") return "middle";
  if (normalized === "bottom") return "bottom";
  return undefined;
}

function normalizeTableStyleColorToken(value: unknown): string | undefined {
  const extracted = extractColorToken(value);
  if (!extracted) return undefined;
  return normalizeBatchColorToken(extracted) ?? extracted;
}

function normalizeTableCellFillForBatch(
  value: unknown,
  raw: Record<string, unknown>
): TableCellProperties["fill"] | undefined {
  const obj = value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
  const color = normalizeTableStyleColorToken(obj?.color ?? obj?.foregroundColor ?? raw.fillColor);
  const transparency = normalizeUnitIntervalValue(
    obj?.transparency ?? raw.fillTransparency ?? raw.transparency
  );
  if (color === undefined && transparency === undefined) return undefined;
  return {
    ...(color !== undefined ? { color } : {}),
    ...(transparency !== undefined ? { transparency } : {}),
  };
}

function normalizeTableCellFontForBatch(
  value: unknown,
  raw: Record<string, unknown>
): TableCellProperties["font"] | undefined {
  const obj = value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
  const color = normalizeTableStyleColorToken(obj?.color ?? raw.fontColor ?? raw.textColor);
  const name = pickFirstNonEmptyString(obj?.name, obj?.family, raw.fontFamily);
  const size = readPositiveNumber(obj?.size ?? raw.fontSize);
  const bold = readOptionalBoolean(obj?.bold ?? raw.bold);
  const italic = readOptionalBoolean(obj?.italic ?? raw.italic);
  const allCaps = readOptionalBoolean(obj?.allCaps ?? raw.allCaps);
  if (
    color === undefined &&
    name === undefined &&
    size === undefined &&
    bold === undefined &&
    italic === undefined &&
    allCaps === undefined
  ) {
    return undefined;
  }
  return {
    ...(color !== undefined ? { color } : {}),
    ...(name !== undefined ? { name } : {}),
    ...(size !== undefined ? { size } : {}),
    ...(bold !== undefined ? { bold } : {}),
    ...(italic !== undefined ? { italic } : {}),
    ...(allCaps !== undefined ? { allCaps } : {}),
  };
}

function normalizeTableCellBorderStyleForBatch(value: unknown): { color?: string; weight?: number } | undefined {
  if (!value || typeof value !== "object") return undefined;
  const obj = value as Record<string, unknown>;
  const color = normalizeTableStyleColorToken(obj.color ?? obj.lineColor ?? obj.borderColor);
  const weight = extractBorderWeight(obj.weight ?? obj.lineWidth ?? obj.borderWeight);
  if (color === undefined && weight === undefined) return undefined;
  return {
    ...(color !== undefined ? { color } : {}),
    ...(weight !== undefined ? { weight } : {}),
  };
}

function normalizeTableCellBordersForBatch(
  value: unknown,
  raw: Record<string, unknown>
): TableCellProperties["borders"] | undefined {
  const obj = value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
  const sharedBorder =
    raw.borderColor !== undefined || raw.borderWeight !== undefined
      ? {
          color: raw.borderColor,
          weight: raw.borderWeight,
        }
      : undefined;
  const top = normalizeTableCellBorderStyleForBatch(obj?.top ?? sharedBorder);
  const bottom = normalizeTableCellBorderStyleForBatch(obj?.bottom ?? sharedBorder);
  const left = normalizeTableCellBorderStyleForBatch(obj?.left ?? sharedBorder);
  const right = normalizeTableCellBorderStyleForBatch(obj?.right ?? sharedBorder);
  if (!top && !bottom && !left && !right) return undefined;
  return {
    ...(top ? { top } : {}),
    ...(bottom ? { bottom } : {}),
    ...(left ? { left } : {}),
    ...(right ? { right } : {}),
  };
}

function normalizeTableCellMarginsForBatch(
  raw: Record<string, unknown>
): TableCellProperties["margins"] | undefined {
  const obj = raw.margins && typeof raw.margins === "object" ? (raw.margins as Record<string, unknown>) : undefined;
  const left = readFiniteNumber(obj?.left ?? raw.marginLeft);
  const right = readFiniteNumber(obj?.right ?? raw.marginRight);
  const top = readFiniteNumber(obj?.top ?? raw.marginTop);
  const bottom = readFiniteNumber(obj?.bottom ?? raw.marginBottom);
  if (left === undefined && right === undefined && top === undefined && bottom === undefined) {
    return undefined;
  }
  return {
    ...(left !== undefined ? { left } : {}),
    ...(right !== undefined ? { right } : {}),
    ...(top !== undefined ? { top } : {}),
    ...(bottom !== undefined ? { bottom } : {}),
  };
}

function normalizeTableTextRunsForBatch(value: unknown): TableCellProperties["textRuns"] | undefined {
  if (!Array.isArray(value)) return undefined;
  const normalizedRuns: NonNullable<TableCellProperties["textRuns"]> = [];
  for (const candidate of value) {
    if (!candidate || typeof candidate !== "object") continue;
    const obj = candidate as Record<string, unknown>;
    if (typeof obj.text !== "string") continue;
    const font = normalizeTableCellFontForBatch(obj.font, obj);
    normalizedRuns.push({
      text: obj.text,
      ...(font ? { font } : {}),
    });
  }
  return normalizedRuns.length > 0 ? normalizedRuns : undefined;
}

function hasTableCellPropertyValuesForBatch(props: TableCellProperties | undefined): boolean {
  if (!props) {
    return false;
  }
  if (props.fill && (props.fill.color !== undefined || props.fill.transparency !== undefined)) {
    return true;
  }
  if (
    props.font &&
    (props.font.color !== undefined ||
      props.font.name !== undefined ||
      props.font.size !== undefined ||
      props.font.bold !== undefined ||
      props.font.italic !== undefined ||
      props.font.allCaps !== undefined)
  ) {
    return true;
  }
  if (
    props.borders &&
    Object.values(props.borders).some(
      (border) => border && (border.color !== undefined || border.weight !== undefined)
    )
  ) {
    return true;
  }
  if (props.horizontalAlignment !== undefined || props.verticalAlignment !== undefined) {
    return true;
  }
  if (props.indentLevel !== undefined) {
    return true;
  }
  if (props.margins && Object.values(props.margins).some((margin) => margin !== undefined)) {
    return true;
  }
  return Boolean(props.textRuns && props.textRuns.length > 0);
}

function normalizeTableCellPropertiesForBatch(value: unknown): TableCellProperties | undefined {
  if (!value || typeof value !== "object") return undefined;
  const raw = value as Record<string, unknown>;
  const normalized: TableCellProperties = {};
  const fill = normalizeTableCellFillForBatch(raw.fill, raw);
  const font = normalizeTableCellFontForBatch(raw.font, raw);
  const borders = normalizeTableCellBordersForBatch(raw.borders, raw);
  const horizontalAlignment = normalizeTableHorizontalAlignmentForBatch(raw.horizontalAlignment);
  const verticalAlignment = normalizeTableVerticalAlignmentForBatch(raw.verticalAlignment);
  const indentLevel = readNonNegativeInteger(raw.indentLevel);
  const margins = normalizeTableCellMarginsForBatch(raw);
  const textRuns = normalizeTableTextRunsForBatch(raw.textRuns);

  if (fill) normalized.fill = fill;
  if (font) normalized.font = font;
  if (borders) normalized.borders = borders;
  if (horizontalAlignment) normalized.horizontalAlignment = horizontalAlignment;
  if (verticalAlignment) normalized.verticalAlignment = verticalAlignment;
  if (indentLevel !== undefined) normalized.indentLevel = indentLevel;
  if (margins) normalized.margins = margins;
  if (textRuns) normalized.textRuns = textRuns;

  return hasTableCellPropertyValuesForBatch(normalized) ? normalized : undefined;
}

function normalizeTableStyleTargetForBatch(value: unknown): TableStyleUpdate["target"] | undefined {
  if (!value || typeof value !== "object") return undefined;
  const obj = value as Record<string, unknown>;
  const targetType = typeof obj.type === "string" ? obj.type.trim().toLowerCase() : "";
  if (!targetType) return undefined;
  if (targetType === "all") return { type: "all" };
  if (targetType === "row") {
    const index = readNonNegativeInteger(obj.index);
    return typeof index === "number" ? { type: "row", index } : undefined;
  }
  if (targetType === "column") {
    const index = readNonNegativeInteger(obj.index);
    return typeof index === "number" ? { type: "column", index } : undefined;
  }
  if (targetType === "cell") {
    const row = readNonNegativeInteger(obj.row);
    const col = readNonNegativeInteger(obj.col);
    return typeof row === "number" && typeof col === "number" ? { type: "cell", row, col } : undefined;
  }
  if (targetType === "range") {
    const startRow = readNonNegativeInteger(obj.startRow);
    const startCol = readNonNegativeInteger(obj.startCol);
    const rowCount = readPositiveInteger(obj.rowCount);
    const colCount = readPositiveInteger(obj.colCount);
    return typeof startRow === "number" &&
      typeof startCol === "number" &&
      typeof rowCount === "number" &&
      typeof colCount === "number"
      ? { type: "range", startRow, startCol, rowCount, colCount }
      : undefined;
  }
  return undefined;
}

function normalizeTableStyleUpdateForBatch(value: unknown): TableStyleUpdate | undefined {
  if (!value || typeof value !== "object") return undefined;
  const obj = value as Record<string, unknown>;
  const target = normalizeTableStyleTargetForBatch(obj.target);
  const set = normalizeTableCellPropertiesForBatch(obj.set);
  if (!target || !set) return undefined;
  return { target, set };
}

function normalizeTableStyleSettingsSourceForBatch(
  value: unknown
): TableStyleSettingsData | undefined {
  if (!value || typeof value !== "object") return undefined;
  const obj = value as Record<string, unknown>;
  const normalized: TableStyleSettingsData = {};
  const style = pickFirstNonEmptyString(obj.style);
  const areRowsBanded = readOptionalBoolean(obj.areRowsBanded);
  const areColumnsBanded = readOptionalBoolean(obj.areColumnsBanded);
  const isFirstRowHighlighted = readOptionalBoolean(obj.isFirstRowHighlighted);
  const isFirstColumnHighlighted = readOptionalBoolean(obj.isFirstColumnHighlighted);
  const isLastRowHighlighted = readOptionalBoolean(obj.isLastRowHighlighted);
  const isLastColumnHighlighted = readOptionalBoolean(obj.isLastColumnHighlighted);

  if (style) normalized.style = style;
  if (areRowsBanded !== undefined) normalized.areRowsBanded = areRowsBanded;
  if (areColumnsBanded !== undefined) normalized.areColumnsBanded = areColumnsBanded;
  if (isFirstRowHighlighted !== undefined) normalized.isFirstRowHighlighted = isFirstRowHighlighted;
  if (isFirstColumnHighlighted !== undefined) normalized.isFirstColumnHighlighted = isFirstColumnHighlighted;
  if (isLastRowHighlighted !== undefined) normalized.isLastRowHighlighted = isLastRowHighlighted;
  if (isLastColumnHighlighted !== undefined) normalized.isLastColumnHighlighted = isLastColumnHighlighted;

  return Object.values(normalized).some((candidate) => candidate !== undefined) ? normalized : undefined;
}

function buildCreateTableStyleUpdatesForBatch(tableData: TableData | undefined): TableStyleUpdate[] {
  const styleUpdates: TableStyleUpdate[] = [];
  const uniformCellProperties = normalizeTableCellPropertiesForBatch(tableData?.uniformCellProperties);
  if (uniformCellProperties) {
    styleUpdates.push({
      target: { type: "all" },
      set: uniformCellProperties,
    });
  }
  for (let row = 0; row < (tableData?.specificCellProperties || []).length; row += 1) {
    const rowProps = tableData?.specificCellProperties?.[row] || [];
    for (let col = 0; col < rowProps.length; col += 1) {
      const cellProps = normalizeTableCellPropertiesForBatch(rowProps[col]);
      if (!cellProps) continue;
      styleUpdates.push({
        target: { type: "cell", row, col },
        set: cellProps,
      });
    }
  }
  for (const candidate of tableData?.styleUpdates || []) {
    const normalized = normalizeTableStyleUpdateForBatch(candidate);
    if (normalized) {
      styleUpdates.push(normalized);
    }
  }
  return styleUpdates;
}

function buildCreateTableStyleSettingsUpdateForBatch(
  tableData: TableData | undefined
): TableStyleSettingsData | undefined {
  const merged: TableStyleSettingsData = {
    ...(normalizeTableStyleSettingsSourceForBatch(tableData?.styleSettings) || {}),
  };
  const style = pickFirstNonEmptyString(tableData?.tableStyle);
  if (style) {
    merged.style = style;
  }
  Object.assign(merged, normalizeTableStyleSettingsSourceForBatch(tableData?.styleSettingsUpdate) || {});
  return Object.values(merged).some((candidate) => candidate !== undefined) ? merged : undefined;
}

function hasCreateTableStyleFieldsForBatch(tableData: TableData | undefined): boolean {
  if (!tableData) return false;
  return Boolean(
    tableData.tableStyle ||
      tableData.styleSettings ||
      tableData.styleSettingsUpdate ||
      tableData.uniformCellProperties ||
      (tableData.specificCellProperties && tableData.specificCellProperties.length > 0) ||
      (tableData.styleUpdates && tableData.styleUpdates.length > 0)
  );
}

function stripCreateTableStylingForBatch(tableData: TableData | undefined): TableData | undefined {
  if (!tableData) return tableData;
  const {
    tableStyle: _tableStyle,
    styleSettings: _styleSettings,
    styleSettingsUpdate: _styleSettingsUpdate,
    uniformCellProperties: _uniformCellProperties,
    specificCellProperties: _specificCellProperties,
    styleUpdates: _styleUpdates,
    ...rest
  } = tableData;
  return rest;
}

function buildCreateTableStyleFollowUpForBatch(
  shape: ShapeModification,
  createdShapeId: string
): ShapeModification | undefined {
  if (shape.action !== "create" || !shape.table) return undefined;
  const styleUpdates = buildCreateTableStyleUpdatesForBatch(shape.table);
  const styleSettingsUpdate = buildCreateTableStyleSettingsUpdateForBatch(shape.table);
  if (styleUpdates.length === 0 && !styleSettingsUpdate) {
    return undefined;
  }
  return {
    id: createdShapeId,
    action: "modify",
    table: {
      action: "edit",
      ...(styleUpdates.length > 0 ? { styleUpdates } : {}),
      ...(styleSettingsUpdate ? { styleSettingsUpdate } : {}),
    },
  };
}

function normalizeGeometricType(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/[\s_-]/g, "")
    .toLowerCase();
}

function isLineLikeGeometricType(value: unknown): boolean {
  const geometricType = normalizeGeometricType(value);
  if (!geometricType) return false;
  if (geometricType.includes("line")) return true;
  if (geometricType.includes("connector")) return true;
  return geometricType === "straight" || geometricType === "elbow";
}

function hasShapeTextIntent(shape: ShapeModification): boolean {
  if (typeof shape.text === "string" && shape.text.trim().length > 0) return true;
  if (Array.isArray(shape.textReplacements) && shape.textReplacements.length > 0) return true;
  if (Array.isArray(shape.paragraphOps) && shape.paragraphOps.length > 0) return true;
  return false;
}

function isFilledGeometricContainer(shape: ShapeModification): boolean {
  if (!shape.geometricType || isLineLikeGeometricType(shape.geometricType)) return false;
  return typeof shape.fillColor === "string" && shape.fillColor.trim().length > 0;
}

// Higher priority value = drawn later = stacked on top in PowerPoint's z-order.
// Background rectangles render first, then filled geometric containers/cards,
// then images/charts/tables, then decorations/connectors, then text-only
// shapes. Non-create operations are not reordered by reorderCreateShapesForLayering.
function getCreateShapeLayerPriority(shape: ShapeModification, bounds: SlideBounds): number {
  if ((shape.action ?? "modify") !== "create") return 99;
  if (isBackgroundLikeCreateShape(shape, bounds)) return 0;
  if (isFilledGeometricContainer(shape)) return 1;
  if (shape.chart || shape.table || hasAnyImagePayload(shape)) return 2;
  if (shape.geometricType && !hasShapeTextIntent(shape)) {
    return isLineLikeGeometricType(shape.geometricType) ? 3 : 2;
  }
  if (hasShapeTextIntent(shape)) return 4;
  return 3;
}

// Stable sort: only `create` ops are reordered against each other. Their
// resulting positions are written back into the original array slots so any
// non-create ops (modify/delete) keep their relative order untouched.
function reorderCreateShapesForLayering(
  shapes: ShapeModification[],
  bounds: SlideBounds
): ShapeModification[] {
  const createEntries = shapes
    .map((shape, index) => ({ shape, index }))
    .filter((entry) => (entry.shape.action ?? "modify") === "create");
  if (createEntries.length < 2) {
    return shapes;
  }

  const sortedCreates = createEntries
    .slice()
    .sort((left, right) => {
      const leftPriority = getCreateShapeLayerPriority(left.shape, bounds);
      const rightPriority = getCreateShapeLayerPriority(right.shape, bounds);
      if (leftPriority !== rightPriority) return leftPriority - rightPriority;
      return left.index - right.index;
    })
    .map((entry) => entry.shape);

  let changed = false;
  for (let i = 0; i < createEntries.length; i += 1) {
    if (createEntries[i].shape !== sortedCreates[i]) {
      changed = true;
      break;
    }
  }
  if (!changed) {
    return shapes;
  }

  const reordered = shapes.slice();
  for (let i = 0; i < createEntries.length; i += 1) {
    reordered[createEntries[i].index] = sortedCreates[i];
  }
  return reordered;
}

// Synthesized follow-up ops must trail the create that materializes their
// target shape. Because create layering can reshuffle creates across the batch,
// we reattach those follow-ups only after the create-order pass is complete.
function attachSynthesizedFollowUpsAfterCreates(
  shapes: ShapeModification[],
  followUpsByCreateId: ReadonlyMap<string, ShapeModification[]>
): ShapeModification[] {
  if (followUpsByCreateId.size === 0) {
    return shapes;
  }

  const finalized: ShapeModification[] = [];
  const attachedCreateIds = new Set<string>();

  for (const shape of shapes) {
    finalized.push(shape);
    if ((shape.action ?? "modify") !== "create") {
      continue;
    }

    const shapeId = typeof shape.id === "string" ? shape.id : "";
    if (!shapeId) {
      continue;
    }

    const followUps = followUpsByCreateId.get(shapeId);
    if (!followUps || followUps.length === 0) {
      continue;
    }

    finalized.push(...followUps);
    attachedCreateIds.add(shapeId);
  }

  if (attachedCreateIds.size === followUpsByCreateId.size) {
    return finalized;
  }

  followUpsByCreateId.forEach((followUps, shapeId) => {
    if (attachedCreateIds.has(shapeId) || followUps.length === 0) {
      return;
    }
    finalized.push(...followUps);
  });

  return finalized;
}

// "Background-like" = a plain, opaque rectangle that covers >=70% of the slide
// and reaches each edge within the safe margin. These get layer priority 0 so
// they render under everything else, even when the LLM happens to emit them
// after the foreground content in its streamed output.
function isBackgroundLikeCreateShape(shape: ShapeModification, bounds: SlideBounds): boolean {
  if ((shape.action ?? "modify") !== "create") return false;
  if (shape.table || shape.chart || shape.cellUpdates || hasAnyImagePayload(shape)) return false;
  if (typeof shape.text === "string" && shape.text.trim().length > 0) return false;
  if (Array.isArray(shape.textReplacements) && shape.textReplacements.length > 0) return false;
  if (Array.isArray(shape.paragraphOps) && shape.paragraphOps.length > 0) return false;

  const geometricType = normalizeGeometricType(shape.geometricType);
  const rectangleLikeTypes = new Set(["rectangle", "roundedrectangle", "roundrectangle", "roundrect"]);
  if (!rectangleLikeTypes.has(geometricType)) return false;

  const left = readFiniteNumber(shape.left);
  const top = readFiniteNumber(shape.top);
  const width = readPositiveNumber(shape.width);
  const height = readPositiveNumber(shape.height);
  if (
    typeof left !== "number" ||
    typeof top !== "number" ||
    typeof width !== "number" ||
    typeof height !== "number"
  ) {
    return false;
  }

  const right = left + width;
  const bottom = top + height;
  const edgeTolerance = Math.max(8, FACTSET_SAFE_MARGIN_PT);
  const spansSurface =
    left <= edgeTolerance &&
    top <= edgeTolerance &&
    right >= bounds.slideWidth - edgeTolerance &&
    bottom >= bounds.slideHeight - edgeTolerance;
  if (!spansSurface) return false;

  const slideArea = Math.max(1, bounds.slideWidth * bounds.slideHeight);
  const shapeArea = width * height;
  const coverageRatio = shapeArea / slideArea;
  return coverageRatio >= 0.7;
}

function normalizeSlideIdentity(id: string): string {
  return String(id ?? "").split("#")[0];
}

function normalizeBatchColorToken(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  let rawHex = trimmed.replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(rawHex)) {
    rawHex = rawHex
      .split("")
      .map((char) => `${char}${char}`)
      .join("");
  }

  if (/^[0-9a-f]{8}$/i.test(rawHex)) {
    // Host expects #RRGGBB. The system prompt tells the model to emit
    // 6-char hex (#RRGGBB), so any 8-char hex here is the model drifting
    // into CSS-style #RRGGBBAA (alpha-last). Drop the trailing alpha
    // byte; assuming Office-style ARGB (alpha-first) here would silently
    // turn `#FF0000CC` (red w/ alpha) into `#0000CC` (blue).
    rawHex = rawHex.slice(0, 6);
  }

  if (/^[0-9a-f]{6}$/i.test(rawHex)) {
    return `#${rawHex.toUpperCase()}`;
  }

  return undefined;
}

function normalizeExtractorShapeType(raw: unknown): ShapeContent["type"] {
  const t = String(raw ?? "").toLowerCase();
  if (t.includes("table")) return "table";
  if (t.includes("image") || t.includes("picture")) return "image";
  if (t.includes("chart")) return "chart";
  if (t.includes("ole")) return "oleObject";
  if (t.includes("line") || t.includes("connector")) return "line";
  if (t.includes("group")) return "group";
  if (t.includes("geometric") || t.includes("autoshape")) return "geometricShape";
  if (t.includes("text") || t.includes("placeholder")) return "textBox";
  return "unknown";
}

function normalizeExtractorGeometricTypeToken(raw: unknown): ShapeContent["geometricType"] | undefined {
  const token = normalizeGeometricType(raw);
  if (!token) return undefined;
  if (token.includes("roundedrectangle") || token.includes("roundrect")) return "roundedRectangle";
  if (token === "rectangle" || token === "rect" || token.endsWith("rectangle")) return "rectangle";
  if (token.includes("ellipse") || token.includes("oval") || token.includes("circle")) return "ellipse";
  if (token.includes("triangle")) return "triangle";
  if (token.includes("diamond")) return "diamond";
  if (token.includes("pentagon")) return "pentagon";
  if (token.includes("hexagon")) return "hexagon";
  if (token.includes("star6")) return "star6";
  if (token.includes("star5")) return "star5";
  if (token.includes("star4")) return "star4";
  if (token.includes("parallelogram")) return "parallelogram";
  if (token.includes("trapezoid")) return "trapezoid";
  if (token.includes("arrowleft") || token.includes("leftarrow")) return "arrowLeft";
  if (token.includes("arrowup") || token.includes("uparrow")) return "arrowUp";
  if (token.includes("arrowdown") || token.includes("downarrow")) return "arrowDown";
  if (token.includes("arrow")) return "arrow";
  if (token.includes("callout")) return "callout";
  if (token.includes("cloud")) return "cloud";
  if (token.includes("heart")) return "heart";
  if (token.includes("lightning") || token.includes("bolt")) return "lightning";
  if (token.includes("bracepair") || token === "brace") return "bracePair";
  if (token.includes("bracketpair") || token === "bracket") return "bracketPair";
  if (token === "plus") return "plus";
  if (token === "minus") return "minus";
  if (token.includes("straightconnector") || token.includes("connectorstraight")) {
    return "straightConnector";
  }
  if (token.includes("connector")) return "straightConnector";
  if (
    token === "line" ||
    token === "straightline" ||
    token === "horizontalline" ||
    token === "verticalline"
  ) {
    return "line";
  }
  return undefined;
}

function resolveExtractorGeometricType(
  shape: ExtractorShapeLite
): ShapeContent["geometricType"] | undefined {
  const explicitRaw = pickFirstNonEmptyString(
    shape.geometricShapeType,
    shape.geometricType,
    shape.autoShapeType
  );
  if (explicitRaw) {
    const normalized = normalizeExtractorGeometricTypeToken(explicitRaw);
    return normalized ?? (explicitRaw as ShapeContent["geometricType"]);
  }
  return undefined;
}

function parseExtractorResult(raw: unknown): ExtractorResultLite | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  if (typeof obj.result === "string") {
    try {
      return JSON.parse(obj.result) as ExtractorResultLite;
    } catch {
      return null;
    }
  }
  if (typeof obj.result === "object" && obj.result !== null) {
    return obj.result as ExtractorResultLite;
  }
  if (Array.isArray(obj.slides)) {
    return obj as unknown as ExtractorResultLite;
  }
  return null;
}

/**
 * Async variant of `parseExtractorResult` that hands off the heavy
 * `JSON.parse` on the doubly-encoded `result` string to a Web Worker via
 * `parseJsonInWorker`. Used on every chat / refiner / design-metadata /
 * bounds-fallback extractor call site so the main thread stays responsive
 * even on 200+ slide decks where the inner string can exceed 15 MB.
 *
 * Tolerates both wire shapes — when the host already returns `result` as
 * a parsed object (after FactSet's intent-format standardization rollout),
 * the worker round-trip is skipped entirely.
 */
async function parseExtractorResultAsync(raw: unknown): Promise<ExtractorResultLite | null> {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  if (typeof obj.result === "string") {
    try {
      return (await parseJsonInWorker<ExtractorResultLite>(obj.result)) ?? null;
    } catch {
      return null;
    }
  }
  if (typeof obj.result === "object" && obj.result !== null) {
    return obj.result as ExtractorResultLite;
  }
  if (Array.isArray(obj.slides)) {
    return obj as unknown as ExtractorResultLite;
  }
  return null;
}

function readPositiveInteger(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  const normalized = Math.trunc(value);
  return normalized > 0 ? normalized : undefined;
}

function readNonNegativeInteger(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    const normalized = Math.trunc(value);
    return normalized >= 0 ? normalized : undefined;
  }
  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
  }
  return undefined;
}

function pickFirstNonEmptySlideId(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0) return trimmed;
      continue;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(Math.trunc(value));
    }
  }
  return undefined;
}

interface NormalizedRunOfficeExtractorContext {
  context: Record<string, unknown>;
  includeSlideRanges: RunOfficeExtractorSlideRangeFilter[];
  includeShapes: RunOfficeExtractorShapeFilter[];
  hasFilters: boolean;
}

// Deep clone of a plain JSON value. Used by the filter path to defensively
// snapshot the caller's filter object before we put it on the wire — if we
// ever mutate the context downstream (we don't today, but) the caller's
// object stays untouched.
function clonePlainJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

// Materialise the wire payload + legacy mirror arrays from an f2_docs-shaped
// filters object. Slide-id / range / shape-ref selectors are mirrored into
// `includeSlideRanges` / `includeShapes` so `resolvePreferredSlideIdsForExtractor`
// (which keys off `includeSlideRanges`) and the extract log keep behaving the
// same as for the legacy callsite.
function normalizeRunOfficeExtractorFiltersOption(
  filters: RunOfficeExtractorFilters
): NormalizedRunOfficeExtractorContext {
  const cloned = clonePlainJson(filters);

  const includeSlideRanges: RunOfficeExtractorSlideRangeFilter[] = [];
  const seenSlideRanges = new Set<string>();
  const includeShapes: RunOfficeExtractorShapeFilter[] = [];
  const seenShapes = new Set<string>();

  const slidesNode = cloned.slides;
  if (slidesNode && typeof slidesNode === "object") {
    for (const rawId of slidesNode.ids ?? []) {
      const id = pickFirstNonEmptyString(rawId);
      if (!id) continue;
      const key = `id:${normalizeSlideIdentity(id)}`;
      if (seenSlideRanges.has(key)) continue;
      includeSlideRanges.push({ id });
      seenSlideRanges.add(key);
    }
    for (const rawRange of slidesNode.ranges ?? []) {
      const start = readNonNegativeInteger(rawRange?.start);
      if (typeof start !== "number") continue;
      const rawEnd = readNonNegativeInteger(rawRange?.end);
      const end = typeof rawEnd === "number" ? Math.max(start, rawEnd) : undefined;
      const key = `range:${start}:${end ?? ""}`;
      if (seenSlideRanges.has(key)) continue;
      includeSlideRanges.push(typeof end === "number" ? { start, end } : { start });
      seenSlideRanges.add(key);
    }

    const shapesNode = slidesNode.shapes;
    if (shapesNode && typeof shapesNode === "object" && !Array.isArray(shapesNode)) {
      for (const rawRef of shapesNode.refs ?? []) {
        const id = pickFirstNonEmptyString(rawRef?.shapeId);
        const slideId = pickFirstNonEmptySlideId(rawRef?.slideId);
        if (!id || !slideId) continue;
        const name = pickFirstNonEmptyString(rawRef?.name);
        const key = `${normalizeSlideIdentity(slideId)}::${id}`;
        if (seenShapes.has(key)) continue;
        includeShapes.push({
          id,
          slideId,
          ...(name ? { name } : {}),
        });
        seenShapes.add(key);
      }
    }
  }

  const filtersIsNonEmpty = Object.keys(cloned).length > 0;
  const context: Record<string, unknown> = {};
  if (filtersIsNonEmpty) {
    context.filters = cloned;
  }

  return {
    context,
    includeSlideRanges,
    includeShapes,
    // Treat any non-empty filters object as a scoped request so the
    // presentation cache is bypassed and the response isn't shared with
    // unfiltered callers. Empty `filters: {}` is equivalent to omitting
    // filters (recipe 1 in the spec), so we leave hasFilters false in that
    // edge case.
    hasFilters: filtersIsNonEmpty,
  };
}

function normalizeRunOfficeExtractorContextOptions(
  options?: RunOfficeExtractorContextOptions
): NormalizedRunOfficeExtractorContext {
  // New f2_docs filter path: caller has supplied the full filter schema
  // directly. We forward it to the host verbatim and mirror the slide/shape
  // selectors back into the legacy arrays so downstream readers
  // (`resolvePreferredSlideIdsForExtractor`, the
  // `extractPresentationContentViaIntents` trace log) keep working unchanged.
  if (options?.filters !== undefined) {
    if (
      (options.includeSlideRanges && options.includeSlideRanges.length > 0) ||
      (options.includeShapes && options.includeShapes.length > 0)
    ) {
      throw new Error(
        "RunOfficeExtractor filters cannot be combined with includeSlideRanges or includeShapes."
      );
    }
    return normalizeRunOfficeExtractorFiltersOption(options.filters);
  }

  const includeSlideRanges: RunOfficeExtractorSlideRangeFilter[] = [];
  const seenSlideRanges = new Set<string>();
  for (const candidate of options?.includeSlideRanges ?? []) {
    const id = "id" in candidate ? pickFirstNonEmptyString(candidate.id) : undefined;
    if (id) {
      const normalizedId = normalizeSlideIdentity(id);
      const key = `id:${normalizedId}`;
      if (!seenSlideRanges.has(key)) {
        includeSlideRanges.push({ id });
        seenSlideRanges.add(key);
      }
      continue;
    }

    const start = "start" in candidate ? readNonNegativeInteger(candidate.start) : undefined;
    if (typeof start !== "number") continue;
    const rawEnd = "end" in candidate ? readNonNegativeInteger(candidate.end) : undefined;
    const end = typeof rawEnd === "number" ? Math.max(start, rawEnd) : undefined;
    const key = `range:${start}:${end ?? ""}`;
    if (seenSlideRanges.has(key)) continue;
    includeSlideRanges.push(typeof end === "number" ? { start, end } : { start });
    seenSlideRanges.add(key);
  }

  const includeShapes: RunOfficeExtractorShapeFilter[] = [];
  const seenShapes = new Set<string>();
  for (const candidate of options?.includeShapes ?? []) {
    const id = pickFirstNonEmptyString(candidate.id);
    const slideId = pickFirstNonEmptySlideId(candidate.slideId);
    if (!id || !slideId) continue;
    const name = pickFirstNonEmptyString(candidate.name);
    const key = `${normalizeSlideIdentity(slideId)}::${id}`;
    if (seenShapes.has(key)) continue;
    includeShapes.push({
      id,
      slideId,
      ...(name ? { name } : {}),
    });
    seenShapes.add(key);
  }

  if (includeSlideRanges.length > 0 && includeShapes.length > 0) {
    throw new Error(
      "RunOfficeExtractor filters must use either includeSlideRanges or includeShapes, not both."
    );
  }

  // Serialise the canonical input lists into the host's documented `filters`
  // schema (see `f2_docs/powerpoint/powerpoint-tools.md` →
  // `factset.Presentation.RunOfficeExtractor`). The internal arrays above stay
  // in their existing shape so downstream readers (e.g.
  // `resolvePreferredSlideIdsForExtractor` and the
  // `extractPresentationContentViaIntents` trace log) keep working unchanged —
  // only the wire payload moves to the new schema.
  const context: Record<string, unknown> = {};
  if (includeSlideRanges.length > 0 || includeShapes.length > 0) {
    const slidesFilter: Record<string, unknown> = {};

    if (includeSlideRanges.length > 0) {
      const ids: string[] = [];
      const ranges: Array<{ start: number; end?: number }> = [];
      for (const entry of includeSlideRanges) {
        if ("id" in entry) {
          ids.push(entry.id);
        } else {
          ranges.push(entry);
        }
      }
      if (ids.length > 0) slidesFilter.ids = ids;
      if (ranges.length > 0) slidesFilter.ranges = ranges;
    }

    if (includeShapes.length > 0) {
      slidesFilter.shapes = {
        refs: includeShapes.map((shape) => {
          const ref: { slideId: string; shapeId: string; name?: string } = {
            slideId: shape.slideId,
            // Host expects `shapeId`; our internal type calls it `id`.
            shapeId: shape.id,
          };
          if (shape.name) ref.name = shape.name;
          return ref;
        }),
      };
    }

    context.filters = { slides: slidesFilter };
  }

  return {
    context,
    includeSlideRanges,
    includeShapes,
    hasFilters: includeSlideRanges.length > 0 || includeShapes.length > 0,
  };
}

function resolvePreferredSlideIdsForExtractor(
  slideInfos: SlideInfo[],
  normalizedContext: NormalizedRunOfficeExtractorContext
): string[] | undefined {
  if (normalizedContext.includeSlideRanges.length === 0) {
    return slideInfos.length > 0 ? slideInfos.map((slide) => slide.slideId) : undefined;
  }

  const slideByIndex = new Map<number, SlideInfo>();
  for (const slideInfo of slideInfos) {
    slideByIndex.set(slideInfo.index, slideInfo);
  }

  const preferredSlideIds: string[] = [];
  const seenSlideIds = new Set<string>();
  const noteSlideId = (candidateId: string | undefined): void => {
    const normalizedCandidateId = String(candidateId ?? "").trim();
    if (!normalizedCandidateId) return;
    const key = normalizeSlideIdentity(normalizedCandidateId);
    if (seenSlideIds.has(key)) return;
    preferredSlideIds.push(normalizedCandidateId);
    seenSlideIds.add(key);
  };

  for (const rangeFilter of normalizedContext.includeSlideRanges) {
    if ("id" in rangeFilter) {
      noteSlideId(rangeFilter.id);
      continue;
    }
    const end = typeof rangeFilter.end === "number" ? rangeFilter.end : rangeFilter.start;
    for (let index = rangeFilter.start; index <= end; index += 1) {
      noteSlideId(slideByIndex.get(index)?.slideId);
    }
  }

  if (preferredSlideIds.length > 0) {
    return preferredSlideIds;
  }
  return slideInfos.length > 0 ? slideInfos.map((slide) => slide.slideId) : undefined;
}

function alignExtractorSlideIndicesWithSlideInfos(
  presentation: PresentationContent,
  slideInfos: SlideInfo[],
  log?: Logger
): PresentationContent {
  if (presentation.slides.length === 0 || slideInfos.length === 0) {
    return presentation;
  }

  const slideInfoById = new Map<string, SlideInfo>();
  for (const slideInfo of slideInfos) {
    slideInfoById.set(normalizeSlideIdentity(slideInfo.slideId), slideInfo);
  }

  let adjustedCount = 0;
  const alignedSlides = presentation.slides.map((slide) => {
    const normalizedId = normalizeSlideIdentity(slide.id);
    if (!normalizedId) {
      return slide;
    }
    const mappedInfo = slideInfoById.get(normalizedId);
    const mappedIndex = mappedInfo?.index;
    const mappedSlideNumber = mappedInfo?.slideNumber;
    if (
      typeof mappedIndex !== "number" ||
      (mappedIndex === slide.index &&
        (typeof mappedSlideNumber !== "number" || mappedSlideNumber === slide.slideNumber))
    ) {
      return slide;
    }
    adjustedCount += 1;
    return {
      ...slide,
      index: mappedIndex,
      ...(typeof mappedSlideNumber === "number" ? { slideNumber: mappedSlideNumber } : {}),
    };
  });

  if (adjustedCount === 0) {
    return presentation;
  }

  const sortedSlides = alignedSlides.slice().sort((left, right) => left.index - right.index);
  logMessage(
    log,
    `Aligned ${adjustedCount} extractor slide index/number entries to GetSlideList for LLM context parity.`
  );
  return {
    ...presentation,
    slides: sortedSlides,
  };
}

function parseActivePresentationContextPayload(
  raw: unknown
): ActiveSlideContextInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;

  let payload: unknown =
    obj.activePresentationContext ??
    (obj.context && typeof obj.context === "object"
      ? (obj.context as Record<string, unknown>).activePresentationContext
      : undefined) ??
    obj;

  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return null;
    }
  }
  if (!payload || typeof payload !== "object") return null;

  const payloadObj = payload as Record<string, unknown>;
  const payloadSlide =
    payloadObj.slide && typeof payloadObj.slide === "object"
      ? (payloadObj.slide as Record<string, unknown>)
      : undefined;
  const payloadActiveSlide =
    payloadObj.activeSlide && typeof payloadObj.activeSlide === "object"
      ? (payloadObj.activeSlide as Record<string, unknown>)
      : undefined;
  const slide = payloadActiveSlide ?? payloadSlide ?? payloadObj;

  const payloadPageSetup =
    payloadObj.pageSetup && typeof payloadObj.pageSetup === "object"
      ? (payloadObj.pageSetup as Record<string, unknown>)
      : undefined;
  const payloadPresentation =
    payloadObj.presentation && typeof payloadObj.presentation === "object"
      ? (payloadObj.presentation as Record<string, unknown>)
      : undefined;
  const payloadPresentationRef =
    payloadObj.presentationRef && typeof payloadObj.presentationRef === "object"
      ? (payloadObj.presentationRef as Record<string, unknown>)
      : undefined;
  const slidePageSetup =
    slide.pageSetup && typeof slide.pageSetup === "object"
      ? (slide.pageSetup as Record<string, unknown>)
      : undefined;
  const presentationPageSetup =
    payloadPresentation?.pageSetup && typeof payloadPresentation.pageSetup === "object"
      ? (payloadPresentation.pageSetup as Record<string, unknown>)
      : undefined;

  const dimensionCandidates: Array<{ width: unknown; height: unknown; source: string }> = [
    {
      width: slide.slideWidth,
      height: slide.slideHeight,
      source: "activeContext.slide.slideWidth/slideHeight",
    },
    {
      width: slide.width,
      height: slide.height,
      source: "activeContext.slide.width/height",
    },
    {
      width: slide.pageWidth,
      height: slide.pageHeight,
      source: "activeContext.slide.pageWidth/pageHeight",
    },
    {
      width: slidePageSetup?.slideWidth,
      height: slidePageSetup?.slideHeight,
      source: "activeContext.slide.pageSetup.slideWidth/slideHeight",
    },
    {
      width: slidePageSetup?.pageWidth,
      height: slidePageSetup?.pageHeight,
      source: "activeContext.slide.pageSetup.pageWidth/pageHeight",
    },
    {
      width: payloadObj.slideWidth,
      height: payloadObj.slideHeight,
      source: "activeContext.slideWidth/slideHeight",
    },
    {
      width: payloadObj.pageWidth,
      height: payloadObj.pageHeight,
      source: "activeContext.pageWidth/pageHeight",
    },
    {
      width: payloadPageSetup?.slideWidth,
      height: payloadPageSetup?.slideHeight,
      source: "activeContext.pageSetup.slideWidth/slideHeight",
    },
    {
      width: payloadPageSetup?.pageWidth,
      height: payloadPageSetup?.pageHeight,
      source: "activeContext.pageSetup.pageWidth/pageHeight",
    },
    {
      width: payloadPresentation?.slideWidth,
      height: payloadPresentation?.slideHeight,
      source: "activeContext.presentation.slideWidth/slideHeight",
    },
    {
      width: payloadPresentation?.pageWidth,
      height: payloadPresentation?.pageHeight,
      source: "activeContext.presentation.pageWidth/pageHeight",
    },
    {
      width: presentationPageSetup?.slideWidth,
      height: presentationPageSetup?.slideHeight,
      source: "activeContext.presentation.pageSetup.slideWidth/slideHeight",
    },
    {
      width: presentationPageSetup?.pageWidth,
      height: presentationPageSetup?.pageHeight,
      source: "activeContext.presentation.pageSetup.pageWidth/pageHeight",
    },
  ];
  let slideWidth: number | undefined;
  let slideHeight: number | undefined;
  let dimensionSource: string | undefined;
  for (const candidate of dimensionCandidates) {
    const normalized = normalizeDimensionPairToPoints(
      candidate.width,
      candidate.height,
      candidate.source
    );
    if (normalized) {
      slideWidth = normalized.width;
      slideHeight = normalized.height;
      dimensionSource = normalized.source;
      break;
    }
  }

  const id = pickFirstNonEmptySlideId(
    slide.slideId,
    slide.slideID,
    slide.id,
    payloadObj.slideId,
    payloadObj.slideID,
    payloadObj.id
  );
  const index = readNonNegativeInteger(slide.slideIndex ?? slide.index ?? payloadObj.slideIndex ?? payloadObj.index);
  const slideNumber = readNonNegativeInteger(
    slide.slideNumber ?? slide.number ?? payloadObj.slideNumber ?? payloadObj.number
  );
  const openPresentationId = pickFirstNonEmptyString(
    payloadPresentation?.openPresentationId,
    payloadPresentation?.openPresentationID,
    payloadPresentationRef?.openPresentationId,
    payloadPresentationRef?.openPresentationID,
    payloadObj.openPresentationId,
    payloadObj.openPresentationID
  );
  const presentationName = pickFirstNonEmptyString(
    payloadPresentation?.name,
    payloadPresentation?.fileName,
    payloadPresentation?.title,
    payloadPresentationRef?.name,
    payloadObj.presentationName
  );
  const presentationFullName = pickFirstNonEmptyString(
    payloadPresentation?.fullName,
    payloadPresentation?.fullPath,
    payloadPresentation?.path,
    payloadPresentationRef?.fullName,
    payloadObj.presentationFullName
  );
  if (
    !id &&
    typeof index !== "number" &&
    !(slideWidth && slideHeight) &&
    !openPresentationId
  ) {
    return null;
  }
  return {
    id,
    index,
    slideNumber,
    slideWidth,
    slideHeight,
    dimensionSource,
    openPresentationId,
    presentationName,
    presentationFullName,
  };
}

function readBooleanFlag(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
    return undefined;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "selected", "active", "current"].includes(normalized)) return true;
    if (["0", "false", "no"].includes(normalized)) return false;
  }
  return undefined;
}

function extractActiveSlideIdFromSlideList(raw: unknown): string | undefined {
  if (!raw) return undefined;
  if (Array.isArray(raw)) {
    for (const slide of raw) {
      if (!slide || typeof slide !== "object") continue;
      const slideObj = slide as Record<string, unknown>;
      const activeFlag =
        readBooleanFlag(slideObj.isActive) ??
        readBooleanFlag(slideObj.active) ??
        readBooleanFlag(slideObj.isSelected) ??
        readBooleanFlag(slideObj.selected) ??
        readBooleanFlag(slideObj.isCurrent) ??
        readBooleanFlag(slideObj.current);
      if (activeFlag) {
        const fromSlide = pickFirstNonEmptySlideId(slideObj.slideId, slideObj.id, slideObj.slideID);
        if (fromSlide) return fromSlide;
      }
    }
    return undefined;
  }
  if (typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;

  if (typeof obj.slidesInformation === "string") {
    try {
      const parsed = JSON.parse(obj.slidesInformation);
      const fromParsed = extractActiveSlideIdFromSlideList(parsed);
      if (fromParsed) return fromParsed;
    } catch {}
  } else if (obj.slidesInformation && typeof obj.slidesInformation === "object") {
    const fromParsed = extractActiveSlideIdFromSlideList(obj.slidesInformation);
    if (fromParsed) return fromParsed;
  }

  const directId = pickFirstNonEmptySlideId(
    obj.activeSlideId,
    obj.selectedSlideId,
    obj.currentSlideId,
    obj.activeSlideID,
    obj.selectedSlideID,
    obj.currentSlideID
  );
  if (directId) return directId;

  const selectedSlideNumber =
    readNonNegativeInteger(obj.activeSlideNumber) ??
    readNonNegativeInteger(obj.selectedSlideNumber) ??
    readNonNegativeInteger(obj.currentSlideNumber);

  const slidesSource = Array.isArray(obj.slides) ? (obj.slides as unknown[]) : [];
  if (typeof selectedSlideNumber === "number" && slidesSource.length > 0) {
    for (const slide of slidesSource) {
      if (!slide || typeof slide !== "object") continue;
      const slideObj = slide as Record<string, unknown>;
      const slideNumber =
        readNonNegativeInteger(slideObj.number) ??
        (typeof slideObj.index === "number" && Number.isFinite(slideObj.index)
          ? Math.trunc(slideObj.index) + 1
          : undefined);
      if (slideNumber === selectedSlideNumber) {
        const fromNumber = pickFirstNonEmptySlideId(slideObj.slideId, slideObj.id, slideObj.slideID);
        if (fromNumber) return fromNumber;
      }
    }
  }

  if (slidesSource.length > 0) {
    return extractActiveSlideIdFromSlideList(slidesSource);
  }
  return undefined;
}

function extractSlideInfos(raw: unknown): SlideInfo[] {
  if (!raw || typeof raw !== "object") return [];
  const obj = raw as Record<string, unknown>;

  let slides: Record<string, unknown>[] = [];
  if (typeof obj.slidesInformation === "string") {
    try {
      const parsed = JSON.parse(obj.slidesInformation);
      const parsedSlides = parsed?.slides ?? (Array.isArray(parsed) ? parsed : []);
      if (Array.isArray(parsedSlides)) slides = parsedSlides as Record<string, unknown>[];
    } catch {}
  } else if (typeof obj.slidesInformation === "object" && obj.slidesInformation !== null) {
    const info = obj.slidesInformation as Record<string, unknown>;
    const maybeSlides = info.slides ?? info;
    if (Array.isArray(maybeSlides)) slides = maybeSlides as Record<string, unknown>[];
  } else if (Array.isArray(obj.slides)) {
    slides = obj.slides as Record<string, unknown>[];
  }

  const normalizeTags = (raw: unknown): Record<string, string> | undefined => {
    if (!raw || typeof raw !== "object") return undefined;
    const entries = Object.entries(raw as Record<string, unknown>).filter(
      ([key, value]) => typeof key === "string" && typeof value === "string"
    ) as [string, string][];
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
  };

  const firstSlideNumber = readNonNegativeInteger(slides[0]?.number);

  return slides
    .map((s, idx) => {
      const slideId = pickFirstNonEmptySlideId(s.slideId, s.id, s.slideID) ?? "";
      const tags = normalizeTags(s.tags);
      const slideNumber = readNonNegativeInteger(s.number);
      const info: SlideInfo = {
        slideId,
        index:
          typeof s.index === "number" && Number.isFinite(s.index)
            ? Math.trunc(s.index)
            : typeof slideNumber === "number" && typeof firstSlideNumber === "number"
              ? slideNumber - firstSlideNumber
              : idx,
      };
      if (typeof slideNumber === "number") info.slideNumber = slideNumber;
      if (tags) info.tags = tags;
      return info;
    })
    .filter((s) => s.slideId.length > 0);
}

function parseSlideCountResult(raw: unknown): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "number") {
    return readNonNegativeInteger(raw);
  }
  if (typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;

  const direct = readNonNegativeInteger(obj.slideCount ?? obj.SlideCount ?? obj.count);
  if (typeof direct === "number") return direct;

  // Defensive: some host builds nest the value under `context` even though
  // `raiseIntent` already unwraps `result.context`. Try one extra level.
  const nestedContext =
    obj.context && typeof obj.context === "object" ? (obj.context as Record<string, unknown>) : undefined;
  if (nestedContext) {
    const nested = readNonNegativeInteger(
      nestedContext.slideCount ?? nestedContext.SlideCount ?? nestedContext.count
    );
    if (typeof nested === "number") return nested;
  }

  return undefined;
}

function cloneSlideInfos(slideInfos: SlideInfo[]): SlideInfo[] {
  return slideInfos.map((slideInfo) => ({ ...slideInfo }));
}

function getCachedSlideInfos(): SlideInfo[] | null {
  if (!intentBridgeCache.slideInfos || !isFresh(intentBridgeCache.slideInfosFetchedAt, SLIDE_INFO_CACHE_TTL_MS)) {
    return null;
  }
  return cloneSlideInfos(intentBridgeCache.slideInfos);
}

function setCachedSlideInfos(slideInfos: SlideInfo[]): void {
  intentBridgeCache.slideInfos = cloneSlideInfos(slideInfos);
  intentBridgeCache.slideInfosFetchedAt = nowMs();
}

function setCachedActiveSlideId(activeSlideId?: string): void {
  intentBridgeCache.activeSlideId = activeSlideId;
  intentBridgeCache.activeSlideIdFetchedAt = nowMs();
}

function cloneSlideRef(slideRef: SlideRef): SlideRef {
  return { ...slideRef };
}

function getCachedSelectedSlideRef(): SlideRef | null {
  if (
    !intentBridgeCache.selectedSlideRef ||
    !isFresh(intentBridgeCache.selectedSlideRefFetchedAt, SELECTED_SLIDE_REF_CACHE_TTL_MS)
  ) {
    return null;
  }
  return cloneSlideRef(intentBridgeCache.selectedSlideRef);
}

function getLastKnownSelectedSlideRef(): SlideRef | null {
  return intentBridgeCache.selectedSlideRef ? cloneSlideRef(intentBridgeCache.selectedSlideRef) : null;
}

function setCachedSelectedSlideRef(slideRef: SlideRef): void {
  intentBridgeCache.selectedSlideRef = cloneSlideRef(slideRef);
  intentBridgeCache.selectedSlideRefFetchedAt = nowMs();
}

function clonePresentationContent(presentation: PresentationContent): PresentationContent {
  return JSON.parse(JSON.stringify(presentation)) as PresentationContent;
}

function getCachedPresentation(): PresentationContent | null {
  if (
    !intentBridgeCache.presentation ||
    !isFresh(intentBridgeCache.presentationFetchedAt, PRESENTATION_CACHE_TTL_MS)
  ) {
    return null;
  }
  return clonePresentationContent(intentBridgeCache.presentation);
}

function setCachedPresentation(presentation: PresentationContent): void {
  intentBridgeCache.presentation = clonePresentationContent(presentation);
  intentBridgeCache.presentationFetchedAt = nowMs();
}

function setCachedSlideBounds(bounds: SlideBounds, source: string): void {
  intentBridgeCache.slideBounds = { ...bounds };
  intentBridgeCache.slideBoundsFetchedAt = nowMs();
  lastSlideBoundsResolutionSource = source;
}

function buildSlideInfoLookups(slideInfos: SlideInfo[]): SlideInfoLookups {
  const byNormalizedId = new Map<string, SlideInfo>();
  const byIndex = new Map<number, SlideInfo>();

  for (const info of slideInfos) {
    byNormalizedId.set(normalizeSlideIdentity(info.slideId), info);
    byIndex.set(info.index, info);
  }

  return { byNormalizedId, byIndex };
}

function extractSlideImageResult(raw: unknown): { imageBase64: string; slideId?: string } {
  if (!raw || typeof raw !== "object") {
    return { imageBase64: "" };
  }
  const obj = raw as Record<string, unknown>;
  const imageBase64 = typeof obj.imageBase64 === "string" ? obj.imageBase64 : "";
  const slideId =
    obj.slideId === null || obj.slideId === undefined ? undefined : String(obj.slideId);
  return { imageBase64, slideId };
}

function normalizePresentationRef(
  ref?: Partial<FactSetPresentationRef> | null
): FactSetPresentationRef | undefined {
  const openPresentationId = pickFirstNonEmptyString(ref?.openPresentationId);
  const name = pickFirstNonEmptyString(ref?.name);
  const fullName = pickFirstNonEmptyString(ref?.fullName);
  if (!openPresentationId) {
    return undefined;
  }
  return {
    openPresentationId,
    ...(name ? { name } : {}),
    ...(fullName ? { fullName } : {}),
  };
}

function toIntentPresentationRef(
  ref?: Partial<FactSetPresentationRef> | null
): { openPresentationId: string } | undefined {
  const normalizedRef = normalizePresentationRef(ref);
  if (!normalizedRef?.openPresentationId) {
    return undefined;
  }

  // Host support is reliable for the active-session id. Keep `name/fullName`
  // as display-only metadata and never send them in outbound intent context.
  return { openPresentationId: normalizedRef.openPresentationId };
}

function describePresentationRef(ref: Partial<FactSetPresentationRef>): string {
  return ref.openPresentationId ?? ref.fullName ?? ref.name ?? "unknown presentation";
}

export async function runWithPresentationRefViaIntents<T>(
  ref: Partial<FactSetPresentationRef> | null | undefined,
  work: () => Promise<T>,
  log?: Logger
): Promise<T> {
  const normalizedRef = normalizePresentationRef(ref);
  if (normalizedRef) {
    logMessage(
      log,
      `Using FactSet presentation ${describePresentationRef(normalizedRef)} for explicit deck operations.`
    );
  }
  return work();
}

function withPresentationRef(
  intentId: IntentId,
  context: Record<string, unknown>,
  presentationRef?: Partial<FactSetPresentationRef> | null
): Record<string, unknown> {
  const ref = toIntentPresentationRef(presentationRef);
  if (!ref) {
    return context;
  }

  if (!PRESENTATION_REF_INTENTS.has(intentId)) {
    return context;
  }

  return {
    ...context,
    presentationRef: ref,
  };
}

/** Narrow arbitrary host payloads to object records before reading bridge fields. */
function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

/** Deduplicate and validate PresLink shape refs before sending them to FactSet intents. */
function normalizePreslinkShapeRefs(shapeRefs: FactSetPreslinkShapeRef[]): FactSetPreslinkShapeRef[] {
  const normalized = new Map<string, FactSetPreslinkShapeRef>();
  for (const rawRef of shapeRefs ?? []) {
    const id = pickFirstNonEmptyString(rawRef?.id);
    const slideId = pickFirstNonEmptyString(rawRef?.slideId);
    if (!id || !slideId) {
      continue;
    }
    const name = pickFirstNonEmptyString(rawRef?.name);
    const key = `${slideId}\u0000${id}`;
    const existing = normalized.get(key);
    if (existing) {
      if (!existing.name && name) {
        normalized.set(key, { ...existing, name });
      }
      continue;
    }
    normalized.set(key, {
      id,
      slideId,
      ...(name ? { name } : {}),
    });
  }
  const refs = Array.from(normalized.values());
  if (refs.length === 0) {
    throw new Error("Preslink intents require at least one shape reference with id and slideId.");
  }
  return refs;
}

/** Split PresLink presentation identity between bridge context and send-intent routing metadata. */
function normalizePreslinkPresentationRef(
  ref?: Partial<FactSetPresentationRef> | null
): { contextRef?: { openPresentationId: string } | { fullName: string }; sendRef?: FactSetPresentationRef } {
  const openPresentationId = pickFirstNonEmptyString(ref?.openPresentationId);
  const fullName = pickFirstNonEmptyString(ref?.fullName);
  if (!openPresentationId && !fullName) return {};
  // `AgentToolContext.presentationRef` commonly carries both
  // `openPresentationId` (routing identity) and `fullName` (display metadata).
  // For preslink intents, always prefer the stable session id when present.
  if (openPresentationId) {
    return {
      sendRef: { openPresentationId },
    };
  }
  return {
    contextRef: { fullName: fullName as string },
  };
}

/** Read a nested object field from a loosely typed intent response. */
function getNestedRecord(source: Record<string, unknown>, key: string): Record<string, unknown> | undefined {
  const value = source[key];
  return isObjectRecord(value) ? value : undefined;
}

/** Surface documented `factset.error` payloads no matter where they appear in the bridge result. */
function throwIfFactSetErrorResult(raw: unknown, fallbackMessage: string): void {
  const queue: unknown[] = [raw];
  const seen = new Set<unknown>();
  while (queue.length > 0) {
    const current = queue.shift();
    if (!isObjectRecord(current) || seen.has(current)) {
      continue;
    }
    seen.add(current);
    const type = pickFirstNonEmptyString(current.type);
    const errorCode = pickFirstNonEmptyString(current.errorCode, current.code);
    const message = pickFirstNonEmptyString(current.message, current.errorMessage, current.failureReason);
    if (type === "factset.error" || errorCode) {
      throw new Error(errorCode && message ? `${errorCode}: ${message}` : (message ?? errorCode ?? fallbackMessage));
    }
    const context = getNestedRecord(current, "context");
    if (context) queue.push(context);
    const result = getNestedRecord(current, "result");
    if (result) queue.push(result);
  }
}

/** Locate the PresLink result wrapper across nested bridge response variants. */
function findPreslinkResultWrapper(raw: unknown, resultKey: string): Record<string, unknown> | null {
  const queue: unknown[] = [raw];
  const seen = new Set<unknown>();
  while (queue.length > 0) {
    const current = queue.shift();
    if (!isObjectRecord(current) || seen.has(current)) {
      continue;
    }
    seen.add(current);
    const nestedResult = getNestedRecord(current, resultKey);
    if (nestedResult) {
      return nestedResult;
    }
    if ("hasError" in current && ("data" in current || current.hasError === true)) {
      return current;
    }
    const data = getNestedRecord(current, "data");
    if (data && Array.isArray(data.slides)) {
      return { hasError: false, data };
    }
    const context = getNestedRecord(current, "context");
    if (context) queue.push(context);
    const result = getNestedRecord(current, "result");
    if (result) queue.push(result);
  }
  return null;
}

/** Return the PresLink data payload or throw a host-facing error with useful context. */
function unwrapPreslinkData(raw: unknown, resultKey: string, fallbackMessage: string): Record<string, unknown> {
  throwIfFactSetErrorResult(raw, fallbackMessage);
  const wrapper = findPreslinkResultWrapper(raw, resultKey);
  if (!wrapper) {
    throw new Error(`${fallbackMessage}: missing ${resultKey}.`);
  }
  const hasError = wrapper.hasError === true;
  if (hasError) {
    const message = pickFirstNonEmptyString(wrapper.message, wrapper.errorMessage, wrapper.failureReason);
    throw new Error(message ?? fallbackMessage);
  }
  const data = getNestedRecord(wrapper, "data") ?? (Array.isArray(wrapper.slides) ? wrapper : undefined);
  if (!data) {
    throw new Error(`${fallbackMessage}: missing data.`);
  }
  return data;
}

/** Normalize the file-path-only PresLink response into the model-facing bridge contract. */
function extractGetPreslinkFilePathsResult(raw: unknown): GetPreslinkFilePathsResult {
  const data = unwrapPreslinkData(
    raw,
    "getPreslinkFilePathsResult",
    "Failed to get PresLink file paths"
  );
  const slides = Array.isArray(data.slides) ? data.slides : [];
  return {
    slides: slides
      .map((slide): FactSetPreslinkFilePathSlide | null => {
        if (!isObjectRecord(slide)) return null;
        const slideId = pickFirstNonEmptySlideId(slide.slideId, slide.id);
        if (!slideId) return null;
        const shapes = Array.isArray(slide.shapes) ? slide.shapes : [];
        return {
          slideId,
          shapes: shapes
            .map((shape): FactSetPreslinkFilePathShape | null => {
              if (!isObjectRecord(shape)) return null;
              const shapeId = pickFirstNonEmptyString(shape.shapeId, shape.id);
              if (!shapeId) return null;
              const files = Array.isArray(shape.files) ? shape.files : [];
              return {
                shapeId,
                files: files
                  .map((file): FactSetPreslinkLinkedFilePath | null => {
                    if (!isObjectRecord(file)) return null;
                    const filePath = pickFirstNonEmptyString(file.filePath, file.path);
                    return filePath ? { filePath } : null;
                  })
                  .filter((file): file is FactSetPreslinkLinkedFilePath => Boolean(file)),
              };
            })
            .filter((shape): shape is FactSetPreslinkFilePathShape => Boolean(shape)),
        };
      })
      .filter((slide): slide is FactSetPreslinkFilePathSlide => Boolean(slide)),
  };
}

/** Normalize the full PresLink file-fetch response, including linked workbook bytes. */
function extractFetchPreslinkFilesResult(raw: unknown): FetchPreslinkFilesResult {
  const data = unwrapPreslinkData(raw, "fetchPreslinkFilesResult", "Failed to fetch PresLink files");
  const presentationRaw = getNestedRecord(data, "presentation") ?? {};
  const presentationId = pickFirstNonEmptyString(
    presentationRaw.presentationId,
    presentationRaw.openPresentationId,
    presentationRaw.id
  );
  const presentationFileName = pickFirstNonEmptyString(presentationRaw.fileName, presentationRaw.name);
  const presentation: FactSetPreslinkPresentationFile = {
    ...(presentationId ? { presentationId } : {}),
    zipBase64: typeof presentationRaw.zipBase64 === "string" ? presentationRaw.zipBase64 : "",
    ...(presentationFileName ? { fileName: presentationFileName } : {}),
  };

  const linkedFilesRaw = Array.isArray(data.linkedFiles) ? data.linkedFiles : [];
  const linkedFiles = linkedFilesRaw
    .map((file): FactSetPreslinkLinkedFile | null => {
      if (!isObjectRecord(file)) return null;
      const linkKey = pickFirstNonEmptyString(file.linkKey, file.key);
      if (!linkKey) return null;
      const fileName = pickFirstNonEmptyString(file.fileName, file.name);
      return {
        linkKey,
        zipBase64: typeof file.zipBase64 === "string" ? file.zipBase64 : "",
        ...(fileName ? { fileName } : {}),
      };
    })
    .filter((file): file is FactSetPreslinkLinkedFile => Boolean(file));

  const slidesRaw = Array.isArray(data.slides) ? data.slides : [];
  const slides = slidesRaw
    .map((slide): FactSetPreslinkFilesSlide | null => {
      if (!isObjectRecord(slide)) return null;
      const slideId = pickFirstNonEmptySlideId(slide.slideId, slide.id);
      if (!slideId) return null;
      const shapesRaw = Array.isArray(slide.shapes) ? slide.shapes : [];
      return {
        slideId,
        shapes: shapesRaw
          .map((shape): FactSetPreslinkShapeLinkKeys | null => {
            if (!isObjectRecord(shape)) return null;
            const shapeId = pickFirstNonEmptyString(shape.shapeId, shape.id);
            if (!shapeId) return null;
            const linkKeys = Array.isArray(shape.linkKeys)
              ? shape.linkKeys
                  .map((linkKey) => pickFirstNonEmptyString(linkKey))
                  .filter((linkKey): linkKey is string => Boolean(linkKey))
              : [];
            return { shapeId, linkKeys };
          })
          .filter((shape): shape is FactSetPreslinkShapeLinkKeys => Boolean(shape)),
      };
    })
    .filter((slide): slide is FactSetPreslinkFilesSlide => Boolean(slide));

  return { presentation, linkedFiles, slides };
}

function buildPresentationRefFromActiveContext(
  activeSlideContext?: ActiveSlideContextInfo | null
): FactSetPresentationRef | undefined {
  return normalizePresentationRef({
    openPresentationId: activeSlideContext?.openPresentationId,
    name: activeSlideContext?.presentationName,
    fullName: activeSlideContext?.presentationFullName,
  });
}

function buildSelectedSlideRefFromActiveContext(
  activeSlideContext?: ActiveSlideContextInfo | null
): SlideRef | null {
  if (typeof activeSlideContext?.index !== "number") {
    return null;
  }

  return {
    id: activeSlideContext.id ?? "",
    index: activeSlideContext.index,
    ...(typeof activeSlideContext.slideNumber === "number"
      ? { slideNumber: activeSlideContext.slideNumber }
      : {}),
    ...(activeSlideContext.openPresentationId
      ? { openPresentationId: activeSlideContext.openPresentationId }
      : {}),
  };
}

interface FactSetSlideImageBatchItem {
  slideId?: string;
  imageBase64: string;
  width?: number;
  height?: number;
}

function normalizeSlideImageBatchItem(raw: unknown): FactSetSlideImageBatchItem | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  const imageBase64 =
    pickFirstNonEmptyString(obj.imageBase64, obj.base64Image, obj.base64, obj.thumbnail) ?? "";
  const slideId = pickFirstNonEmptySlideId(obj.slideId, obj.slideID, obj.id);
  const width = readNonNegativeInteger(obj.width);
  const height = readNonNegativeInteger(obj.height);
  if (!imageBase64 && !slideId) {
    return null;
  }
  const item: FactSetSlideImageBatchItem = { imageBase64 };
  if (slideId) item.slideId = slideId;
  if (typeof width === "number") item.width = width;
  if (typeof height === "number") item.height = height;
  return item;
}

function extractSlideImagesResult(raw: unknown): {
  images: FactSetSlideImageBatchItem[];
  errors: unknown[];
} {
  const queue: unknown[] = [raw];
  while (queue.length > 0) {
    const current = queue.shift();
    if (current == null) {
      continue;
    }
    if (typeof current === "string") {
      const trimmed = current.trim();
      if (!trimmed) {
        continue;
      }
      try {
        queue.push(JSON.parse(trimmed));
      } catch {
        // Ignore non-JSON strings while scanning nested payloads.
      }
      continue;
    }
    if (Array.isArray(current)) {
      const images = current
        .map((entry) => normalizeSlideImageBatchItem(entry))
        .filter((entry): entry is FactSetSlideImageBatchItem => !!entry);
      if (images.length > 0) {
        return { images, errors: [] };
      }
      continue;
    }
    if (typeof current !== "object") {
      continue;
    }
    const obj = current as Record<string, unknown>;
    const rawImages = Array.isArray(obj.data)
      ? obj.data
      : Array.isArray(obj.images)
        ? obj.images
        : Array.isArray(obj.slideImages)
          ? obj.slideImages
          : null;
    if (rawImages) {
      return {
        images: rawImages
          .map((entry) => normalizeSlideImageBatchItem(entry))
          .filter((entry): entry is FactSetSlideImageBatchItem => !!entry),
        errors: Array.isArray(obj.errors) ? obj.errors : [],
      };
    }
    for (const nested of [obj.result, obj.context, obj.payload]) {
      if (nested != null) {
        queue.push(nested);
      }
    }
  }
  return { images: [], errors: [] };
}

function extractExportPresentationBase64(raw: unknown): string {
  const queue: unknown[] = [raw];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== "object") {
      continue;
    }
    const obj = current as Record<string, unknown>;
    const direct = pickFirstNonEmptyString(
      obj.presentationAsBase64,
      obj.base64File,
      obj.fileAsBase64,
      obj.pptxAsBase64,
      obj.base64
    );
    if (direct) {
      return direct;
    }

    for (const nested of [obj.result, obj.context, obj.data, obj.payload]) {
      if (!nested) {
        continue;
      }
      if (typeof nested === "string") {
        const trimmed = nested.trim();
        if (!trimmed) {
          continue;
        }
        try {
          queue.push(JSON.parse(trimmed));
          continue;
        } catch {
          // Keep a conservative fallback for unexpected raw-base64 payloads.
          if (trimmed.length > 128 && !trimmed.includes("{") && !trimmed.includes(" ")) {
            return trimmed;
          }
        }
      } else if (typeof nested === "object") {
        queue.push(nested);
      }
    }
  }
  return "";
}

export interface FactSetInsertedSlideRef {
  insertedSlideId: string;
  insertedSlideIndex?: number;
}

export interface FactSetSelectedShapeRef {
  id: string;
  name?: string;
  slideId?: string;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
}

export interface FactSetSelectedShapesResult {
  totalShapes?: number;
  shapes: FactSetSelectedShapeRef[];
}

function selectedShapeRecordsMatch(
  left: FactSetSelectedShapeRef,
  right: FactSetSelectedShapeRef,
): boolean {
  if (left.id !== right.id) return false;
  if (!left.slideId || !right.slideId) return true;
  return left.slideId === right.slideId;
}

function mergeSelectedShapeRecord(
  first: FactSetSelectedShapeRef,
  second: FactSetSelectedShapeRef,
): FactSetSelectedShapeRef {
  return {
    id: first.id,
    name: first.name || second.name,
    slideId: first.slideId || second.slideId,
    left: first.left ?? second.left,
    top: first.top ?? second.top,
    width: first.width ?? second.width,
    height: first.height ?? second.height,
  };
}

function mergeSelectedShapeRefs(
  selectedShapes: FactSetSelectedShapeRef[],
): FactSetSelectedShapeRef[] {
  const merged: FactSetSelectedShapeRef[] = [];
  for (const candidate of selectedShapes) {
    const matchIndex = merged.findIndex((existing) =>
      selectedShapeRecordsMatch(existing, candidate),
    );
    if (matchIndex < 0) {
      merged.push(candidate);
      continue;
    }
    merged[matchIndex] = mergeSelectedShapeRecord(merged[matchIndex], candidate);
  }
  return merged;
}

function extractSelectedShapesResult(raw: unknown): FactSetSelectedShapesResult {
  const queue: unknown[] = [raw];
  const selectedShapes: FactSetSelectedShapeRef[] = [];
  let totalShapes: number | undefined;

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }
    if (Array.isArray(current)) {
      queue.push(...current);
      continue;
    }
    if (typeof current === "string") {
      const trimmed = current.trim();
      if (!trimmed) {
        continue;
      }
      try {
        queue.push(JSON.parse(trimmed));
      } catch {
        // ignore non-JSON string payloads
      }
      continue;
    }
    if (typeof current !== "object") {
      continue;
    }

    const obj = current as Record<string, unknown>;
    const parsedTotalShapes = readNonNegativeInteger(obj.totalShapes);
    if (typeof parsedTotalShapes === "number" && totalShapes === undefined) {
      totalShapes = parsedTotalShapes;
    }

    const shapeId = pickFirstNonEmptyString(obj.id, obj.shapeId);
    const shapeName = pickFirstNonEmptyString(obj.name, obj.shapeName);
    const slideId = pickFirstNonEmptyString(obj.slideId, obj.slideID);
    const position =
      obj.position && typeof obj.position === "object" && !Array.isArray(obj.position)
        ? (obj.position as Record<string, unknown>)
        : undefined;
    if (shapeId) {
      selectedShapes.push({
        id: shapeId,
        name: shapeName,
        slideId,
        left: readFiniteNumber(obj.left) ?? readFiniteNumber(position?.left),
        top: readFiniteNumber(obj.top) ?? readFiniteNumber(position?.top),
        width: readPositiveNumber(obj.width) ?? readPositiveNumber(position?.width),
        height: readPositiveNumber(obj.height) ?? readPositiveNumber(position?.height),
      });
      continue;
    }

    for (const nested of [
      obj.result,
      obj.context,
      obj.data,
      obj.payload,
      obj.selectedShapes,
      obj.shapes,
      obj.firstShape,
    ]) {
      if (nested !== undefined && nested !== null) {
        queue.push(nested);
      }
    }
  }

  const shapes = mergeSelectedShapeRefs(selectedShapes);
  if (totalShapes === undefined && shapes.length > 0) {
    totalShapes = shapes.length;
  }
  return {
    totalShapes,
    shapes,
  };
}

function extractInsertedSlidesFromBase64Result(raw: unknown): FactSetInsertedSlideRef[] {
  const queue: unknown[] = [raw];
  const insertedSlides: FactSetInsertedSlideRef[] = [];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }

    if (Array.isArray(current)) {
      queue.push(...current);
      continue;
    }

    if (typeof current === "string") {
      const trimmed = current.trim();
      if (!trimmed) {
        continue;
      }
      try {
        queue.push(JSON.parse(trimmed));
      } catch {
        // ignore non-JSON string payloads
      }
      continue;
    }

    if (typeof current !== "object") {
      continue;
    }

    const obj = current as Record<string, unknown>;
    const insertedSlideId = pickFirstNonEmptyString(obj.insertedSlideId, obj.slideId, obj.id);
    const insertedSlideIndex = readNonNegativeInteger(
      obj.insertedSlideIndex ?? obj.slideIndex ?? obj.index
    );
    if (insertedSlideId) {
      insertedSlides.push({
        insertedSlideId,
        insertedSlideIndex,
      });
      continue;
    }

    for (const nested of [
      obj.result,
      obj.context,
      obj.data,
      obj.payload,
      obj.results,
      obj.insertedSlides,
      obj.slides,
    ]) {
      if (nested !== undefined && nested !== null) {
        queue.push(nested);
      }
    }
  }

  return dedupeInsertedSlideRefs(insertedSlides);
}

function dedupeInsertedSlideRefs(insertedSlides: FactSetInsertedSlideRef[]): FactSetInsertedSlideRef[] {
  const deduped = new Map<string, FactSetInsertedSlideRef>();
  for (const inserted of insertedSlides) {
    const existing = deduped.get(inserted.insertedSlideId);
    if (!existing) {
      deduped.set(inserted.insertedSlideId, inserted);
      continue;
    }
    if (existing.insertedSlideIndex === undefined && inserted.insertedSlideIndex !== undefined) {
      deduped.set(inserted.insertedSlideId, inserted);
    }
  }
  return Array.from(deduped.values());
}

function filterInsertedSlideRefsToNewSlides(
  insertedSlides: FactSetInsertedSlideRef[],
  preInsertSlideInfos: SlideInfo[]
): FactSetInsertedSlideRef[] {
  if (preInsertSlideInfos.length === 0) {
    return insertedSlides;
  }

  const previousIds = new Set(preInsertSlideInfos.map((slide) => slide.slideId));
  return insertedSlides.filter((inserted) => !previousIds.has(inserted.insertedSlideId));
}

function detectInsertedSlidesFromSlideInfoDelta(
  before: SlideInfo[],
  after: SlideInfo[]
): FactSetInsertedSlideRef[] {
  if (before.length === 0 || after.length === 0) {
    return [];
  }
  const previousIds = new Set(before.map((slide) => slide.slideId));
  const insertedSlides = after
    .filter((slide) => !previousIds.has(slide.slideId))
    .map((slide) => ({
      insertedSlideId: slide.slideId,
      insertedSlideIndex: slide.index,
    }))
    .sort((left, right) => {
      const leftIndex = left.insertedSlideIndex ?? Number.MAX_SAFE_INTEGER;
      const rightIndex = right.insertedSlideIndex ?? Number.MAX_SAFE_INTEGER;
      return leftIndex - rightIndex;
    });
  return dedupeInsertedSlideRefs(insertedSlides);
}

async function inferInsertedSlidesFromCurrentDelta(
  preInsertSlideInfos: SlideInfo[],
  log?: Logger,
  presentationRef?: Partial<FactSetPresentationRef> | null
): Promise<FactSetInsertedSlideRef[]> {
  if (preInsertSlideInfos.length === 0) {
    return [];
  }
  try {
    const postInsertSlideInfos = await ensureSlideInfos(log, {
      forceRefresh: true,
      presentationRef,
    });
    return detectInsertedSlidesFromSlideInfoDelta(preInsertSlideInfos, postInsertSlideInfos);
  } catch (slideListError) {
    logMessage(
      log,
      `Could not infer inserted slides from slide list delta: ${toErrorMessage(slideListError)}`
    );
    return [];
  }
}

// Convert extractor dimensions into PowerPoint points (pt).
const EMU_PER_POINT = 12700;
const POINTS_PER_INCH = 72;
const PIXELS_PER_INCH = 96;
const PX_TO_PT = POINTS_PER_INCH / PIXELS_PER_INCH;
const LIKELY_EMU_THRESHOLD_PT = 5000;
const LIKELY_INCH_DIMENSION_MAX = 30;
const LIKELY_PIXEL_WIDTH_MIN = 1000;
const LIKELY_PIXEL_HEIGHT_MIN = 560;
// Keep profile matching tight so near-miss unit values don't get mislabeled.
const DIMENSION_MATCH_TOLERANCE = 0.25;
const COMMON_POINT_DIMENSIONS: Array<{ width: number; height: number }> = [
  { width: 960, height: 540 }, // PowerPoint 16:9 default
  { width: 720, height: 540 }, // PowerPoint 4:3 default
  { width: 1008, height: 567 }, // Common 14"x7.875" custom widescreen
];
const COMMON_PIXEL_DIMENSIONS: Array<{ width: number; height: number }> = [
  { width: 1024, height: 576 },
  { width: 1152, height: 648 },
  { width: 1280, height: 720 },
  { width: 1366, height: 768 },
  { width: 1440, height: 810 },
  { width: 1536, height: 864 },
  { width: 1600, height: 900 },
  { width: 1920, height: 1080 },
];

function toFinitePositiveNumber(value: unknown): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const normalized = trimmed.replace(/,/g, "");
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const nested =
      toFinitePositiveNumber(obj.value) ??
      toFinitePositiveNumber(obj.amount) ??
      toFinitePositiveNumber(obj.numericValue) ??
      toFinitePositiveNumber(obj.number);
    if (nested) return nested;
  }
  return undefined;
}

function roundToTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

function isApprox(value: number, target: number, tolerance = DIMENSION_MATCH_TOLERANCE): boolean {
  return Math.abs(value - target) <= tolerance;
}

function matchesDimensionPair(
  width: number,
  height: number,
  candidates: Array<{ width: number; height: number }>
): boolean {
  return candidates.some(
    (candidate) => isApprox(width, candidate.width) && isApprox(height, candidate.height)
  );
}

function normalizeDimensionPairToPoints(
  rawWidth: unknown,
  rawHeight: unknown,
  source: string
): { width: number; height: number; source: string; geometryScale: number } | null {
  const width = toFinitePositiveNumber(rawWidth);
  const height = toFinitePositiveNumber(rawHeight);
  if (!width || !height) {
    return null;
  }

  if (width > LIKELY_EMU_THRESHOLD_PT || height > LIKELY_EMU_THRESHOLD_PT) {
    return {
      width: roundToTenth(width / EMU_PER_POINT),
      height: roundToTenth(height / EMU_PER_POINT),
      source: `${source} (EMU -> pt)`,
      geometryScale: 1 / EMU_PER_POINT,
    };
  }

  if (matchesDimensionPair(width, height, COMMON_POINT_DIMENSIONS)) {
    return {
      width: roundToTenth(width),
      height: roundToTenth(height),
      source: `${source} (known pt profile)`,
      geometryScale: 1,
    };
  }

  if (matchesDimensionPair(width, height, COMMON_PIXEL_DIMENSIONS)) {
    return {
      width: roundToTenth(width * PX_TO_PT),
      height: roundToTenth(height * PX_TO_PT),
      source: `${source} (known px profile -> pt)`,
      geometryScale: PX_TO_PT,
    };
  }

  if (width <= LIKELY_INCH_DIMENSION_MAX && height <= LIKELY_INCH_DIMENSION_MAX) {
    return {
      width: roundToTenth(width * POINTS_PER_INCH),
      height: roundToTenth(height * POINTS_PER_INCH),
      source: `${source} (inches -> pt)`,
      geometryScale: POINTS_PER_INCH,
    };
  }

  const sourceHintsPixels = source.includes("pageWidth") || source.includes("pageSetup.pageWidth");
  const looksLikePixelWidescreen =
    sourceHintsPixels &&
    width >= LIKELY_PIXEL_WIDTH_MIN &&
    height >= LIKELY_PIXEL_HEIGHT_MIN &&
    Math.abs(width / height - 16 / 9) < 0.03;
  if (looksLikePixelWidescreen) {
    return {
      width: roundToTenth(width * PX_TO_PT),
      height: roundToTenth(height * PX_TO_PT),
      source: `${source} (widescreen px heuristic -> pt)`,
      geometryScale: PX_TO_PT,
    };
  }

  return {
    width: roundToTenth(width),
    height: roundToTenth(height),
    source: `${source} (pt)`,
    geometryScale: 1,
  };
}

/**
 * Walks `designs[*]`, `designs[*].slideMaster` and `designs[*].(slideMaster.)customLayouts[*]`
 * looking for the first node that exposes a positive (width, height) pair. Used as a
 * last-resort dimension source for FactSet-host responses where `slides: []` and the
 * top-level `pageSetup` is absent (typical when extracting an unopened template).
 *
 * When multiple candidates qualify we prefer the largest area to avoid under-allocating
 * the canvas — most templates ship a single layout size, but if a host mixes 4:3 and
 * widescreen layouts we'd rather hand the LLM the bigger one and let it center.
 */
function findCustomLayoutDimensions(
  result: ExtractorResultLite
): { width: unknown; height: unknown; source: string } | undefined {
  const designs = Array.isArray(result.designs) ? result.designs : [];
  if (designs.length === 0) return undefined;

  const candidates: Array<{ width: unknown; height: unknown; source: string }> = [];
  designs.forEach((design, designIndex) => {
    if (!design || typeof design !== "object") return;
    candidates.push({
      width: design.width,
      height: design.height,
      source: `designs[${designIndex}].width/height`,
    });
    const designLayouts = Array.isArray(design.customLayouts) ? design.customLayouts : [];
    designLayouts.forEach((layout, layoutIndex) => {
      if (!layout || typeof layout !== "object") return;
      candidates.push({
        width: layout.width,
        height: layout.height,
        source: `designs[${designIndex}].customLayouts[${layoutIndex}].width/height`,
      });
    });
    const master = design.slideMaster;
    if (master && typeof master === "object") {
      candidates.push({
        width: master.width,
        height: master.height,
        source: `designs[${designIndex}].slideMaster.width/height`,
      });
      const masterLayouts = Array.isArray(master.customLayouts) ? master.customLayouts : [];
      masterLayouts.forEach((layout, layoutIndex) => {
        if (!layout || typeof layout !== "object") return;
        candidates.push({
          width: layout.width,
          height: layout.height,
          source: `designs[${designIndex}].slideMaster.customLayouts[${layoutIndex}].width/height`,
        });
      });
    }
  });

  let best: { width: unknown; height: unknown; source: string; area: number } | undefined;
  for (const candidate of candidates) {
    const w = typeof candidate.width === "number" && Number.isFinite(candidate.width) ? candidate.width : 0;
    const h = typeof candidate.height === "number" && Number.isFinite(candidate.height) ? candidate.height : 0;
    if (w <= 0 || h <= 0) continue;
    const area = w * h;
    if (!best || area > best.area) {
      best = { width: w, height: h, source: candidate.source, area };
    }
  }
  if (!best) return undefined;
  return { width: best.width, height: best.height, source: best.source };
}

function resolveExtractorSlideDimensions(
  result: ExtractorResultLite
): { width: number; height: number; source: string; geometryScale: number } {
  const resultSlide =
    result.slide && typeof result.slide === "object"
      ? (result.slide as Record<string, unknown>)
      : undefined;
  const resultSlidePageSetup =
    resultSlide?.pageSetup && typeof resultSlide.pageSetup === "object"
      ? (resultSlide.pageSetup as Record<string, unknown>)
      : undefined;
  const resultPresentation =
    result.presentation && typeof result.presentation === "object"
      ? (result.presentation as Record<string, unknown>)
      : undefined;
  const resultPresentationPageSetup =
    resultPresentation?.pageSetup && typeof resultPresentation.pageSetup === "object"
      ? (resultPresentation.pageSetup as Record<string, unknown>)
      : undefined;

  const candidates: Array<{ width: unknown; height: unknown; source: string }> = [
    {
      width: result.pageSetup?.slideWidth,
      height: result.pageSetup?.slideHeight,
      source: "pageSetup.slideWidth/slideHeight",
    },
    {
      width: resultSlidePageSetup?.slideWidth,
      height: resultSlidePageSetup?.slideHeight,
      source: "slide.pageSetup.slideWidth/slideHeight",
    },
    { width: resultSlide?.width, height: resultSlide?.height, source: "slide.width/height" },
    {
      width: resultSlide?.slideWidth,
      height: resultSlide?.slideHeight,
      source: "slide.slideWidth/slideHeight",
    },
    { width: result.slideWidth, height: result.slideHeight, source: "slideWidth/slideHeight" },
    { width: result.pageWidth, height: result.pageHeight, source: "pageWidth/pageHeight" },
    {
      width: resultSlide?.pageWidth,
      height: resultSlide?.pageHeight,
      source: "slide.pageWidth/pageHeight",
    },
    {
      width: result.pageSetup?.pageWidth,
      height: result.pageSetup?.pageHeight,
      source: "pageSetup.pageWidth/pageHeight",
    },
    {
      width: resultSlidePageSetup?.pageWidth,
      height: resultSlidePageSetup?.pageHeight,
      source: "slide.pageSetup.pageWidth/pageHeight",
    },
    {
      width: resultPresentation?.slideWidth,
      height: resultPresentation?.slideHeight,
      source: "presentation.slideWidth/slideHeight",
    },
    {
      width: resultPresentation?.width,
      height: resultPresentation?.height,
      source: "presentation.width/height",
    },
    {
      width: resultPresentation?.pageWidth,
      height: resultPresentation?.pageHeight,
      source: "presentation.pageWidth/pageHeight",
    },
    {
      width: resultPresentationPageSetup?.slideWidth,
      height: resultPresentationPageSetup?.slideHeight,
      source: "presentation.pageSetup.slideWidth/slideHeight",
    },
    {
      width: resultPresentationPageSetup?.pageWidth,
      height: resultPresentationPageSetup?.pageHeight,
      source: "presentation.pageSetup.pageWidth/pageHeight",
    },
  ];

  // Append the design / custom-layout fallback last so it only fires when every
  // explicit `pageSetup` / `slideWidth` path is absent — that protects decks where the
  // host already returned authoritative dimensions, while still rescuing template-only
  // payloads (slides:[]) such as the Rothschild "Long – A4 – EN" file at 780x540.
  const layoutFallback = findCustomLayoutDimensions(result);
  if (layoutFallback) {
    candidates.push(layoutFallback);
  }

  for (const candidate of candidates) {
    const normalized = normalizeDimensionPairToPoints(
      candidate.width,
      candidate.height,
      candidate.source
    );
    if (normalized) {
      return normalized;
    }
  }

  return {
    width: DEFAULT_SLIDE_WIDTH_PT,
    height: DEFAULT_SLIDE_HEIGHT_PT,
    source: "default (FactSet extractor did not expose slide dimensions)",
    geometryScale: 1,
  };
}

let slideDimensionDiagnosticLogged = false;
interface ExtractorSlideDimensionMetadata {
  width: number;
  height: number;
  source: string;
  geometryScale: number;
  extractorKeys: string;
  capturedAt: number;
}

let lastExtractorSlideDimensionMetadata: ExtractorSlideDimensionMetadata | null = null;

function scaleExtractorCoordinate(raw: unknown, geometryScale: number): number {
  const numeric = typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
  return roundToTenth(numeric * geometryScale);
}

function readExtractorFontSize(raw: unknown): number | undefined {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) return undefined;
  return roundToTenth(raw);
}

function normalizeExtractorHorizontalAlignment(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const normalized = trimmed.toLowerCase().replace(/[\s_-]+/g, "");
  if (normalized.includes("justify")) return "Justify";
  if (normalized.includes("center") || normalized.includes("centre")) return "Center";
  if (normalized.includes("right")) return "Right";
  if (normalized.includes("left")) return "Left";
  if (normalized.includes("distributed")) return "Distributed";
  return trimmed;
}

function normalizeExtractorVerticalAlignment(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const normalized = trimmed.toLowerCase().replace(/[\s_-]+/g, "");
  if (normalized.includes("middle") || normalized.includes("center") || normalized.includes("centre")) {
    return "Middle";
  }
  if (normalized.includes("bottom")) return "Bottom";
  if (normalized.includes("top")) return "Top";
  return trimmed;
}

function normalizeExtractedColorToken(value: unknown): string | undefined {
  const token = extractColorToken(value);
  if (!token) return undefined;
  return normalizeBatchColorToken(token) ?? token;
}

function readThemeColorFromScheme(
  scheme: Record<string, unknown> | undefined,
  keys: string[],
  fallback: string
): string {
  for (const key of keys) {
    const color = normalizeExtractedColorToken(scheme?.[key]);
    if (color) return color;
  }
  return fallback;
}

function extractFontNameCandidate(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (!value || typeof value !== "object") return undefined;
  const obj = value as Record<string, unknown>;
  return (
    extractFontNameCandidate(obj.typeface) ??
    extractFontNameCandidate(obj.latin) ??
    extractFontNameCandidate(obj.name)
  );
}

function readThemeFontFromScheme(
  scheme: Record<string, unknown> | undefined,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const fontName = extractFontNameCandidate(scheme?.[key]);
    if (fontName) return fontName;
  }
  return undefined;
}

function normalizePlaceholderTypeToken(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase().replace(/[\s_-]+/g, "");
  return normalized.length > 0 ? normalized : undefined;
}

function isTitlePlaceholderType(value: unknown): boolean {
  const normalized = normalizePlaceholderTypeToken(value);
  return typeof normalized === "string" && normalized.includes("title");
}

function isBodyPlaceholderType(value: unknown): boolean {
  const normalized = normalizePlaceholderTypeToken(value);
  if (!normalized || normalized.includes("title")) return false;
  return (
    normalized.includes("body") ||
    normalized.includes("object") ||
    normalized.includes("content") ||
    normalized.includes("text")
  );
}

function findFirstPlaceholderRunFont(
  paragraph: ExtractorParagraphLite | undefined
): ExtractorFontLite | undefined {
  const runs = Array.isArray(paragraph?.runs) ? paragraph.runs : [];
  for (const run of runs) {
    const font = run?.runFormat?.font;
    if (font && typeof font === "object") return font;
  }
  return undefined;
}

function extractPlaceholderFontFamilyFromShape(
  shape: ExtractorShapeLite | undefined
): string | undefined {
  const paragraphs = shape?.textFrame?.paragraphs ?? shape?.paragraphs;
  if (!Array.isArray(paragraphs)) return undefined;
  for (const paragraph of paragraphs) {
    const font = findFirstPlaceholderRunFont(paragraph);
    if (typeof font?.name === "string" && font.name.trim().length > 0) {
      return font.name.trim();
    }
  }
  return undefined;
}

function collectLayoutPlaceholderHints(
  shapes: ExtractorShapeLite[] | undefined,
  geometryScale: number,
  hints: {
    titlePlaceholder?: TitlePlaceholderHint;
    titleFont?: string;
    bodyFont?: string;
  }
): void {
  if (!Array.isArray(shapes)) return;
  for (const shape of shapes) {
    if (!hints.titlePlaceholder) {
      const titlePlaceholder = buildTitlePlaceholderHintFromShape(shape, geometryScale);
      if (titlePlaceholder) hints.titlePlaceholder = titlePlaceholder;
    }

    const placeholderType = shape?.placeholderFormat?.type;
    if (!hints.titleFont && isTitlePlaceholderType(placeholderType)) {
      hints.titleFont = extractPlaceholderFontFamilyFromShape(shape);
    }
    if (!hints.bodyFont && isBodyPlaceholderType(placeholderType)) {
      hints.bodyFont = extractPlaceholderFontFamilyFromShape(shape);
    }

    if (hints.titlePlaceholder && hints.titleFont && hints.bodyFont) {
      return;
    }
  }
}

function buildTitlePlaceholderHintFromShape(
  shape: ExtractorShapeLite | undefined,
  geometryScale: number
): TitlePlaceholderHint | undefined {
  if (!shape || !isTitlePlaceholderType(shape.placeholderFormat?.type)) {
    return undefined;
  }
  const left = scaleExtractorCoordinate(shape.left, geometryScale);
  const top = scaleExtractorCoordinate(shape.top, geometryScale);
  const width = scaleExtractorCoordinate(shape.width, geometryScale);
  const height = scaleExtractorCoordinate(shape.height, geometryScale);
  if ([left, top, width, height].some((value) => typeof value !== "number" || !Number.isFinite(value))) {
    return undefined;
  }
  return { left, top, width, height };
}

function parseDesignMetadataFromExtractor(result: ExtractorResultLite): FactSetDesignMetadata {
  const design = result.designs?.[0];
  if (!design) {
    return { themeColors: null, layoutInfo: null };
  }

  const colorScheme =
    design.slideMaster?.colorScheme ??
    design.slideMaster?.colorSchema ??
    design.colorScheme ??
    design.colorSchema;
  const fontScheme =
    design.slideMaster?.fontScheme ??
    design.slideMaster?.fontSchema ??
    design.fontScheme ??
    design.fontSchema;
  const { geometryScale } = resolveExtractorSlideDimensions(result);
  const placeholderHints: {
    titlePlaceholder?: TitlePlaceholderHint;
    titleFont?: string;
    bodyFont?: string;
  } = {};
  const customLayouts = [
    ...(Array.isArray(design.slideMaster?.customLayouts) ? design.slideMaster.customLayouts : []),
    ...(Array.isArray(design.customLayouts) ? design.customLayouts : []),
  ];
  for (const layout of customLayouts) {
    collectLayoutPlaceholderHints(layout.shapes, geometryScale, placeholderHints);
    if (placeholderHints.titlePlaceholder && placeholderHints.titleFont && placeholderHints.bodyFont) {
      break;
    }
  }
  if ((!placeholderHints.titleFont || !placeholderHints.bodyFont) && Array.isArray(design.slideMaster?.shapes)) {
    collectLayoutPlaceholderHints(design.slideMaster.shapes, geometryScale, placeholderHints);
  }
  const themeColors: ThemeColors | null =
    colorScheme || fontScheme || placeholderHints.titleFont || placeholderHints.bodyFont
      ? {
          dark1: readThemeColorFromScheme(colorScheme, ["dark1", "Dark1"], "#000000"),
          dark2: readThemeColorFromScheme(colorScheme, ["dark2", "Dark2"], "#333333"),
          light1: readThemeColorFromScheme(colorScheme, ["light1", "Light1"], "#FFFFFF"),
          light2: readThemeColorFromScheme(colorScheme, ["light2", "Light2"], "#F5F5F5"),
          accent1: readThemeColorFromScheme(colorScheme, ["accent1", "Accent1"], "#4472C4"),
          accent2: readThemeColorFromScheme(colorScheme, ["accent2", "Accent2"], "#ED7D31"),
          accent3: readThemeColorFromScheme(colorScheme, ["accent3", "Accent3"], "#A5A5A5"),
          accent4: readThemeColorFromScheme(colorScheme, ["accent4", "Accent4"], "#FFC000"),
          accent5: readThemeColorFromScheme(colorScheme, ["accent5", "Accent5"], "#5B9BD5"),
          accent6: readThemeColorFromScheme(colorScheme, ["accent6", "Accent6"], "#70AD47"),
          hyperlink: readThemeColorFromScheme(
            colorScheme,
            ["hyperlink", "Hyperlink"],
            "#0563C1"
          ),
          followedHyperlink: readThemeColorFromScheme(
            colorScheme,
            ["followedHyperlink", "FollowedHyperlink"],
            "#954F72"
          ),
          majorFont:
            placeholderHints.titleFont ??
            readThemeFontFromScheme(fontScheme, [
              "majorFont",
              "MajorFont",
              "latinMajorFont",
              "LatinMajorFont",
            ]),
          minorFont:
            placeholderHints.bodyFont ??
            readThemeFontFromScheme(fontScheme, [
              "minorFont",
              "MinorFont",
              "latinMinorFont",
              "LatinMinorFont",
            ]),
        }
      : null;
  const titlePlaceholder = placeholderHints.titlePlaceholder;
  const layoutInfo = titlePlaceholder ? { titlePlaceholder } : null;

  return { themeColors, layoutInfo };
}

function normalizeDeckLayoutPlaceholder(
  shape: ExtractorShapeLite,
  geometryScale: number
): DeckLayoutPlaceholder | null {
  if (!shape.placeholderFormat) return null;
  const type = pickFirstNonEmptyString(shape.type, shape.shapeType) ?? "Placeholder";
  const placeholder: DeckLayoutPlaceholder = {
    id: pickFirstNonEmptyString(shape.id, shape.shapeId, shape.shapeID, shape.objectId),
    name: pickFirstNonEmptyString(shape.name, shape.shapeName),
    type,
    left: scaleExtractorCoordinate(shape.left, geometryScale),
    top: scaleExtractorCoordinate(shape.top, geometryScale),
    width: scaleExtractorCoordinate(shape.width, geometryScale),
    height: scaleExtractorCoordinate(shape.height, geometryScale),
    placeholderFormat: {
      type: pickFirstNonEmptyString(shape.placeholderFormat.type),
      name: pickFirstNonEmptyString(shape.placeholderFormat.name),
      containedType: pickFirstNonEmptyString(shape.placeholderFormat.containedType),
    },
  };
  if (
    !placeholder.id &&
    !placeholder.name &&
    !placeholder.placeholderFormat?.type &&
    !placeholder.placeholderFormat?.name
  ) {
    return null;
  }
  return placeholder;
}

function cloneDeckLayoutCatalog(catalog: DeckLayoutCatalog): DeckLayoutCatalog {
  return {
    layouts: catalog.layouts.map((layout) => ({
      ...layout,
      dimensions: layout.dimensions ? { ...layout.dimensions } : undefined,
      placeholders: layout.placeholders?.map((placeholder) => ({
        ...placeholder,
        placeholderFormat: placeholder.placeholderFormat
          ? { ...placeholder.placeholderFormat }
          : undefined,
      })),
      usedBySlides: layout.usedBySlides?.map((slide) => ({ ...slide })),
    })),
  };
}

function findLayoutTypeFromSlides(
  slides: ExtractorSlideLite[] | undefined,
  layoutName: string | undefined,
  matchingName: string | undefined
): string | undefined {
  if (!Array.isArray(slides)) return undefined;
  const names = new Set(
    [layoutName, matchingName]
      .map((value) => (typeof value === "string" ? value.trim().toLowerCase() : ""))
      .filter(Boolean)
  );
  if (names.size === 0) return undefined;
  for (const slide of slides) {
    const customLayoutName = pickFirstNonEmptyString(slide.customLayoutName, slide.layout);
    if (!customLayoutName || !names.has(customLayoutName.trim().toLowerCase())) continue;
    const layoutType = pickFirstNonEmptyString(slide.layout);
    if (layoutType) return layoutType;
  }
  return undefined;
}

function findUsedBySlidesFromExtractor(
  slides: ExtractorSlideLite[] | undefined,
  layoutName: string | undefined,
  matchingName: string | undefined
): DeckLayoutUsedBySlide[] | undefined {
  if (!Array.isArray(slides)) return undefined;
  const names = new Set(
    [layoutName, matchingName]
      .map((value) => (typeof value === "string" ? value.trim().toLowerCase() : ""))
      .filter(Boolean)
  );
  if (names.size === 0) return undefined;
  const used: DeckLayoutUsedBySlide[] = [];
  for (const slide of slides) {
    const customLayoutName = pickFirstNonEmptyString(slide.customLayoutName, slide.layout);
    if (!customLayoutName || !names.has(customLayoutName.trim().toLowerCase())) continue;
    const id = pickFirstNonEmptyString(slide.slideId, slide.slideID);
    const index = readNonNegativeInteger(slide.index);
    if (!id || typeof index !== "number") continue;
    const slideNumber = readNonNegativeInteger(slide.slideNumber);
    used.push({
      id,
      index,
      ...(typeof slideNumber === "number" ? { slideNumber } : {}),
    });
  }
  return used.length > 0 ? used.slice(0, 5) : undefined;
}

function parseDeckLayoutCatalogFromExtractor(result: ExtractorResultLite): DeckLayoutCatalog {
  const { geometryScale } = resolveExtractorSlideDimensions(result);
  const layouts: DeckLayoutCatalogEntry[] = [];
  const seen = new Set<string>();
  const designs = Array.isArray(result.designs) ? result.designs : [];

  for (const design of designs) {
    const layoutSources: Array<{ layout: ExtractorCustomLayoutLite; masterName?: string }> = [
      ...(Array.isArray(design.slideMaster?.customLayouts)
        ? design.slideMaster.customLayouts.map((layout) => ({
            layout,
            masterName: pickFirstNonEmptyString(design.slideMaster?.name),
          }))
        : []),
      ...(Array.isArray(design.customLayouts)
        ? design.customLayouts.map((layout) => ({
            layout,
            masterName: pickFirstNonEmptyString(
              (layout.parentMaster as ExtractorSlideMasterLite | undefined)?.name,
              design.slideMaster?.name
            ),
          }))
        : []),
    ];

    for (const { layout, masterName } of layoutSources) {
      const layoutName = pickFirstNonEmptyString(layout.name);
      const matchingName = pickFirstNonEmptyString(layout.matchingName);
      const key = `${masterName ?? ""}::${layoutName ?? matchingName ?? ""}`;
      if (!layoutName && !matchingName) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      const placeholders = (layout.shapes || [])
        .map((shape) => normalizeDeckLayoutPlaceholder(shape, geometryScale))
        .filter((placeholder): placeholder is DeckLayoutPlaceholder => Boolean(placeholder));
      const width = scaleExtractorCoordinate(
        layout.width ?? layout.slideWidth ?? layout.pageWidth,
        geometryScale
      );
      const height = scaleExtractorCoordinate(
        layout.height ?? layout.slideHeight ?? layout.pageHeight,
        geometryScale
      );
      const entry: DeckLayoutCatalogEntry = {
        layoutName,
        matchingName,
        slideMasterName: masterName,
        layoutType: findLayoutTypeFromSlides(result.slides, layoutName, matchingName),
        dimensions:
          typeof width === "number" || typeof height === "number" ? { width, height } : undefined,
        inheritsMasterShapes: readBooleanFlag(layout.displayMasterShapes),
        followsMasterBackground: readBooleanFlag(layout.followMasterBackground),
        placeholders: placeholders.length > 0 ? placeholders : undefined,
        usedBySlides: findUsedBySlidesFromExtractor(result.slides, layoutName, matchingName),
      };
      layouts.push(entry);
    }
  }

  return { layouts };
}

function cloneDesignMetadata(metadata: FactSetDesignMetadata): FactSetDesignMetadata {
  return {
    themeColors: metadata.themeColors ? { ...metadata.themeColors } : null,
    layoutInfo: metadata.layoutInfo
      ? {
          ...metadata.layoutInfo,
          titlePlaceholder: metadata.layoutInfo.titlePlaceholder
            ? { ...metadata.layoutInfo.titlePlaceholder }
            : undefined,
        }
      : null,
  };
}

function parsePromptDesignContextFromExtractor(result: ExtractorResultLite): PromptDesignContext {
  let themeColors: ThemeColors | null = null;
  let deckLayoutCatalog: DeckLayoutCatalog | null = null;
  try {
    themeColors = parseDesignMetadataFromExtractor(result).themeColors;
  } catch {
    themeColors = null;
  }
  try {
    deckLayoutCatalog = parseDeckLayoutCatalogFromExtractor(result);
  } catch {
    deckLayoutCatalog = null;
  }
  return { themeColors, deckLayoutCatalog };
}

function clonePromptDesignContext(context: PromptDesignContext): PromptDesignContext {
  return {
    themeColors: context.themeColors ? { ...context.themeColors } : null,
    deckLayoutCatalog: context.deckLayoutCatalog
      ? cloneDeckLayoutCatalog(context.deckLayoutCatalog)
      : null,
  };
}

function findFirstRunFont(paragraph: ExtractorParagraphLite | undefined): ExtractorFontLite | undefined {
  const runs = Array.isArray(paragraph?.runs) ? paragraph.runs : [];
  for (const run of runs) {
    const font = run?.runFormat?.font;
    if (font && typeof font === "object") return font;
  }
  return undefined;
}

// Depth 0 = top-level slide shape. Depth 4 is still emitted; its nested shapes are not.
const MAX_GROUP_NESTING_DEPTH = 4;

function readExtractorShapeId(shape: ExtractorShapeLite): string {
  return String(shape.id ?? shape.shapeId ?? shape.shapeID ?? shape.objectId ?? "");
}

function mapExtractorShapeLite(
  shape: ExtractorShapeLite,
  shapeIndex: number,
  geometryScale: number,
  extractorDecisionSummary: ExtractorDecisionSummary,
  seenIds: Set<string>,
  depth: number,
  parentGroupId?: string
): ShapeContent {
  extractorDecisionSummary.shapesProcessed += 1;
  const normalizedType = normalizeExtractorShapeType(shape.type ?? shape.shapeType);
  const resolvedGeometricType = resolveExtractorGeometricType(shape);
  // Placeholder-hosted tables are reported by PowerPoint as Placeholder
  // shapes even though they carry a real `table` payload. Downstream
  // callers reason about content type, so prefer the table payload over
  // the wrapper's broad shape type.
  const type =
    shape.tableData !== undefined && shape.tableData !== null
      ? "table"
      : shape.table !== undefined && shape.table !== null
        ? "table"
        : normalizedType;
  // Mirror the OfficeJS extractor's Shape_<index> fallback when the host
  // returns an unnamed shape so downstream summaries stay consistent.
  const rawName = pickFirstNonEmptyString(shape.name, shape.shapeName);
  const name = rawName ?? `Shape_${shapeIndex}`;
  const placeholderType =
    typeof shape.placeholderFormat?.type === "string" &&
    shape.placeholderFormat.type.trim().length > 0
      ? shape.placeholderFormat.type.trim()
      : undefined;
  const left = scaleExtractorCoordinate(shape.left, geometryScale);
  const top = scaleExtractorCoordinate(shape.top, geometryScale);
  const width = scaleExtractorCoordinate(shape.width, geometryScale);
  const height = scaleExtractorCoordinate(shape.height, geometryScale);
  // Host RunOfficeExtractor nests the body of a shape inside `textFrame`
  // (text + paragraphs[*].runs[*]). Older host shapes carried `text` /
  // `paragraphs` directly on the shape, so we still honor those as a
  // fallback for backward compatibility.
  const textFrame = shape.textFrame;
  const paragraphSource = textFrame?.paragraphs ?? shape.paragraphs;
  const horizontalAlignment = normalizeExtractorHorizontalAlignment(
    paragraphSource?.[0]?.paragraphFormat?.alignment
  );
  const verticalAlignment = normalizeExtractorVerticalAlignment(
    textFrame?.textFrameFormat?.verticalAnchor ?? shape.textFrameFormat?.verticalAnchor
  );
  const paragraphs = Array.isArray(paragraphSource)
    ? paragraphSource.map((p, pIndex) => {
        const firstRunFont = findFirstRunFont(p);
        const runText = Array.isArray(p?.runs)
          ? p.runs
              .map((run) => (typeof run?.text === "string" ? run.text : ""))
              .join("")
          : "";
        const text = String(p?.text ?? runText ?? "");
        const bulletFormat = p?.paragraphFormat?.bulletFormat;
        const bulletVisible = bulletFormat?.visible === true ? true : undefined;
        return {
          index: typeof p?.index === "number" ? p.index : pIndex,
          text,
          fontSize: readExtractorFontSize(firstRunFont?.size),
          fontFamily:
            typeof firstRunFont?.name === "string" && firstRunFont.name.length > 0
              ? firstRunFont.name
              : undefined,
          textColor: extractColorToken(firstRunFont) ?? extractColorToken(firstRunFont?.color),
          bold: typeof firstRunFont?.bold === "boolean" ? firstRunFont.bold : undefined,
          italic: typeof firstRunFont?.italic === "boolean" ? firstRunFont.italic : undefined,
          bulletVisible,
          bulletType:
            bulletVisible && bulletFormat?.type !== undefined
              ? String(bulletFormat.type)
              : undefined,
          bulletStyle:
            bulletVisible && bulletFormat?.style !== undefined
              ? String(bulletFormat.style)
              : undefined,
        };
      })
    : undefined;
  const textFromParagraphs =
    paragraphs && paragraphs.length > 0
      ? paragraphs
          .map((p) => p.text)
          .join("\n")
          .trim()
      : undefined;
  const rawShapeText = pickFirstNonEmptyString(textFrame?.text, shape.text, textFromParagraphs);
  const shapeText = rawShapeText;
  const normalizedParagraphs = paragraphs;

  // Font formatting is per-run on the host. Mirror Office.js's flat
  // textRange.font snapshot by reading the first non-empty run's
  // runFormat.font, then falling back to legacy shape-level fields.
  const firstRunFont = ((): ExtractorFontLite | undefined => {
    const sourceParagraphs = (textFrame?.paragraphs ?? shape.paragraphs) as
      | ExtractorParagraphLite[]
      | undefined;
    if (!Array.isArray(sourceParagraphs)) return undefined;
    for (const paragraph of sourceParagraphs) {
      const font = findFirstRunFont(paragraph);
      if (font) return font;
    }
    return undefined;
  })();

  const normalizedFontSize =
    readExtractorFontSize(firstRunFont?.size) ?? readExtractorFontSize(shape.fontSize);
  const fontFamily =
    typeof firstRunFont?.name === "string" && firstRunFont.name.length > 0
      ? firstRunFont.name
      : typeof shape.fontFamily === "string"
        ? shape.fontFamily
        : undefined;
  const textColor =
    extractColorToken(firstRunFont) ??
    extractColorToken(firstRunFont?.color) ??
    (typeof shape.textColor === "string" ? shape.textColor : undefined);
  const bold =
    typeof firstRunFont?.bold === "boolean"
      ? firstRunFont.bold
      : typeof shape.bold === "boolean"
        ? shape.bold
        : undefined;
  const italic =
    typeof firstRunFont?.italic === "boolean"
      ? firstRunFont.italic
      : typeof shape.italic === "boolean"
        ? shape.italic
        : undefined;

  const extractedFillColor = pickFirstNonEmptyString(shape.fillColor, extractColorToken(shape.fill));
  const outlineSource = shape.border ?? shape.line;
  // Real RunOfficeExtractor payloads expose outlines on `shape.border`.
  // Only surface a color when the border looks explicit so hidden/mixed
  // borders do not leak placeholder/default colors into the AI envelope.
  const extractedLineColor = pickFirstNonEmptyString(
    shape.lineColor,
    extractExplicitBorderColor(outlineSource)
  );
  const extractedLineWidth = extractBorderWeight(outlineSource);
  const geometricType = type === "geometricShape" ? resolvedGeometricType : undefined;
  const id = readExtractorShapeId(shape);
  if (id) seenIds.add(id);

  let nestedShapes: ShapeContent[] | undefined;
  if (depth < MAX_GROUP_NESTING_DEPTH && Array.isArray(shape.shapes) && shape.shapes.length > 0) {
    const mappedChildren: ShapeContent[] = [];
    for (let childIndex = 0; childIndex < shape.shapes.length; childIndex += 1) {
      const child = shape.shapes[childIndex];
      const childId = readExtractorShapeId(child);
      if (childId && seenIds.has(childId)) continue;
      mappedChildren.push(
        mapExtractorShapeLite(
          child,
          childIndex,
          geometryScale,
          extractorDecisionSummary,
          seenIds,
          depth + 1,
          id || undefined
        )
      );
    }
    if (mappedChildren.length > 0) nestedShapes = mappedChildren;
  }

  return {
    id,
    name,
    type,
    ...(placeholderType ? { placeholderType } : {}),
    text: shapeText,
    paragraphs: normalizedParagraphs,
    geometricType,
    left,
    top,
    width,
    height,
    fillColor: extractedFillColor,
    lineColor: extractedLineColor,
    lineWidth: extractedLineWidth,
    fontSize: normalizedFontSize,
    fontFamily,
    textColor,
    bold,
    italic,
    horizontalAlignment,
    verticalAlignment,
    tableData: normalizeTableCellData(shape.tableData ?? shape.table) ?? undefined,
    ...(parentGroupId ? { parentGroupId } : {}),
    ...(nestedShapes ? { shapes: nestedShapes } : {}),
  };
}

function toPresentationContentFromExtractor(
  result: ExtractorResultLite,
  preferredSlideIds?: string[],
  log?: Logger
): PresentationContent {
  const { width, height, source, geometryScale } = resolveExtractorSlideDimensions(result);
  const extractorKeys = Object.keys(result as unknown as Record<string, unknown>).join(", ");
  lastExtractorSlideDimensionMetadata = {
    width,
    height,
    source,
    geometryScale,
    extractorKeys,
    capturedAt: nowMs(),
  };
  if (log) {
    logMessage(
      log,
      `Extractor slide dimensions: ${width}x${height}pt ` +
        `(source=${source}, geometryScale=${geometryScale}). Keys: ${extractorKeys}`
    );
  }
  if (!slideDimensionDiagnosticLogged) {
    slideDimensionDiagnosticLogged = true;
    console.log(
      `Slide dimensions resolved to ${width}x${height}pt (source: ${source}). ` +
        `Top-level extractor keys: ${extractorKeys}`
    );
  }
  const extractorDecisionSummary: ExtractorDecisionSummary = {
    slidesProcessed: 0,
    shapesProcessed: 0,
  };

  const mappedSlides: PresentationContent["slides"] = (result.slides ?? []).map((slide, index) => {
    extractorDecisionSummary.slidesProcessed += 1;
    const slideLayout =
      typeof slide.layout === "string" && slide.layout.trim().length > 0
        ? slide.layout.trim()
        : undefined;
    const customLayoutName = pickFirstNonEmptyString(slide.customLayoutName);
    const slideMasterName = pickFirstNonEmptyString(slide.slideMasterName);
    const slideNumber = readNonNegativeInteger(slide.slideNumber);
    return {
      id: String(slide.slideId ?? slide.slideID ?? preferredSlideIds?.[index] ?? ""),
      index,
      ...(typeof slideNumber === "number" ? { slideNumber } : {}),
      ...(slideLayout ? { layout: slideLayout } : {}),
      ...(customLayoutName ? { customLayoutName } : {}),
      ...(slideMasterName ? { slideMasterName } : {}),
      ...(typeof slide.displayMasterShapes === "boolean"
        ? { displayMasterShapes: slide.displayMasterShapes }
        : {}),
      ...(typeof slide.followMasterBackground === "boolean"
        ? { followMasterBackground: slide.followMasterBackground }
        : {}),
      shapes: (() => {
        const seenIds = new Set<string>();
        return (slide.shapes ?? []).map((shape, shapeIndex) =>
          mapExtractorShapeLite(
            shape,
            shapeIndex,
            geometryScale,
            extractorDecisionSummary,
            seenIds,
            0
          )
        );
      })(),
    };
  });

  updateIntentParityDecisionSummary({ extractor: extractorDecisionSummary });

  return {
    selectedSlideIndex: 0,
    slideWidth: width,
    slideHeight: height,
    slides: mappedSlides,
  };
}

export function toPresentationContentFromExtractorForTesting(
  result: ExtractorResultLite,
  preferredSlideIds?: string[]
): PresentationContent {
  return toPresentationContentFromExtractor(result, preferredSlideIds);
}

export function parseDesignMetadataFromExtractorForTesting(
  result: ExtractorResultLite
): FactSetDesignMetadata {
  return parseDesignMetadataFromExtractor(result);
}

export function parseDeckLayoutCatalogFromExtractorForTesting(
  result: ExtractorResultLite
): DeckLayoutCatalog {
  return parseDeckLayoutCatalogFromExtractor(result);
}

export function parsePromptDesignContextFromExtractorForTesting(
  result: ExtractorResultLite
): PromptDesignContext {
  return parsePromptDesignContextFromExtractor(result);
}

export function parseExtractorResultAsyncForTesting(
  raw: unknown
): Promise<ExtractorResultLite | null> {
  return parseExtractorResultAsync(raw);
}

export function extractSlideInfosForTesting(raw: unknown): FactSetSlideInfo[] {
  return extractSlideInfos(raw).map((info) => ({
    slideId: info.slideId,
    index: info.index,
    ...(typeof info.slideNumber === "number" ? { slideNumber: info.slideNumber } : {}),
  }));
}

export function normalizeRunOfficeExtractorContextForTesting(
  options?: RunOfficeExtractorContextOptions
): {
  context: Record<string, unknown>;
  hasFilters: boolean;
  includeSlideRanges: RunOfficeExtractorSlideRangeFilter[];
  includeShapes: RunOfficeExtractorShapeFilter[];
} {
  const normalized = normalizeRunOfficeExtractorContextOptions(options);
  return {
    context: normalized.context,
    hasFilters: normalized.hasFilters,
    includeSlideRanges: normalized.includeSlideRanges,
    includeShapes: normalized.includeShapes,
  };
}

export async function extractDesignMetadataViaIntents(
  log?: Logger,
  options?: { forceRefresh?: boolean; presentationRef?: Partial<FactSetPresentationRef> | null }
): Promise<FactSetDesignMetadata> {
  const forceRefresh = options?.forceRefresh ?? false;
  const presentationRef = normalizePresentationRef(options?.presentationRef);
  const bypassSharedCache = Boolean(presentationRef);
  if (
    !forceRefresh &&
    !bypassSharedCache &&
    cachedDesignMetadata &&
    isFresh(cachedDesignMetadataFetchedAt, DESIGN_METADATA_CACHE_TTL_MS)
  ) {
    return cloneDesignMetadata(cachedDesignMetadata);
  }
  if (!forceRefresh && !bypassSharedCache && inFlightDesignMetadata) {
    return cloneDesignMetadata(await inFlightDesignMetadata);
  }

  const fetchPromise = (async () => {
    const extractorRaw = await sendIntentById(
      "runOfficeExtractor",
      {
        filters: {
          designs: {
            fontSchema: true,
            colorSchema: true,
            customLayouts: {
              shapes: true,
            },
          },
        },
      },
      30_000,
      {
        log,
        traceLabel: "extract-design-metadata",
        presentationRef,
      }
    );
    const extractorResult = await parseExtractorResultAsync(extractorRaw);
    if (!extractorResult) {
      throw new Error("RunOfficeExtractor returned an invalid designs payload.");
    }
    const metadata = parseDesignMetadataFromExtractor(extractorResult);
    if (!bypassSharedCache) {
      cachedDesignMetadata = metadata;
      cachedDesignMetadataFetchedAt = nowMs();
    }
    return metadata;
  })();

  if (forceRefresh || bypassSharedCache) {
    return fetchPromise;
  }

  inFlightDesignMetadata = fetchPromise.finally(() => {
    inFlightDesignMetadata = null;
  });
  return cloneDesignMetadata(await inFlightDesignMetadata);
}

const DECK_LAYOUT_CATALOG_SHAPE_FILTER = {
  placeholderFormat: true,
  fill: false,
  border: false,
  textFrame: false,
  table: false,
  chart: false,
  tags: false,
  glow: false,
  reflection: false,
  shadow: false,
  softEdge: false,
  callout: false,
  textEffectFormat: false,
  pictureFormat: false,
  connectorFormat: false,
} as const;

function buildDeckLayoutCatalogExtractorFilters(includeColorAndFontSchema: boolean) {
  return {
    slides: false,
    designs: {
      customLayouts: {
        background: false,
        headersFooters: false,
        shapes: { ...DECK_LAYOUT_CATALOG_SHAPE_FILTER },
      },
      fontSchema: includeColorAndFontSchema,
      colorSchema: includeColorAndFontSchema,
      textStyles: false,
      masterShapes: false,
    },
    notesMaster: false,
    source: false,
    extraColors: false,
    tags: false,
    builtInDocumentProperties: false,
    customDocumentProperties: false,
    pageSetup: false,
    runtime: false,
    sections: false,
  };
}

export async function extractDeckLayoutCatalogViaIntents(
  log?: Logger,
  options?: { forceRefresh?: boolean; presentationRef?: Partial<FactSetPresentationRef> | null }
): Promise<DeckLayoutCatalog> {
  const forceRefresh = options?.forceRefresh ?? false;
  const presentationRef = normalizePresentationRef(options?.presentationRef);
  const bypassSharedCache = Boolean(presentationRef);
  if (
    !forceRefresh &&
    !bypassSharedCache &&
    cachedDeckLayoutCatalog &&
    isFresh(cachedDeckLayoutCatalogFetchedAt, DESIGN_METADATA_CACHE_TTL_MS)
  ) {
    return cloneDeckLayoutCatalog(cachedDeckLayoutCatalog);
  }
  if (!forceRefresh && !bypassSharedCache && inFlightDeckLayoutCatalog) {
    return cloneDeckLayoutCatalog(await inFlightDeckLayoutCatalog);
  }

  const fetchPromise = (async () => {
    const extractorRaw = await sendIntentById(
      "runOfficeExtractor",
      {
        filters: buildDeckLayoutCatalogExtractorFilters(false),
      },
      30_000,
      {
        log,
        traceLabel: "extract-layout-catalog",
        presentationRef,
      }
    );
    const extractorResult = await parseExtractorResultAsync(extractorRaw);
    if (!extractorResult) {
      throw new Error("RunOfficeExtractor returned an invalid layout catalog payload.");
    }
    const catalog = parseDeckLayoutCatalogFromExtractor(extractorResult);
    if (!bypassSharedCache) {
      cachedDeckLayoutCatalog = catalog;
      cachedDeckLayoutCatalogFetchedAt = nowMs();
    }
    return catalog;
  })();

  if (forceRefresh || bypassSharedCache) {
    return fetchPromise;
  }

  inFlightDeckLayoutCatalog = fetchPromise.finally(() => {
    inFlightDeckLayoutCatalog = null;
  });
  return cloneDeckLayoutCatalog(await inFlightDeckLayoutCatalog);
}

type SplitExtractSettledResult<T> =
  | { status: "fulfilled"; value: T }
  | { status: "rejected"; reason: unknown };

// Local `Promise.allSettled` — FactSet Workstation's embedded Chromium
// predates ES2020, and rspack's `es6-promise` ProvidePlugin has no
// `allSettled`. Calling the global throws `Promise.allSettled is not a
// function`, which would drop both theme and catalog retries here. Inline
// it the same way ExcelApp/slideLibraryAgentTools do rather than depending
// on the citation-dialog polyfill, which never loads in the taskpane.
function settleSplitExtract<T>(promise: Promise<T>): Promise<SplitExtractSettledResult<T>> {
  return promise.then(
    (value): SplitExtractSettledResult<T> => ({ status: "fulfilled", value }),
    (reason): SplitExtractSettledResult<T> => ({ status: "rejected", reason })
  );
}

async function fallbackPromptDesignContextFromSplitExtracts(
  log?: Logger,
  options?: { forceRefresh?: boolean; presentationRef?: Partial<FactSetPresentationRef> | null }
): Promise<PromptDesignContext> {
  const [themeResult, catalogResult] = await Promise.all([
    settleSplitExtract(extractDesignMetadataViaIntents(log, options)),
    settleSplitExtract(extractDeckLayoutCatalogViaIntents(log, options)),
  ]);
  if (themeResult.status === "rejected") {
    log?.(
      `extractPromptDesignContextViaIntents: theme fallback failed (${
        themeResult.reason instanceof Error ? themeResult.reason.message : String(themeResult.reason)
      })`
    );
  }
  if (catalogResult.status === "rejected") {
    log?.(
      `extractPromptDesignContextViaIntents: layout catalog fallback failed (${
        catalogResult.reason instanceof Error
          ? catalogResult.reason.message
          : String(catalogResult.reason)
      })`
    );
  }
  return {
    themeColors: themeResult.status === "fulfilled" ? themeResult.value.themeColors : null,
    deckLayoutCatalog: catalogResult.status === "fulfilled" ? catalogResult.value : null,
  };
}

export async function extractPromptDesignContextViaIntents(
  log?: Logger,
  options?: { forceRefresh?: boolean; presentationRef?: Partial<FactSetPresentationRef> | null }
): Promise<PromptDesignContext> {
  const forceRefresh = options?.forceRefresh ?? false;
  const presentationRef = normalizePresentationRef(options?.presentationRef);
  const bypassSharedCache = Boolean(presentationRef);
  const catalogFresh =
    !forceRefresh &&
    !bypassSharedCache &&
    cachedDeckLayoutCatalog &&
    isFresh(cachedDeckLayoutCatalogFetchedAt, DESIGN_METADATA_CACHE_TTL_MS);
  const metadataFresh =
    !forceRefresh &&
    !bypassSharedCache &&
    cachedDesignMetadata &&
    isFresh(cachedDesignMetadataFetchedAt, DESIGN_METADATA_CACHE_TTL_MS);
  if (
    catalogFresh &&
    metadataFresh &&
    cachedDesignMetadata &&
    cachedDeckLayoutCatalog
  ) {
    return {
      themeColors: cloneDesignMetadata(cachedDesignMetadata).themeColors,
      deckLayoutCatalog: cloneDeckLayoutCatalog(cachedDeckLayoutCatalog),
    };
  }
  if (!forceRefresh && !bypassSharedCache && inFlightPromptDesignContext) {
    return clonePromptDesignContext(await inFlightPromptDesignContext);
  }

  const fetchPromise = (async (): Promise<PromptDesignContext> => {
    try {
      const extractorRaw = await sendIntentById(
        "runOfficeExtractor",
        {
          filters: buildDeckLayoutCatalogExtractorFilters(true),
        },
        30_000,
        {
          log,
          traceLabel: "extract-prompt-design-context",
          presentationRef,
        }
      );
      const extractorResult = await parseExtractorResultAsync(extractorRaw);
      if (!extractorResult) {
        throw new Error("RunOfficeExtractor returned an invalid designs payload.");
      }
      let metadata: FactSetDesignMetadata | null = null;
      let catalog: DeckLayoutCatalog | null = null;
      try {
        metadata = parseDesignMetadataFromExtractor(extractorResult);
      } catch (error) {
        log?.(
          `extractPromptDesignContextViaIntents: theme parse failed (${
            error instanceof Error ? error.message : String(error)
          })`
        );
      }
      try {
        catalog = parseDeckLayoutCatalogFromExtractor(extractorResult);
      } catch (error) {
        log?.(
          `extractPromptDesignContextViaIntents: layout catalog parse failed (${
            error instanceof Error ? error.message : String(error)
          })`
        );
      }
      if (!bypassSharedCache) {
        if (metadata) {
          cachedDesignMetadata = metadata;
          cachedDesignMetadataFetchedAt = nowMs();
        }
        if (catalog) {
          cachedDeckLayoutCatalog = catalog;
          cachedDeckLayoutCatalogFetchedAt = nowMs();
        }
      }
      if (!catalog) {
        try {
          catalog = await extractDeckLayoutCatalogViaIntents(log, options);
        } catch (fallbackError) {
          log?.(
            `extractPromptDesignContextViaIntents: layout catalog fallback failed (${
              fallbackError instanceof Error ? fallbackError.message : String(fallbackError)
            })`
          );
        }
      }
      return {
        themeColors: metadata?.themeColors ?? null,
        deckLayoutCatalog: catalog,
      };
    } catch (error) {
      log?.(
        `extractPromptDesignContextViaIntents: combined designs extract failed (${
          error instanceof Error ? error.message : String(error)
        })`
      );
      return fallbackPromptDesignContextFromSplitExtracts(log, options);
    }
  })();

  if (forceRefresh || bypassSharedCache) {
    return fetchPromise;
  }

  inFlightPromptDesignContext = fetchPromise.finally(() => {
    inFlightPromptDesignContext = null;
  });
  return clonePromptDesignContext(await inFlightPromptDesignContext);
}

/**
 * Extract the raw design/master data from the FactSet RunOfficeExtractor,
 * including color scheme, font scheme, custom layouts with shapes, and
 * slide master shapes. Returns the raw JSON for inspection/debugging.
 */
export async function extractRawDesignJsonViaIntents(
  log?: Logger
): Promise<string> {
  const extractorRaw = await sendIntentById(
    "runOfficeExtractor",
    {
      filters: {
        designs: {
          fontSchema: true,
          colorSchema: true,
          shapes: true,
          customLayouts: {
            shapes: true,
          },
        },
      },
    },
    30_000,
    { log, traceLabel: "extract-raw-design" }
  );

  const parsed = parseExtractorResult(extractorRaw);
  if (!parsed) {
    throw new Error("RunOfficeExtractor returned an invalid payload.");
  }

  return JSON.stringify(parsed.designs ?? [], null, 2);
}

/**
 * Extract slide backgrounds via RunOfficeExtractor.
 * Requests full slide data (shapes + layout metadata) so we can identify
 * background fills, background-like shapes, and layout background flags.
 */
export async function extractSlideBackgroundsViaIntents(
  log?: Logger
): Promise<string> {
  const extractorRaw = await sendIntentById(
    "runOfficeExtractor",
    {
      filters: {
        designs: {
          fontSchema: true,
          colorSchema: true,
          shapes: true,
          customLayouts: {
            shapes: true,
          },
        },
      },
    },
    30_000,
    { log, traceLabel: "extract-backgrounds" }
  );

  const parsed = parseExtractorResult(extractorRaw);
  if (!parsed) {
    throw new Error("RunOfficeExtractor returned an invalid payload for background extraction.");
  }

  const designs = Array.isArray(parsed.designs) ? parsed.designs : [];
  const result: unknown[] = [];

  for (const design of designs) {
    if (!design || typeof design !== "object") continue;
    const designObj = design as Record<string, unknown>;
    const master = designObj.slideMaster as Record<string, unknown> | undefined;
    const masterShapes = Array.isArray(master?.shapes) ? master.shapes : [];

    const masterBackground: Record<string, unknown> = {
      name: designObj.name ?? "Unknown",
      masterName: (master?.name as string) ?? "Unknown",
    };

    if (masterShapes.length > 0) {
      masterBackground.masterShapes = masterShapes.map((s: unknown) => {
        const shape = s as Record<string, unknown>;
        return {
          id: shape.id,
          name: shape.name,
          type: shape.type,
          left: shape.left,
          top: shape.top,
          width: shape.width,
          height: shape.height,
          visible: shape.visible,
          fill: shape.fill,
          autoShapeType: shape.autoShapeType,
          alternativeText: shape.alternativeText,
        };
      });
    }

    const layouts = Array.isArray(
      (designObj.slideMaster as Record<string, unknown> | undefined)?.customLayouts ??
        designObj.customLayouts
    )
      ? (
          ((designObj.slideMaster as Record<string, unknown> | undefined)?.customLayouts ??
            designObj.customLayouts) as unknown[]
        )
      : [];

    masterBackground.layouts = layouts.map((layout: unknown) => {
      const l = layout as Record<string, unknown>;
      const layoutShapes = Array.isArray(l.shapes) ? l.shapes : [];
      const layoutBgShapes = layoutShapes.filter((s: unknown) => {
        const shape = s as Record<string, unknown>;
        const name = String(shape.name ?? "").toLowerCase();
        const fill = shape.fill as Record<string, unknown> | undefined;
        const isLargeRect =
          typeof shape.left === "number" &&
          typeof shape.top === "number" &&
          typeof shape.width === "number" &&
          typeof shape.height === "number" &&
          (shape.width as number) > 500 &&
          (shape.height as number) > 300;
        return name.includes("background") || name.includes("bg") || (isLargeRect && fill?.visible !== false);
      });

      const entry: Record<string, unknown> = {
        name: l.matchingName ?? l.name ?? "Unknown",
        followMasterBackground: l.followMasterBackground,
        displayMasterShapes: l.displayMasterShapes,
      };
      if (layoutBgShapes.length > 0) {
        entry.backgroundShapes = layoutBgShapes.map((s: unknown) => {
          const shape = s as Record<string, unknown>;
          return {
            id: shape.id,
            name: shape.name,
            left: shape.left,
            top: shape.top,
            width: shape.width,
            height: shape.height,
            fill: shape.fill,
          };
        });
      }
      return entry;
    });

    result.push(masterBackground);
  }

  return JSON.stringify(result, null, 2);
}

function createSummary(modifications: PresentationModification): ApplyResultSummary {
  const requestedByAction = createActionCounts();
  let requestedShapes = 0;

  for (const slideMod of modifications.slides || []) {
    const slideAction = slideMod.action || "modify";
    if (slideAction === "duplicate") {
      requestedByAction.duplicate += 1;
    } else if (slideAction === "delete") {
      requestedByAction.delete += 1;
    }
    for (const shapeMod of slideMod.shapes || []) {
      requestedShapes += 1;
      requestedByAction[shapeMod.action] += 1;
    }
  }

  return {
    requested: {
      slides: modifications.slides?.length || 0,
      shapes: requestedShapes,
      byAction: requestedByAction,
    },
    applied: {
      slides: 0,
      shapes: 0,
      byAction: createActionCounts(),
    },
    skipped: {
      count: 0,
      byReason: {},
      issues: [],
    },
    errors: {
      count: 0,
      byReason: {},
      issues: [],
    },
    classification: "true_no_op",
  };
}

function isBatchFailureText(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return false;
  const parsedJson = tryParseJsonValue(value);
  if (parsedJson && typeof parsedJson === "object") {
    return hasBatchFailureSignal(parsedJson);
  }
  if (BATCH_ZERO_FAILURE_PATTERN.test(normalized)) return false;
  if (BATCH_SUCCESS_PATTERN.test(normalized) && !BATCH_FAILURE_PATTERN.test(normalized)) {
    return false;
  }
  return BATCH_FAILURE_PATTERN.test(normalized);
}

function hasBatchFailureSignal(value: unknown, depth = 0): boolean {
  if (depth > 6 || value == null) return false;
  if (typeof value === "string") {
    return isBatchFailureText(value);
  }
  if (Array.isArray(value)) {
    return value.some((entry) => hasBatchFailureSignal(entry, depth + 1));
  }
  if (typeof value !== "object") return false;

  const obj = value as Record<string, unknown>;
  for (const [key, raw] of Object.entries(obj)) {
    const loweredKey = key.toLowerCase();
    if ((loweredKey === "success" || loweredKey === "succeeded" || loweredKey === "ok") && raw === false) {
      return true;
    }
    if (loweredKey.includes("error") || loweredKey.includes("failed") || loweredKey.includes("failure")) {
      if (typeof raw === "boolean") {
        if (raw) return true;
        continue;
      }
      if (typeof raw === "number") {
        if (raw > 0) return true;
        continue;
      }
      if (typeof raw === "string") {
        if (isBatchFailureText(raw)) return true;
        continue;
      }
      if (Array.isArray(raw)) {
        if (raw.some((entry) => hasBatchFailureSignal(entry, depth + 1))) {
          return true;
        }
        continue;
      }
      if (raw && typeof raw === "object") {
        if (hasBatchFailureSignal(raw, depth + 1)) return true;
        continue;
      }
    }
    if (hasBatchFailureSignal(raw, depth + 1)) return true;
  }
  return false;
}

function tryParseJsonValue(raw: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (
    (!trimmed.startsWith("{") || !trimmed.endsWith("}")) &&
    (!trimmed.startsWith("[") || !trimmed.endsWith("]"))
  ) {
    return null;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function extractBatchFailureDiagnostics(batchResult: unknown, maxEntries = 6): string[] {
  const diagnostics: string[] = [];
  const seenDiagnostics = new Set<string>();
  const visitedObjects = new Set<object>();
  const noteDiagnostic = (value: string): void => {
    const trimmed = value.trim();
    if (!trimmed || !isBatchFailureText(trimmed) || seenDiagnostics.has(trimmed)) return;
    seenDiagnostics.add(trimmed);
    diagnostics.push(trimmed);
  };
  const visit = (value: unknown, depth = 0): void => {
    if (diagnostics.length >= maxEntries || depth > 8 || value == null) return;
    if (typeof value === "string") {
      noteDiagnostic(value);
      const parsed = tryParseJsonValue(value);
      if (parsed) {
        visit(parsed, depth + 1);
      }
      return;
    }
    if (Array.isArray(value)) {
      for (const entry of value) {
        if (diagnostics.length >= maxEntries) return;
        visit(entry, depth + 1);
      }
      return;
    }
    if (typeof value !== "object") return;
    if (visitedObjects.has(value)) return;
    visitedObjects.add(value);

    const obj = value as Record<string, unknown>;
    const diagnosticKeys = [
      "message",
      "error",
      "errorMessage",
      "statusMessage",
      "reason",
      "jsonReason",
      "failureReason",
      "failedReason",
      "validationMessage",
      "operationReason",
      "operationResult",
      "operationsResult",
      "resultJson",
      "json",
    ];
    for (const key of diagnosticKeys) {
      if (!(key in obj)) continue;
      visit(obj[key], depth + 1);
      if (diagnostics.length >= maxEntries) return;
    }
    for (const raw of Object.values(obj)) {
      if (diagnostics.length >= maxEntries) return;
      visit(raw, depth + 1);
    }
  };
  visit(batchResult);
  return diagnostics;
}

function getBatchFailureReason(batchResult: unknown): string | null {
  if (!batchResult || typeof batchResult !== "object") return null;
  const obj = batchResult as Record<string, unknown>;
  const message = pickFirstNonEmptyString(obj.message, obj.errorMessage, obj.statusMessage);
  if (message && isBatchFailureText(message)) {
    return message;
  }
  if (!hasBatchFailureSignal(batchResult)) {
    return null;
  }
  const diagnostics = extractBatchFailureDiagnostics(batchResult, 1);
  if (diagnostics.length > 0) {
    return diagnostics[0];
  }
  return message ?? "Batch result included failure/error indicators.";
}

function summarizeBatchResultForLog(batchResult: unknown, maxLength = 1200): string {
  if (batchResult == null) {
    return "null";
  }
  try {
    const serialized = JSON.stringify(batchResult);
    if (!serialized) {
      return String(batchResult);
    }
    return serialized.length > maxLength
      ? `${serialized.slice(0, maxLength)}...(truncated)`
      : serialized;
  } catch {
    return String(batchResult);
  }
}

let createShapeIdNonce = 0;

function sanitizeCreateShapeIdToken(value: string): string {
  return value
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
}

function buildUniqueCreateShapeId(baseId: string, slideIndex: number, shapePos: number): string {
  createShapeIdNonce += 1;
  const normalizedBase = sanitizeCreateShapeIdToken(baseId);
  const fallbackBase = `new_${slideIndex + 1}_${shapePos + 1}`;
  const effectiveBase = normalizedBase.length > 0 ? normalizedBase : fallbackBase;
  return `${effectiveBase}__${nowMs().toString(36)}_${createShapeIdNonce.toString(36)}`;
}

interface BatchShapeDecisionFlags {
  lineFallbackToRectangle: boolean;
  droppedInvalidFillColor: boolean;
  droppedInvalidLineColor: boolean;
  droppedInvalidTextColor: boolean;
}

function normalizeTextVerticalAlignmentForBatch(
  value: unknown
): ShapeModification["textVerticalAlignment"] | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized === "top") return "top";
  if (normalized === "middle" || normalized === "center" || normalized === "centre") {
    return "middle";
  }
  if (normalized === "bottom") return "bottom";
  return undefined;
}

// Patch a single field on the cloned pass-through shape: assign the
// host-adapted value in place (preserving the AI's original key position when
// the key already exists) or delete the key when the adapter produced no value
// (absent input or an intentionally dropped/invalid value). This keeps
// mapShapeForBatch a clone-and-patch over the model JSON instead of an
// allowlist rebuild that reorders keys and silently drops unlisted fields.
function assignOrDeleteShapeField(
  target: ShapeModification,
  key: keyof ShapeModification,
  value: unknown
): void {
  const record = target as unknown as Record<string, unknown>;
  if (value === undefined) {
    delete record[key as string];
  } else {
    record[key as string] = value;
  }
}

function normalizeInlineImagePayloadForBatch(shape: ShapeModification): {
  imageBase64?: string;
  imageMimeType?: string;
} {
  const parsedDataUri = parseBase64DataUri(shape.imageDataUri);
  if (parsedDataUri) {
    return {
      imageBase64: parsedDataUri.base64,
      imageMimeType: parsedDataUri.mimeType,
    };
  }

  const imageBase64 = normalizeBase64Payload(shape.imageBase64);
  if (!imageBase64) return {};

  return {
    imageBase64,
    imageMimeType: pickFirstNonEmptyString(shape.imageMimeType),
  };
}

function mapShapeForBatch(
  shape: ShapeModification,
  slideIndex: number,
  slideId: string,
  shapePos: number,
  bounds: SlideBounds,
  log?: Logger
): { shape: ShapeModification | null; issue?: ApplyDiagnostic; decisionFlags?: BatchShapeDecisionFlags } {
  const shapeAction = shape.action ?? "modify";
  const modelShapeId = pickFirstNonEmptyString(shape.id);
  const shapeId =
    shapeAction === "create"
      ? buildUniqueCreateShapeId(
          modelShapeId ?? `new_${slideIndex + 1}_${shapePos + 1}`,
          slideIndex,
          shapePos
        )
      : modelShapeId;

  if (!shapeId) {
    logMessage(
      log,
      `Skipping shape with missing id on slide #${slideIndex} (${summarizeSlideIdForLog(slideId)}), ` +
        `action=${shapeAction}, position=${shapePos + 1}`
    );
    return {
      shape: null,
      issue: {
        code: "SHAPE_ID_NOT_FOUND",
        category: "target_mismatch",
        message: `Shape id missing for non-create operation on slide ${slideId}.`,
        slideIndex,
        slideId,
        action: shapeAction,
      },
      decisionFlags: {
        lineFallbackToRectangle: false,
        droppedInvalidFillColor: false,
        droppedInvalidLineColor: false,
        droppedInvalidTextColor: false,
      },
    };
  }

  if (shapeAction === "create" && hasAnyImagePayload(shape) && !hasInlineImagePayload(shape)) {
    logMessage(
      log,
      `Skipping image create shape ${shapeId} on slide #${slideIndex} (${summarizeSlideIdForLog(slideId)}): ` +
        "inline image bytes are missing"
    );
    return {
      shape: null,
      issue: {
        code: "IMAGE_PREFETCH_FAILED",
        category: "apply_error",
        message: `Image bytes are required for create shape ${shapeId} in intent-bridge mode.`,
        slideIndex,
        slideId,
        shapeId,
        action: "create",
      },
      decisionFlags: {
        lineFallbackToRectangle: false,
        droppedInvalidFillColor: false,
        droppedInvalidLineColor: false,
        droppedInvalidTextColor: false,
      },
    };
  }

  const lineLikeForBounds =
    shapeAction === "create" && isLineLikeGeometricType(shape.geometricType);
  let geometricTypeForBatch = lineLikeForBounds ? "rectangle" : shape.geometricType;

  const minSizeForShape = 20;
  const boundedShape = applyBoundsToShape(
    {
      left: shape.left,
      top: shape.top,
      width: shape.width,
      height: shape.height,
    },
    bounds,
    log,
    { minSize: minSizeForShape }
  );
  const createShapeBounds =
    shapeAction === "create"
      ? (() => {
          // Chart shapes carry their position inside chart.position (matches the
          // f2_docs goldens for PerformBatchPptOperations). We deliberately
          // skip shape-level position defaults so we don't accidentally
          // override chart.position with bridge-fabricated defaults.
          if (shape.chart) {
            return {
              left: shape.left,
              top: shape.top,
              width: shape.width,
              height: shape.height,
            };
          }
          const isImageCreateShape = hasAnyImagePayload(shape);
          const left = boundedShape.left ?? 50;
          const top = boundedShape.top ?? 80;
          const maxAvailWidth = Math.max(20, bounds.slideWidth - left - FACTSET_SAFE_MARGIN_PT);
          const maxAvailHeight = Math.max(20, bounds.slideHeight - top - FACTSET_SAFE_MARGIN_PT);

          const defaultWidth = lineLikeForBounds
            ? Math.min(400, maxAvailWidth)
            : isImageCreateShape
              ? Math.min(300, maxAvailWidth)
              : shape.table
                ? Math.min(760, maxAvailWidth)
                : geometricTypeForBatch
                  ? Math.min(200, maxAvailWidth)
                  : Math.min(400, maxAvailWidth);

          const defaultHeight = lineLikeForBounds
            ? Math.min(1, maxAvailHeight)
            : isImageCreateShape
              ? Math.min(200, maxAvailHeight)
              : shape.table
                ? Math.min(200, maxAvailHeight)
                : geometricTypeForBatch
                  ? Math.min(100, maxAvailHeight)
                  : Math.min(100, maxAvailHeight);

          return applyBoundsToShape(
            {
              left,
              top,
              width: boundedShape.width ?? defaultWidth,
              height: boundedShape.height ?? defaultHeight,
            },
            bounds,
            log,
            { minSize: minSizeForShape }
          );
        })()
      : boundedShape;
  const finalizedCreateShapeBounds = createShapeBounds;
  const boundedTable = applyBoundsToTableLayout(normalizeCreateTableForBatch(shape), bounds, log);
  const rawFillColor = pickFirstNonEmptyString(shape.fillColor);
  const rawLineColor = pickFirstNonEmptyString(shape.lineColor);
  const rawTextColor = pickFirstNonEmptyString(shape.textColor);
  const normalizedFillColor = normalizeBatchColorToken(shape.fillColor);
  const normalizedLineColor = normalizeBatchColorToken(shape.lineColor);
  const normalizedTextColor = normalizeBatchColorToken(shape.textColor);
  const droppedInvalidFillColor = Boolean(rawFillColor && !normalizedFillColor);
  const droppedInvalidLineColor = Boolean(rawLineColor && !normalizedLineColor);
  const droppedInvalidTextColor = Boolean(rawTextColor && !normalizedTextColor);
  if (droppedInvalidFillColor) {
    logMessage(
      log,
      `Dropping unsupported fillColor "${rawFillColor}" for shape ${shapeId} (slide #${slideIndex}); host expects hex RGB.`
    );
  }
  if (droppedInvalidLineColor) {
    logMessage(
      log,
      `Dropping unsupported lineColor "${rawLineColor}" for shape ${shapeId} (slide #${slideIndex}); host expects hex RGB.`
    );
  }
  if (droppedInvalidTextColor) {
    logMessage(
      log,
      `Dropping unsupported textColor "${rawTextColor}" for shape ${shapeId} (slide #${slideIndex}); host expects hex RGB.`
    );
  }
  // Promote text-box creates to geometric "rectangle" with no fill/line so
  // the COM bridge creates them via AddShape() instead of AddTextBox().
  // AddShape-created shapes properly inherit the explicit wordWrap=true we set,
  // whereas AddTextBox shapes on Citi masters ignore it and overflow.
  const isTextBoxCreateForOfficeParity = false;
  // Exclude `lineLikeForBounds`: those creates were coerced from a line into a
  // thin filled rectangle purely for rendering. They carry no text content and
  // shouldn't inherit OfficeJS geo-shape text-frame defaults (wordWrap=true,
  // verticalAlignment=middle), which would mis-signal intent to the host and
  // could trigger text-frame auto-fit on a 1pt-tall geometry.
  const isGeometricCreateForOfficeParity =
    shapeAction === "create" && Boolean(geometricTypeForBatch) && !lineLikeForBounds;

  // Resolve fill/line/width with mode-specific overrides:
  //   line-fallback: render the coerced rectangle as a solid filled bar so it
  //     reads as a line regardless of host fill defaults.
  //   text-box create: match OfficeJS addTextBox (no fill, no outline).
  //   otherwise: pass through the LLM-supplied colors unchanged.
  let effectiveFillColor: string | undefined;
  let effectiveLineColor: string | undefined;
  let effectiveLineWidth: number | undefined;
  if (lineLikeForBounds) {
    effectiveFillColor = normalizedLineColor ?? normalizedFillColor;
    effectiveLineColor = undefined;
    effectiveLineWidth = undefined;
  } else if (isTextBoxCreateForOfficeParity) {
    // Promoted to rectangle — must explicitly set white fill + invisible line,
    // otherwise the Citi master's default shape fill (blue) gets inherited.
    // lineWidth must be >0 (0 is treated as "unset" by COM, causing a default outline);
    // 0.01pt is sub-pixel and invisible, with lineColor matching fill to ensure no contrast.
    effectiveFillColor = normalizedFillColor ?? "#FFFFFF";
    effectiveLineColor = normalizedLineColor ?? (normalizedFillColor ?? "#FFFFFF");
    effectiveLineWidth = shape.lineWidth != null ? shape.lineWidth : 0.01;
  } else {
    effectiveFillColor = normalizedFillColor;
    effectiveLineColor = normalizedLineColor;
    effectiveLineWidth = shape.lineWidth;
    // COM treats missing/0 lineWidth as unset and draws a default outline on
    // AddShape. Match fill with a sub-pixel line so lineless filled creates
    // stay visually borderless (same workaround as the text-box parity path).
    if (
      isGeometricCreateForOfficeParity &&
      normalizedFillColor &&
      rawLineColor == null &&
      shape.lineWidth == null
    ) {
      effectiveLineColor = normalizedFillColor;
      effectiveLineWidth = 0.01;
    }
  }
  const effectiveFontSize = readPositiveNumber(shape.fontSize);

  // Force wordWrap=true on all creates (except line-fallback thin rectangles).
  // The Citi master's default text body format may disable wrapping for new
  // text frames; sending wordWrap explicitly overrides that at the shape level.
  const isAnyCreateWithText =
    shapeAction === "create" && !lineLikeForBounds && !shape.table && !shape.chart;
  const wordWrapDefault = isAnyCreateWithText ? true : undefined;
  const textVerticalAlignmentDefault: "top" | "middle" | undefined =
    isTextBoxCreateForOfficeParity ? "top"
    : isGeometricCreateForOfficeParity ? "middle"
    : undefined;
  const explicitTextVerticalAlignment =
    normalizeTextVerticalAlignmentForBatch(shape.verticalAlignment) ??
    normalizeTextVerticalAlignmentForBatch(shape.textVerticalAlignment);
  const chartPayload: ShapeModification["chart"] | undefined =
    shape.chart ??
    (shape.chartEdit
      ? {
          type: "Chart" as const,
          ...shape.chartEdit,
        } as ShapeModification["chart"]
      : undefined);

  // Clone the model shape and patch ONLY the fields the FactSet host adapter
  // must transform. Rebuilding the object from a fixed allowlist previously
  // reordered `paragraphOps` ahead of the whole-shape font defaults (flipping
  // paragraph-level formatting precedence on the host) and silently dropped any
  // field not in the list (e.g. `horizontalAlignment`). Spreading preserves the
  // model's original property order and forwards fields we don't need to touch
  // (text, textReplacements, paragraphOps, fontFamily, bold, italic, image
  // payloads, cellUpdates, horizontalAlignment, verticalAlignment, ...).
  const mappedShape: ShapeModification = { ...shape };

  // Identity: resolved/unique create id (slide-ID normalization must not corrupt
  // edit/delete targets) and the normalized action (defaults to "modify").
  assignOrDeleteShapeField(mappedShape, "id", shapeId);
  assignOrDeleteShapeField(mappedShape, "action", shapeAction);

  // Geometry: bounded values plus create-time defaults. Assign only when
  // defined so sparse modify ops keep their original (absent) geometry.
  assignOrDeleteShapeField(mappedShape, "left", finalizedCreateShapeBounds.left);
  assignOrDeleteShapeField(mappedShape, "top", finalizedCreateShapeBounds.top);
  assignOrDeleteShapeField(mappedShape, "width", finalizedCreateShapeBounds.width);
  assignOrDeleteShapeField(mappedShape, "height", finalizedCreateShapeBounds.height);

  // Line/connector creates are coerced to a thin filled rectangle.
  assignOrDeleteShapeField(mappedShape, "geometricType", geometricTypeForBatch);

  // Colors: normalize to host hex; a dropped (invalid) or absent token removes
  // the field so the host does not inherit a stale model value.
  assignOrDeleteShapeField(mappedShape, "fillColor", effectiveFillColor);
  assignOrDeleteShapeField(mappedShape, "lineColor", effectiveLineColor);
  assignOrDeleteShapeField(mappedShape, "lineWidth", effectiveLineWidth);
  assignOrDeleteShapeField(mappedShape, "textColor", normalizedTextColor);

  // Font size: keep only a positive numeric value.
  assignOrDeleteShapeField(mappedShape, "fontSize", effectiveFontSize);

  // Table: create-shell normalization + bounds on the layout target region.
  assignOrDeleteShapeField(mappedShape, "table", boundedTable);

  // Images: the FactSet batch primitive inserts images from raw base64 bytes.
  // `imageDataUri` is an add-in/LLM-facing representation used during hydrate.
  if (hasAnyImagePayload(shape)) {
    const inlineImagePayload = normalizeInlineImagePayloadForBatch(shape);
    assignOrDeleteShapeField(mappedShape, "imageDataUri", undefined);
    assignOrDeleteShapeField(mappedShape, "imageBase64", inlineImagePayload.imageBase64);
    assignOrDeleteShapeField(mappedShape, "imageMimeType", inlineImagePayload.imageMimeType);
    if (inlineImagePayload.imageBase64) {
      assignOrDeleteShapeField(mappedShape, "imageUrl", undefined);
      assignOrDeleteShapeField(mappedShape, "brandIconId", undefined);
    }
  }

  // Chart: full chart create/replace is forwarded as-is; an existing-chart
  // title/legend patch (`chartEdit`) is lowered to the minimal `Chart` payload
  // that PerformBatchPptOperations applies in place. Never send both.
  assignOrDeleteShapeField(mappedShape, "chart", chartPayload);
  if (chartPayload !== undefined) {
    delete (mappedShape as { chartEdit?: unknown }).chartEdit;
  }

  // System-applied text-frame defaults (host compatibility for new shapes).
  assignOrDeleteShapeField(mappedShape, "wordWrap", shape.wordWrap ?? wordWrapDefault);
  assignOrDeleteShapeField(
    mappedShape,
    "textVerticalAlignment",
    explicitTextVerticalAlignment ?? textVerticalAlignmentDefault
  );
  return {
    shape: mappedShape,
    decisionFlags: {
      lineFallbackToRectangle: lineLikeForBounds,
      droppedInvalidFillColor,
      droppedInvalidLineColor,
      droppedInvalidTextColor,
    },
  };
}

function resolveSlideTarget(
  slide: PresentationModification["slides"][number],
  slideLookups: SlideInfoLookups
): { slideId: string; index: number | undefined } {
  const explicitId = pickFirstNonEmptyString(slide.id);
  const explicitIndex = Number.isFinite(slide.index) ? slide.index : undefined;

  const byId = explicitId
    ? slideLookups.byNormalizedId.get(normalizeSlideIdentity(explicitId))
    : undefined;
  const byIndex =
    typeof explicitIndex === "number"
      ? slideLookups.byIndex.get(explicitIndex)
      : undefined;

  const resolvedId = normalizeSlideIdentity(
    String(byId?.slideId ?? byIndex?.slideId ?? explicitId ?? "")
  );
  const resolvedIndex = byId?.index ?? byIndex?.index ?? explicitIndex;
  return { slideId: resolvedId, index: resolvedIndex };
}

function reverseDuplicateRunsForInsertionOrder(
  slides: PresentationModification["slides"]
): PresentationModification["slides"] {
  if (!slides || slides.length <= 1) return slides;

  const result: PresentationModification["slides"] = [];
  let i = 0;
  while (i < slides.length) {
    const current = slides[i];
    if ((current.action ?? "modify") !== "duplicate") {
      result.push(current);
      i++;
      continue;
    }
    const sourceId = current.id;
    const sourceIndex = current.index;
    const runStart = i;
    while (
      i < slides.length &&
      (slides[i].action ?? "modify") === "duplicate" &&
      slides[i].id === sourceId &&
      slides[i].index === sourceIndex
    ) {
      i++;
    }
    const run = slides.slice(runStart, i);
    if (run.length > 1) {
      run.reverse();
    }
    result.push(...run);
  }
  return result;
}

function prepareBatchOperations(
  modifications: PresentationModification,
  slideInfos: SlideInfo[],
  bounds: SlideBounds,
  log?: Logger
): PreparedBatchOperations {
  const requestedSlides = modifications.slides || [];
  const requestedShapeCount = requestedSlides.reduce(
    (sum, slide) => sum + (slide.shapes?.length || 0),
    0
  );
  const batchDecisionSummary: BatchDecisionSummary = {
    requestedSlides: requestedSlides.length,
    requestedShapes: requestedShapeCount,
    preparedSlides: 0,
    preparedShapes: 0,
    skippedByIssue: 0,
    lineFallbacksToRectangle: 0,
    droppedInvalidFillColor: 0,
    droppedInvalidLineColor: 0,
    droppedInvalidTextColor: 0,
  };

  logMessage(
    log,
    `Preparing batch operations: requestedSlides=${requestedSlides.length}, ` +
      `bounds=${bounds.slideWidth}x${bounds.slideHeight}pt`
  );
  const slideLookups = buildSlideInfoLookups(slideInfos);
  const issues: ApplyDiagnostic[] = [];
  const slides: PresentationModification["slides"] = [];

  for (const slide of requestedSlides) {
    const action = slide.action ?? "modify";
    const resolved = resolveSlideTarget(slide, slideLookups);
    if (!resolved.slideId || typeof resolved.index !== "number") {
      logMessage(
        log,
        `Could not resolve slide target: requested(index=${slide.index}, id=${summarizeSlideIdForLog(slide.id)}) action=${action}`
      );
      issues.push({
        code: "SLIDE_INDEX_OUT_OF_RANGE",
        category: "target_mismatch",
        message: `Could not resolve slide target for operation at index ${slide.index}.`,
        slideIndex: slide.index,
        slideId: slide.id,
        action: action === "duplicate" || action === "delete" ? action : "modify",
      });
      continue;
    }
    logMessage(
      log,
      `Mapped slide request(index=${slide.index}, id=${summarizeSlideIdForLog(slide.id)}) -> ` +
        `target(index=${resolved.index}, id=${summarizeSlideIdForLog(resolved.slideId)}), action=${action}, ` +
        `requestedShapes=${(slide.shapes || []).length}`
    );

    const mappedShapes: ShapeModification[] = [];
    const synthesizedFollowUpsByCreateId = new Map<string, ShapeModification[]>();
    let synthesizedFollowUpShapeCount = 0;
    const createShapeDebugRows: Array<{
      shapePos: number;
      shapeId?: string;
      modelRect: { left?: number; top?: number; width?: number; height?: number };
      mappedRect: { left?: number; top?: number; width?: number; height?: number };
    }> = [];
    for (let shapePos = 0; shapePos < (slide.shapes || []).length; shapePos += 1) {
      const shape = slide.shapes[shapePos];
      const mapped = mapShapeForBatch(
        shape,
        resolved.index,
        resolved.slideId,
        shapePos,
        bounds,
        log
      );
      if (mapped.issue) {
        issues.push(mapped.issue);
      }
      if (mapped.decisionFlags) {
        if (mapped.decisionFlags.lineFallbackToRectangle) {
          batchDecisionSummary.lineFallbacksToRectangle += 1;
        }
        if (mapped.decisionFlags.droppedInvalidFillColor) {
          batchDecisionSummary.droppedInvalidFillColor += 1;
        }
        if (mapped.decisionFlags.droppedInvalidLineColor) {
          batchDecisionSummary.droppedInvalidLineColor += 1;
        }
        if (mapped.decisionFlags.droppedInvalidTextColor) {
          batchDecisionSummary.droppedInvalidTextColor += 1;
        }
      }
      if (mapped.shape) {
        const hasCreateTableStyleFields = hasCreateTableStyleFieldsForBatch(shape.table);
        const styleFollowUp =
          mapped.shape.action === "create" && hasCreateTableStyleFields
            ? buildCreateTableStyleFollowUpForBatch(shape, mapped.shape.id)
            : undefined;
        const preparedShape =
          mapped.shape.action === "create" && hasCreateTableStyleFields
            ? {
                ...mapped.shape,
                table: stripCreateTableStylingForBatch(mapped.shape.table),
              }
            : mapped.shape;
        mappedShapes.push(preparedShape);
        if (styleFollowUp) {
          const createId =
            typeof preparedShape.id === "string" && preparedShape.id.trim().length > 0
              ? preparedShape.id
              : undefined;
          if (createId) {
            const existingFollowUps = synthesizedFollowUpsByCreateId.get(createId) || [];
            existingFollowUps.push(styleFollowUp);
            synthesizedFollowUpsByCreateId.set(createId, existingFollowUps);
          } else {
            mappedShapes.push(styleFollowUp);
          }
          synthesizedFollowUpShapeCount += 1;
        }
        const rawAction = shape.action ?? "modify";
        const mappedAction = preparedShape.action ?? "modify";
        if (rawAction === "create" && mappedAction === "create") {
          createShapeDebugRows.push({
            shapePos,
            shapeId: preparedShape.id,
            modelRect: {
              left: shape.left,
              top: shape.top,
              width: shape.width,
              height: shape.height,
            },
            mappedRect: {
              left: preparedShape.left,
              top: preparedShape.top,
              width: preparedShape.width,
              height: preparedShape.height,
            },
          });
        }
      }
    }
    if (mappedShapes.length < (slide.shapes || []).length) {
      logMessage(
        log,
        `Slide #${resolved.index} retained ${mappedShapes.length}/${(slide.shapes || []).length} shape ops after validation`
      );
    }
    if (synthesizedFollowUpShapeCount > 0) {
      logMessage(
        log,
        `Slide #${resolved.index} synthesized ${synthesizedFollowUpShapeCount} follow-up table style op(s)`
      );
    }

    // Sort `create` ops by layer priority before handing the batch to the host.
    // The buffering in generateSlideContent.ts (FactSet bulk path) depends on this
    // step to keep text-only creates above decorations like arrows/dividers/
    // background rectangles, regardless of the order the LLM streamed them in.
    const layeredShapes = reorderCreateShapesForLayering(mappedShapes, bounds);
    const finalizedShapes = attachSynthesizedFollowUpsAfterCreates(
      layeredShapes,
      synthesizedFollowUpsByCreateId
    );
    if (createShapeDebugRows.length > 0) {
      const usedFinalShapeIndices = new Set<number>();
      for (const row of createShapeDebugRows) {
        let finalShapeIndex = finalizedShapes.findIndex(
          (candidate, index) =>
            !usedFinalShapeIndices.has(index) &&
            (candidate.action ?? "modify") === "create" &&
            candidate.id === row.shapeId
        );
        if (finalShapeIndex < 0) {
          finalShapeIndex = finalizedShapes.findIndex(
            (candidate, index) =>
              !usedFinalShapeIndices.has(index) && (candidate.action ?? "modify") === "create"
          );
        }
        if (finalShapeIndex >= 0) {
          usedFinalShapeIndices.add(finalShapeIndex);
        }
        const finalShape = finalShapeIndex >= 0 ? finalizedShapes[finalShapeIndex] : undefined;
        logMessage(
          log,
          `Create shape trace: slide=${resolved.index}, ` +
            `shape=${row.shapeId ?? `pos-${row.shapePos + 1}`}, ` +
            `model={${formatShapeRectForLog(row.modelRect)}}, ` +
            `prepared={${formatShapeRectForLog(row.mappedRect)}}, ` +
            `final={${formatShapeRectForLog({
              left: finalShape?.left,
              top: finalShape?.top,
              width: finalShape?.width,
              height: finalShape?.height,
            })}}, ` +
            `style={fill=${pickFirstNonEmptyString(finalShape?.fillColor) ?? "none"}, ` +
            `line=${pickFirstNonEmptyString(finalShape?.lineColor) ?? "none"}, ` +
            `text=${pickFirstNonEmptyString(finalShape?.textColor) ?? "none"}, ` +
            `lineWidth=${formatPointValueForLog(finalShape?.lineWidth)}}`
        );
      }
    }

    slides.push({
      id: resolved.slideId,
      index: resolved.index,
      action,
      shapes: action === "delete" ? [] : finalizedShapes,
    });
  }

  // When multiple duplicates target the same source slide, the COM host
  // inserts each copy directly after the source. Processing them in the
  // original (plan) order yields reversed slides because each insert pushes
  // earlier copies further away. Reversing consecutive same-source
  // duplicate runs makes the first planned duplicate end up closest to the
  // source after all inserts complete.
  const reorderedSlides = reverseDuplicateRunsForInsertionOrder(slides);

  const operations: PresentationModification = {
    slides: reorderedSlides,
    ...(Array.isArray(modifications.diagnostics) && modifications.diagnostics.length > 0
      ? { diagnostics: modifications.diagnostics }
      : {}),
  };
  logPreparedBatchBoundsSummary(operations, bounds, log);
  const mappedShapeCount = operations.slides.reduce((sum, slide) => sum + (slide.shapes?.length || 0), 0);
  batchDecisionSummary.preparedSlides = operations.slides.length;
  batchDecisionSummary.preparedShapes = mappedShapeCount;
  batchDecisionSummary.skippedByIssue = issues.length;
  updateIntentParityDecisionSummary({ batch: batchDecisionSummary });

  logMessage(
    log,
    `Prepared batch payload: slides=${slides.length}, shapes=${mappedShapeCount}, issues=${issues.length}`
  );
  logMessage(
    log,
    `Batch decision summary: requestedShapes=${batchDecisionSummary.requestedShapes}, ` +
      `preparedShapes=${batchDecisionSummary.preparedShapes}, skipped=${batchDecisionSummary.skippedByIssue}, ` +
      `lineFallbacks=${batchDecisionSummary.lineFallbacksToRectangle}, ` +
      `droppedInvalidFill=${batchDecisionSummary.droppedInvalidFillColor}, ` +
      `droppedInvalidLine=${batchDecisionSummary.droppedInvalidLineColor}, ` +
      `droppedInvalidText=${batchDecisionSummary.droppedInvalidTextColor}`
  );
  if (issues.length > 0) {
    for (const issue of issues.slice(0, 5)) {
      logMessage(log, `${issue.code}: ${issue.message}`);
    }
    if (issues.length > 5) {
      logMessage(log, `...and ${issues.length - 5} additional prep issue(s).`);
    }
  }
  return { operations, issues };
}

export function prepareBatchOperationsForTesting(
  modifications: PresentationModification,
  slideInfos: Array<{ slideId: string; index: number; tags?: Record<string, string> }>,
  bounds: SlideBounds
): { operations: PresentationModification; issues: ApplyDiagnostic[] } {
  return prepareBatchOperations(modifications, slideInfos, bounds);
}

function markAppliedSummaryFromSlides(
  summary: ApplyResultSummary,
  slides: PresentationModification["slides"]
): void {
  const touchedSlides = new Set<number>();
  for (const slide of slides) {
    const slideAction = slide.action ?? "modify";
    touchedSlides.add(slide.index);
    if (slideAction === "duplicate") {
      summary.applied.byAction.duplicate += 1;
    } else if (slideAction === "delete") {
      summary.applied.byAction.delete += 1;
    }
    for (const shape of slide.shapes) {
      const action =
        shape.action === "create" || shape.action === "modify" || shape.action === "delete"
          ? shape.action
          : "modify";
      summary.applied.shapes += 1;
      summary.applied.byAction[action] += 1;
    }
  }
  summary.applied.slides = touchedSlides.size;
}

type BatchOperationResultStatus = "success" | "failure" | "unknown";

interface BatchOperationResultRow {
  shapeId?: unknown;
  slideId?: unknown;
  action?: unknown;
  status?: unknown;
  failureReason?: unknown;
}

function getBatchOperationResults(batchResult: unknown): BatchOperationResultRow[] | null {
  if (!batchResult || typeof batchResult !== "object") return null;

  const root = batchResult as Record<string, unknown>;
  const candidates = [
    root,
    root.operationsResult,
    root.operationResult,
    root.result,
  ];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const operationResults = (candidate as Record<string, unknown>).operationResults;
    if (Array.isArray(operationResults)) {
      return operationResults.filter(
        (entry): entry is BatchOperationResultRow => Boolean(entry && typeof entry === "object")
      );
    }
  }

  return null;
}

function hasBatchOperationResults(batchResult: unknown): boolean {
  return Boolean(getBatchOperationResults(batchResult)?.length);
}

function normalizeBatchOperationStatus(status: unknown): BatchOperationResultStatus {
  if (typeof status !== "string") return "unknown";
  const normalized = status.trim().toLowerCase();
  if (normalized === "success" || normalized === "succeeded" || normalized === "ok") {
    return "success";
  }
  if (normalized === "failure" || normalized === "failed" || normalized === "error") {
    return "failure";
  }
  return "unknown";
}

function normalizeBatchOperationAction(action: unknown): keyof ApplyActionCounts | null {
  if (typeof action !== "string") return null;
  const normalized = action.trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes("duplicate")) return "duplicate";
  if (normalized.includes("delete")) return "delete";
  if (normalized.includes("create") || normalized.includes("add")) return "create";
  if (normalized.includes("modify") || normalized.includes("edit") || normalized.includes("update")) {
    return "modify";
  }
  return null;
}

function getBatchFailureCode(action: keyof ApplyActionCounts | null): ApplyReasonCode {
  if (action === "create") return "SHAPE_CREATE_FAILED";
  if (action === "delete") return "SHAPE_DELETE_FAILED";
  if (action === "duplicate") return "SLIDE_DUPLICATE_FAILED";
  return "SHAPE_MODIFY_FAILED";
}

function toBatchFailureMessage(
  row: BatchOperationResultRow,
  action: keyof ApplyActionCounts | null,
  shapeId: string | undefined
): string {
  const failureReason =
    typeof row.failureReason === "string" && row.failureReason.trim().length > 0
      ? row.failureReason.trim()
      : "Host reported operation failure.";
  const actionLabel = action ?? "operation";
  return shapeId
    ? `Batch ${actionLabel} failed for shape ${shapeId}: ${failureReason}`
    : `Batch ${actionLabel} failed: ${failureReason}`;
}

function markAppliedSummaryFromBatchResult(
  summary: ApplyResultSummary,
  batchResult: unknown,
  slides: PresentationModification["slides"]
): boolean {
  const operationResults = getBatchOperationResults(batchResult);
  if (!operationResults || operationResults.length === 0) return false;

  const slideIndexById = new Map<string, number>();
  for (const slide of slides) {
    if (slide.id) {
      slideIndexById.set(normalizeSlideIdentity(slide.id), slide.index);
    }
  }

  const touchedSlides = new Set<number>();

  for (const row of operationResults) {
    const status = normalizeBatchOperationStatus(row.status);
    const action = normalizeBatchOperationAction(row.action);
    const shapeId = pickFirstNonEmptyString(row.shapeId);
    const slideId = pickFirstNonEmptyString(row.slideId);
    const slideIndex = slideId ? slideIndexById.get(normalizeSlideIdentity(slideId)) : undefined;

    if (status === "success") {
      if (shapeId && action && action !== "duplicate") {
        summary.applied.shapes += 1;
        summary.applied.byAction[action] += 1;
        if (typeof slideIndex === "number") {
          touchedSlides.add(slideIndex);
        }
      } else if (action === "duplicate" || action === "delete") {
        summary.applied.byAction[action] += 1;
        if (typeof slideIndex === "number") {
          touchedSlides.add(slideIndex);
        }
      }
      continue;
    }

    if (status === "failure") {
      recordError(summary, {
        code: getBatchFailureCode(action),
        category: "apply_error",
        message: toBatchFailureMessage(row, action, shapeId),
        slideIndex,
        slideId,
        shapeId,
        action: action ?? undefined,
      });
    }
  }

  summary.applied.slides = Math.max(summary.applied.slides, touchedSlides.size);
  return true;
}

export function markAppliedSummaryFromBatchResultForTesting(
  summary: ApplyResultSummary,
  batchResult: unknown,
  slides: PresentationModification["slides"]
): boolean {
  return markAppliedSummaryFromBatchResult(summary, batchResult, slides);
}

function getImageDimensions(height?: number): { width: number; height: number } {
  const finalHeight = Math.max(120, Math.round(height ?? DEFAULT_IMAGE_HEIGHT));
  const finalWidth = Math.max(160, Math.round(finalHeight * getSlideAspectRatio()));
  return { width: finalWidth, height: finalHeight };
}

async function readSlideBoundsFromOfficePageSetup(log?: Logger): Promise<SlideBounds | null> {
  const cached =
    lastOfficePageSetupDimensionMetadata &&
    isFresh(lastOfficePageSetupDimensionMetadata.capturedAt, OFFICE_PAGE_SETUP_CACHE_TTL_MS)
      ? lastOfficePageSetupDimensionMetadata
      : null;
  if (cached) {
    const ageMs = Math.max(0, nowMs() - cached.capturedAt);
    logMessage(
      log,
      `Using cached Office pageSetup bounds ${cached.width}x${cached.height}pt ` +
        `(age=${ageMs}ms, ttl=${OFFICE_PAGE_SETUP_CACHE_TTL_MS}ms)`
    );
    return { slideWidth: cached.width, slideHeight: cached.height };
  }

  try {
    const dimensions = await guardedPowerPointRun<{ width: number; height: number } | null>(
      async (context) => {
        const pageSetup = context.presentation.pageSetup;
        pageSetup.load("slideWidth,slideHeight");
        await context.sync();
        const width = readPositiveNumber(pageSetup.slideWidth);
        const height = readPositiveNumber(pageSetup.slideHeight);
        return width && height ? { width, height } : null;
      },
      null
    );

    if (!dimensions) {
      return null;
    }

    const metadata: ContextSlideDimensionMetadata = {
      width: roundToTenth(dimensions.width),
      height: roundToTenth(dimensions.height),
      source: "office.pageSetup.slideWidth/slideHeight",
      capturedAt: nowMs(),
    };
    lastOfficePageSetupDimensionMetadata = metadata;
    logMessage(
      log,
      `Office pageSetup slide dimensions: ${metadata.width}x${metadata.height}pt ` +
        `(source=${metadata.source})`
    );
    return { slideWidth: metadata.width, slideHeight: metadata.height };
  } catch (error) {
    const message = toErrorMessage(error);
    if (!isPropertyNotLoadedError(error)) {
      logMessage(
        log,
        `Failed to read Office pageSetup slide dimensions (continuing): ${message}`
      );
    }
    return null;
  }
}

interface ResolveSlideBoundsOptions {
  preferredBounds?: SlideBounds;
  activeSlideRefFromContext?: ActiveSlideContextInfo | null;
  extractorResult?: ExtractorResultLite | null;
  presentationRef?: Partial<FactSetPresentationRef> | null;
}

function resolveSlideBoundsFromExtractorResult(
  extractorResult: ExtractorResultLite,
  sourcePrefix: string,
  log?: Logger
): SlideBounds | null {
  const resolved = resolveExtractorSlideDimensions(extractorResult);
  if (resolved.source.startsWith("default")) {
    logMessage(
      log,
      `${sourcePrefix} did not provide explicit slide dimensions for bounds resolution; ` +
        "skipping shape-envelope inference for deterministic Office parity."
    );
    return null;
  }

  const extractorBounds = {
    slideWidth: roundToTenth(resolved.width),
    slideHeight: roundToTenth(resolved.height),
  };
  logMessage(
    log,
    `Resolved slide bounds ${extractorBounds.slideWidth}x${extractorBounds.slideHeight}pt ` +
      `from ${sourcePrefix} (${resolved.source})`
  );
  return extractorBounds;
}

async function readSlideBoundsFromExtractorIntent(
  log?: Logger,
  options?: { presentationRef?: Partial<FactSetPresentationRef> | null }
): Promise<SlideBounds | null> {
  try {
    const extractorRaw = await sendIntentById(
      "runOfficeExtractor",
      {},
      EXTRACTOR_BOUNDS_INTENT_TIMEOUT_MS,
      {
        log,
        traceLabel: "bounds-extractor-fallback",
        presentationRef: options?.presentationRef,
      }
    );
    const extractorResult = await parseExtractorResultAsync(extractorRaw);
    if (!extractorResult) {
      logMessage(log, "RunOfficeExtractor returned an invalid payload for bounds fallback.");
      return null;
    }
    return resolveSlideBoundsFromExtractorResult(extractorResult, "RunOfficeExtractor", log);
  } catch (error) {
    logMessage(
      log,
      `RunOfficeExtractor bounds fallback failed (continuing): ${toErrorMessage(error)}`
    );
    return null;
  }
}

async function resolveSlideBoundsDimensions(
  log?: Logger,
  options?: ResolveSlideBoundsOptions
): Promise<SlideBounds> {
  logMessage(log, "Resolving slide bounds for intent apply...");
  const presentationRef = normalizePresentationRef(options?.presentationRef);
  const bypassSharedCache = Boolean(presentationRef);
  const preferredWidth = readPositiveNumber(options?.preferredBounds?.slideWidth);
  const preferredHeight = readPositiveNumber(options?.preferredBounds?.slideHeight);
  if (preferredWidth && preferredHeight) {
    const preferredBounds = {
      slideWidth: roundToTenth(preferredWidth),
      slideHeight: roundToTenth(preferredHeight),
    };
    if (!bypassSharedCache) {
      setCachedSlideBounds(preferredBounds, "caller-preferred");
    }
    logMessage(
      log,
      `Using caller-preferred slide bounds ${preferredBounds.slideWidth}x${preferredBounds.slideHeight}pt`
    );
    return preferredBounds;
  }

  // Wave 4 fix 2: cache-first short-circuit. Slide dimensions are deck-level
  // and immutable across the kinds of operations a single chat-edit cycle
  // performs (shape modifications, undo wrapping). Without this guard, every
  // call to `resolveSlideBoundsDimensions` re-runs the entire resolution
  // chain, and on hosts where Office pageSetup and active-context don't
  // expose dimensions that chain ends at `readSlideBoundsFromExtractorIntent`
  // — a full unfiltered `RunOfficeExtractor` round-trip purely for two
  // numbers. The cache is invalidated by any `resetIntentBridgeCache()` call
  // with `clearSlideBounds: true` (including the deck-clear path inside
  // `extractPresentationContentViaIntents` for force-refresh non-scoped
  // extracts), so real deck-resize events still resolve fresh bounds.
  if (
    !bypassSharedCache &&
    intentBridgeCache.slideBounds &&
    isFresh(intentBridgeCache.slideBoundsFetchedAt, SLIDE_BOUNDS_CACHE_TTL_MS)
  ) {
    const cachedBounds = intentBridgeCache.slideBounds;
    logMessage(
      log,
      `Using cached slide bounds ${cachedBounds.slideWidth}x${cachedBounds.slideHeight}pt ` +
        `(source=${lastSlideBoundsResolutionSource ?? "unknown"}, ` +
        `ageMs=${nowMs() - intentBridgeCache.slideBoundsFetchedAt})`
    );
    return { ...cachedBounds };
  }

  const officePageSetupBounds = await readSlideBoundsFromOfficePageSetup(log);
  if (officePageSetupBounds) {
    if (!bypassSharedCache) {
      setCachedSlideBounds(officePageSetupBounds, "office-page-setup");
    }
    logMessage(
      log,
      `Resolved slide bounds ${officePageSetupBounds.slideWidth}x${officePageSetupBounds.slideHeight}pt ` +
        "from Office pageSetup"
    );
    return officePageSetupBounds;
  }

  const extractorBoundsFromOptions =
    options?.extractorResult
      ? resolveSlideBoundsFromExtractorResult(options.extractorResult, "extractor payload", log)
      : null;
  if (extractorBoundsFromOptions) {
    if (!bypassSharedCache) {
      setCachedSlideBounds(extractorBoundsFromOptions, "extractor-payload");
    }
    logMessage(
      log,
      `Resolved slide bounds ${extractorBoundsFromOptions.slideWidth}x${extractorBoundsFromOptions.slideHeight}pt ` +
        "from RunOfficeExtractor payload (preferred over active-context fallback)."
    );
    return extractorBoundsFromOptions;
  }

  if (!bypassSharedCache) {
    const activeSlideRefFromContext =
      options?.activeSlideRefFromContext ?? (await captureActiveSlideRefFromContext(log));
    const activeContextWidth = readPositiveNumber(activeSlideRefFromContext?.slideWidth);
    const activeContextHeight = readPositiveNumber(activeSlideRefFromContext?.slideHeight);
    if (activeContextWidth && activeContextHeight) {
      const activeContextBounds = {
        slideWidth: roundToTenth(activeContextWidth),
        slideHeight: roundToTenth(activeContextHeight),
      };
      setCachedSlideBounds(activeContextBounds, "active-context");
      logMessage(
        log,
        `Resolved slide bounds ${activeContextBounds.slideWidth}x${activeContextBounds.slideHeight}pt ` +
          `from getActivePresentationContext (${activeSlideRefFromContext?.dimensionSource ?? "activeContext"})`
      );
      return activeContextBounds;
    }
  }

  const extractorIntentBounds = await readSlideBoundsFromExtractorIntent(log, {
    presentationRef,
  });
  if (extractorIntentBounds) {
    if (!bypassSharedCache) {
      setCachedSlideBounds(extractorIntentBounds, "extractor-intent");
    }
    return extractorIntentBounds;
  }

  const defaultBounds = {
    slideWidth: DEFAULT_SLIDE_WIDTH_PT,
    slideHeight: DEFAULT_SLIDE_HEIGHT_PT,
  };
  if (!bypassSharedCache) {
    setCachedSlideBounds(defaultBounds, "default");
  }
  logMessage(
    log,
    `Office pageSetup, active-context, and extractor bounds unavailable; ` +
      `falling back to default slide bounds ` +
      `${defaultBounds.slideWidth}x${defaultBounds.slideHeight}pt`
  );
  return defaultBounds;
}

interface PreparedBatchOperations {
  operations: PresentationModification;
  issues: ApplyDiagnostic[];
}

function stripInlineImagePayload(shape: ShapeModification): ShapeModification {
  return {
    ...shape,
    imageDataUri: undefined,
    imageBase64: undefined,
    imageMimeType: undefined,
  };
}

function shapeNeedsInlineImageExtraction(shape: ShapeModification): boolean {
  if (!hasAnyImagePayload(shape)) return false;
  if (hasInlineImagePayload(shape)) return false;
  if (typeof shape.brandIconId === "string" && shape.brandIconId.trim().length > 0) {
    return false;
  }
  return typeof shape.imageUrl === "string" && shape.imageUrl.trim().length > 0;
}

async function fetchInlineImagePayloadFromUrl(
  imageUrl: string,
  log?: Logger
): Promise<{ dataUri: string; mimeType: string } | null> {
  const url = imageUrl.trim();
  if (!url) return null;
  try {
    const response = await fetch(url, { credentials: "omit", mode: "cors" });
    if (!response.ok) {
      logMessage(log, `Image fetch failed (${response.status}) for ${url}`);
      return null;
    }
    const blob = await response.blob();
    const mimeType = (blob.type || "image/png").toLowerCase();
    if (!mimeType.startsWith("image/")) {
      logMessage(log, `Ignoring non-image payload (${mimeType}) for ${url}`);
      return null;
    }
    const rawBase64 = await blobToBase64(blob);
    if (!rawBase64) return null;
    const normalizedBase64 = normalizeBase64Payload(rawBase64);
    if (!normalizedBase64) {
      logMessage(log, `Could not normalize fetched image payload for ${url}`);
      return null;
    }
    const dataUri = `data:${mimeType};base64,${normalizedBase64}`;
    return { dataUri, mimeType };
  } catch (error) {
    logMessage(log, `Could not inline image URL ${url}: ${toErrorMessage(error)}`);
    return null;
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        resolve("");
        return;
      }
      const parsed = parseBase64DataUri(result);
      resolve(parsed?.base64 ?? "");
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error("Failed to read blob as base64."));
    };
    reader.readAsDataURL(blob);
  });
}

async function hydrateImagePayloadsForModifications(
  modifications: PresentationModification,
  log?: Logger
): Promise<PresentationModification> {
  if (!modifications.slides || modifications.slides.length === 0) return modifications;

  let touched = false;
  const nextSlides = await Promise.all(
    modifications.slides.map(async (slide) => {
      if (!slide.shapes || slide.shapes.length === 0) return slide;
      const nextShapes = await Promise.all(
        slide.shapes.map(async (shape) => {
          if (shape.action !== "create") return shape;

          // Precedence: inline bytes > brandIconId > imageUrl
          if (hasInlineImagePayload(shape)) {
            if (typeof shape.brandIconId === "string" && shape.brandIconId.trim()) {
              touched = true;
              return { ...shape, brandIconId: undefined };
            }
            return shape;
          }

          const brandIconId =
            typeof shape.brandIconId === "string" ? shape.brandIconId.trim() : "";
          if (brandIconId) {
            const dataUri = await resolveBrandIconDataUri(brandIconId, (message) =>
              logMessage(log, message)
            );
            touched = true;
            if (!dataUri) {
              // Keep brandIconId so prepareBatch skips (image payload without inline bytes).
              return {
                ...shape,
                imageDataUri: undefined,
                imageBase64: undefined,
                imageMimeType: undefined,
              };
            }
            return {
              ...shape,
              imageDataUri: dataUri,
              imageBase64: undefined,
              imageMimeType: undefined,
              brandIconId: undefined,
              imageUrl: undefined,
            };
          }

          if (!shapeNeedsInlineImageExtraction(shape)) return shape;
          const imageUrl = shape.imageUrl?.trim();
          if (!imageUrl) return shape;
          const inlinePayload = await fetchInlineImagePayloadFromUrl(imageUrl, log);
          if (!inlinePayload) {
            touched = true;
            return stripInlineImagePayload(shape);
          }
          touched = true;
          return {
            ...shape,
            imageDataUri: inlinePayload.dataUri,
            imageBase64: undefined,
            imageMimeType: undefined,
          };
        })
      );
      if (nextShapes === slide.shapes) return slide;
      return {
        ...slide,
        shapes: nextShapes,
      };
    })
  );

  if (!touched) return modifications;
  return {
    ...modifications,
    slides: nextSlides,
  };
}

export async function hydrateImagePayloadsForModificationsForTesting(
  modifications: PresentationModification,
  log?: Logger
): Promise<PresentationModification> {
  return hydrateImagePayloadsForModifications(modifications, log);
}
function readSelectedIndexFromActiveSlide(
  presentation: PresentationContent,
  activeSlideId?: string
): number {
  const target = normalizeSlideIdentity(String(activeSlideId ?? ""));
  if (!target) return presentation.selectedSlideIndex;
  const idx = presentation.slides.findIndex(
    (slide) => normalizeSlideIdentity(slide.id) === target || String(slide.id) === String(activeSlideId)
  );
  return idx >= 0 ? idx : presentation.selectedSlideIndex;
}

function resolveSelectedSlideIndexFromContext(
  presentation: PresentationContent,
  activeSlideRefFromContext: ActiveSlideContextInfo | null,
  fallbackActiveSlideId?: string
): number {
  const resolvedById = readSelectedIndexFromActiveSlide(
    presentation,
    activeSlideRefFromContext?.id ?? fallbackActiveSlideId
  );
  if (typeof activeSlideRefFromContext?.index !== "number" || presentation.slides.length === 0) {
    return resolvedById;
  }

  const clampedIndex = Math.max(
    0,
    Math.min(activeSlideRefFromContext.index, presentation.slides.length - 1)
  );
  if (!activeSlideRefFromContext.id) {
    return clampedIndex;
  }

  const expectedId = normalizeSlideIdentity(activeSlideRefFromContext.id);
  const actualId = normalizeSlideIdentity(presentation.slides[clampedIndex]?.id ?? "");
  return expectedId && actualId && expectedId === actualId ? clampedIndex : resolvedById;
}

function resolveSlideRefFromInfos(slideInfos: SlideInfo[], activeSlideId?: string): SlideRef | null {
  const target = normalizeSlideIdentity(String(activeSlideId ?? ""));
  if (!target) return null;
  const match = slideInfos.find(
    (slide) =>
      normalizeSlideIdentity(slide.slideId) === target || String(slide.slideId) === String(activeSlideId)
  );
  if (!match) return null;
  return {
    id: match.slideId,
    index: match.index,
    ...(typeof match.slideNumber === "number" ? { slideNumber: match.slideNumber } : {}),
  };
}

async function captureActiveSlideRefFromContext(
  log?: Logger,
  options?: { forceRefresh?: boolean }
): Promise<ActiveSlideContextInfo | null> {
  const forceRefresh = options?.forceRefresh ?? false;
  if (!forceRefresh && inFlightActiveSlideRefFromContext) {
    return inFlightActiveSlideRefFromContext;
  }

  const fetchPromise = (async () => {
    try {
      const contextResult = await sendIntentById(
        "getActivePresentationContext",
        {},
        ACTIVE_CONTEXT_INTENT_TIMEOUT_MS,
        { log, traceLabel: "active-context" }
      );
      const activeSlideRef = parseActivePresentationContextPayload(contextResult);
      if (!activeSlideRef) {
        logMessage(log, "Active context intent returned no usable slide reference.");
        return null;
      }
      if (activeSlideRef.id) {
        setCachedActiveSlideId(activeSlideRef.id);
      }
      if (typeof activeSlideRef.index === "number") {
        setCachedSelectedSlideRef({
          id: activeSlideRef.id ?? "",
          index: activeSlideRef.index,
          ...(typeof activeSlideRef.slideNumber === "number"
            ? { slideNumber: activeSlideRef.slideNumber }
            : {}),
          ...(activeSlideRef.openPresentationId
            ? { openPresentationId: activeSlideRef.openPresentationId }
            : {}),
        });
      }
      if (activeSlideRef.slideWidth && activeSlideRef.slideHeight) {
        logMessage(
          log,
          `Active context slide dimensions: ${activeSlideRef.slideWidth}x${activeSlideRef.slideHeight}pt ` +
            `(source=${activeSlideRef.dimensionSource ?? "activeContext"})`
        );
      } else {
        logMessage(
          log,
          `Active context returned without slide dimensions ` +
            `(id=${summarizeSlideIdForLog(activeSlideRef.id)}, index=${
              typeof activeSlideRef.index === "number" ? activeSlideRef.index : "na"
            })`
        );
      }
      return activeSlideRef;
    } catch (error) {
      logMessage(
        log,
        `GetActivePresentationContext failed (continuing, timeout=${ACTIVE_CONTEXT_INTENT_TIMEOUT_MS}ms): ` +
          toErrorMessage(error)
      );
      return null;
    }
  })();

  if (forceRefresh) {
    return fetchPromise;
  }

  inFlightActiveSlideRefFromContext = fetchPromise.finally(() => {
    inFlightActiveSlideRefFromContext = null;
  });
  return inFlightActiveSlideRefFromContext;
}

export async function getActivePresentationRefViaIntents(
  log?: Logger
): Promise<FactSetPresentationRef | null> {
  const context = await getActivePresentationContextViaIntents(log);
  return context.presentationRef;
}

export async function getActivePresentationContextViaIntents(
  log?: Logger
): Promise<ActivePresentationContextViaIntents> {
  const activeSlideRef = await captureActiveSlideRefFromContext(log);
  return {
    presentationRef: buildPresentationRefFromActiveContext(activeSlideRef) ?? null,
    selectedSlideRef: buildSelectedSlideRefFromActiveContext(activeSlideRef),
  };
}

interface SendIntentOptions {
  log?: Logger;
  traceLabel?: string;
  presentationRef?: Partial<FactSetPresentationRef> | null;
}

function toTruncatedSingleLineForLog(value: unknown, maxChars = INTENT_LOG_PREVIEW_MAX_CHARS): string {
  let serialized: string;
  if (value === undefined) {
    serialized = "undefined";
  } else if (value === null) {
    serialized = "null";
  } else if (typeof value === "string") {
    serialized = value;
  } else {
    try {
      serialized = JSON.stringify(value);
    } catch {
      serialized = String(value);
    }
  }

  const compact = serialized.replace(/\s+/g, " ").trim();
  if (compact.length <= maxChars) {
    return compact;
  }
  return `${compact.slice(0, maxChars)}... (truncated, len=${compact.length})`;
}

function redactIntentContextForLog(
  intentId: IntentId,
  payload: Record<string, unknown>
): Record<string, unknown> {
  const redacted: Record<string, unknown> = { ...payload };
  const redactLongString = (key: string, label: string) => {
    const raw = redacted[key];
    if (typeof raw === "string") {
      redacted[key] = `[${label} len=${raw.length}]`;
    } else if (raw !== undefined) {
      redacted[key] = `[${label}]`;
    }
  };

  if (intentId === "addImageFromBase64") {
    redactLongString("dataUri", "dataUri");
  }
  if (intentId === "insertSlidesFromBase64") {
    redactLongString("base64Slides", "base64Slides");
  }
  if (intentId === "insertSlidesFromBase64WithSourceFormatting") {
    redactLongString("base64Slides", "base64Slides");
  }
  if (intentId === "writeFileFromBase64") {
    redactLongString("base64File", "base64File");
    if (redacted.filePath !== undefined) {
      redacted.filePath = "[localFilePath]";
    }
    if (redacted.fullFileName !== undefined) {
      redacted.fullFileName = "[localFilePath]";
    }
  }
  redactLongString("imageDataUri", "dataUri");
  redactLongString("imageBase64", "base64");
  redactLongString("json", "json");
  return redacted;
}

function summarizeIntentRequestForLog(
  intentId: IntentId,
  payload: Record<string, unknown>
): string {
  return toTruncatedSingleLineForLog(redactIntentContextForLog(intentId, payload));
}

function summarizeIntentResultForLog(intentId: IntentId, result: unknown): string {
  if (intentId === "getSelectedShapes") {
    const selectedShapes = extractSelectedShapesResult(result);
    return (
      `totalShapes=${selectedShapes.totalShapes ?? selectedShapes.shapes.length}, ` +
      `sample=${selectedShapes.shapes
        .slice(0, 3)
        .map((shape) => summarizeSlideIdForLog(shape.id))
        .join(", ")}`
    );
  }

  if (intentId === "getSlideList") {
    const slideInfos = extractSlideInfos(result);
    const activeSlideId = extractActiveSlideIdFromSlideList(result);
    return (
      `slideCount=${slideInfos.length}, active=${summarizeSlideIdForLog(activeSlideId)}, ` +
      `sample=${summarizeSlideInfosForLog(slideInfos)}`
    );
  }

  if (intentId === "getSlideCount") {
    const count = parseSlideCountResult(result);
    return `slideCount=${typeof count === "number" ? count : "na"}`;
  }

  if (intentId === "getActivePresentationContext") {
    const parsed = parseActivePresentationContextPayload(result);
    if (parsed) {
      return (
        `slideId=${summarizeSlideIdForLog(parsed.id)}, index=${
          typeof parsed.index === "number" ? parsed.index : "na"
        }, presentationId=${parsed.openPresentationId ?? "na"}, dims=${parsed.slideWidth ?? "na"}x${parsed.slideHeight ?? "na"}pt, ` +
        `source=${parsed.dimensionSource ?? "na"}`
      );
    }
  }

  if (intentId === "runOfficeExtractor") {
    // P2 log-preview short-circuit: on large decks the inner `result` is a
    // multi-megabyte doubly-encoded JSON string. Parsing it just to print
    // five stats would burn the same main-thread time we moved off-thread
    // on the hot path. For oversize strings, emit a length-only summary;
    // the actual extractor consumers use `parseExtractorResultAsync` which
    // hands the parse to a worker.
    if (result && typeof result === "object") {
      const obj = result as Record<string, unknown>;
      if (typeof obj.result === "string" && obj.result.length > LOG_PREVIEW_EXTRACTOR_PARSE_MAX_BYTES) {
        return `resultLen=${obj.result.length} (parse skipped for log preview)`;
      }
    }
    const parsed = parseExtractorResult(result);
    if (parsed) {
      const dims = resolveExtractorSlideDimensions(parsed);
      const slideCount = Array.isArray(parsed.slides) ? parsed.slides.length : 0;
        const keys = Object.keys(parsed as unknown as Record<string, unknown>).join(",");
      return (
        `slides=${slideCount}, dims=${dims.width}x${dims.height}pt, ` +
        `source=${dims.source}, scale=${dims.geometryScale}, keys=${keys}`
      );
    }
  }

  if (intentId === "getSlideImage") {
    const imageResult = extractSlideImageResult(result);
    return (
      `slideId=${summarizeSlideIdForLog(imageResult.slideId)}, ` +
      `imageBase64Len=${imageResult.imageBase64?.length ?? 0}`
    );
  }

  if (intentId === "getSlideImages") {
    const batchResult = extractSlideImagesResult(result);
    const firstImage = batchResult.images[0];
    return (
      `imageCount=${batchResult.images.length}, errors=${batchResult.errors.length}, ` +
      `firstSlide=${summarizeSlideIdForLog(firstImage?.slideId)}, ` +
      `firstBase64Len=${firstImage?.imageBase64?.length ?? 0}`
    );
  }

  if (intentId === "exportPresentation") {
    const base64 = extractExportPresentationBase64(result);
    return `presentationBase64Len=${base64.length}`;
  }

  if (intentId === "getPreslinkFilePaths") {
    try {
      const filePathsResult = extractGetPreslinkFilePathsResult(result);
      const shapeCount = filePathsResult.slides.reduce((sum, slide) => sum + slide.shapes.length, 0);
      const fileCount = filePathsResult.slides.reduce(
        (sum, slide) => sum + slide.shapes.reduce((shapeSum, shape) => shapeSum + shape.files.length, 0),
        0
      );
      return `slides=${filePathsResult.slides.length}, shapes=${shapeCount}, files=${fileCount}`;
    } catch {
      return "preslinkFilePaths=unparseable";
    }
  }

  if (intentId === "fetchPreslinkFiles") {
    try {
      const filesResult = extractFetchPreslinkFilesResult(result);
      const shapeCount = filesResult.slides.reduce((sum, slide) => sum + slide.shapes.length, 0);
      const linkKeyCount = filesResult.slides.reduce(
        (sum, slide) =>
          sum + slide.shapes.reduce((shapeSum, shape) => shapeSum + shape.linkKeys.length, 0),
        0
      );
      const linkedBase64TotalLen = filesResult.linkedFiles.reduce(
        (sum, file) => sum + file.zipBase64.length,
        0
      );
      return (
        `slides=${filesResult.slides.length}, shapes=${shapeCount}, ` +
        `linkedFiles=${filesResult.linkedFiles.length}, linkKeys=${linkKeyCount}, ` +
        `presentationBase64Len=${filesResult.presentation.zipBase64.length}, ` +
        `linkedBase64TotalLen=${linkedBase64TotalLen}`
      );
    } catch {
      return "preslinkFiles=unparseable";
    }
  }

  if (intentId === "deleteSlides") {
    const failureReason = getBatchFailureReason(result);
    return failureReason ? `failure=${failureReason}` : "success=true";
  }

  if (
    intentId === "insertSlidesFromBase64" ||
    intentId === "insertSlidesFromBase64WithSourceFormatting"
  ) {
    const insertedSlides = extractInsertedSlidesFromBase64Result(result);
    if (insertedSlides.length > 0) {
      const firstInserted = insertedSlides[0];
      return (
        `insertedSlides=${insertedSlides.length}, ` +
        `first=${summarizeSlideIdForLog(firstInserted.insertedSlideId)}@${firstInserted.insertedSlideIndex ?? "na"}`
      );
    }
    const failureReason = getBatchFailureReason(result);
    return failureReason ? `failure=${failureReason}` : toTruncatedSingleLineForLog(result);
  }

  if (intentId === "writeFileFromBase64") {
    const failureReason = getBatchFailureReason(result);
    return failureReason ? `failure=${failureReason}` : "success=true";
  }

  if (intentId === "addSlideTags") {
    const failureReason = getBatchFailureReason(result);
    return failureReason ? `failure=${failureReason}` : "success=true";
  }

  return toTruncatedSingleLineForLog(result);
}

function logIntentMappingOnce(log?: Logger): void {
  if (hasLoggedIntentMapping) return;
  hasLoggedIntentMapping = true;
  const mapping = (Object.entries(INTENT_DEFS) as Array<[IntentId, IntentDefinition]>)
    .map(([id, def]) => `${id}=>${def.intentName} (${def.contextType})`)
    .join(" | ");
  logMessage(log, `Intent mappings: ${mapping}`);
}

async function sendIntentById(
  intentId: IntentId,
  context: Record<string, unknown> = {},
  timeoutMs = DEFAULT_TIMEOUT_MS,
  options?: SendIntentOptions
): Promise<unknown> {
  const def = getIntentDef(intentId);
  const payload: Record<string, unknown> = withPresentationRef(
    intentId,
    {
      type: def.contextType,
      ...context,
    },
    options?.presentationRef
  );
  const tracePrefix = options?.traceLabel ? `Intent(${options.traceLabel})` : "Intent";
  const startedAt = nowMs();

  logIntentMappingOnce(options?.log);
  logMessage(
    options?.log,
    `${tracePrefix} REQ id=${intentId}, name=${def.intentName}, timeout=${timeoutMs}ms, ` +
      `context=${summarizeIntentRequestForLog(intentId, payload)}`
  );

  try {
    const result = await raiseIntent(def.intentName, payload, timeoutMs, POWERPOINT_INTENT_APP_ID);
    const elapsedMs = nowMs() - startedAt;
    logMessage(
      options?.log,
      `${tracePrefix} RES id=${intentId}, elapsed=${elapsedMs}ms, ` +
        `summary=${summarizeIntentResultForLog(intentId, result)}`
    );
    return result;
  } catch (error) {
    const elapsedMs = nowMs() - startedAt;
    logMessage(
      options?.log,
      `${tracePrefix} ERR id=${intentId}, elapsed=${elapsedMs}ms, error=${toErrorMessage(error)}`
    );
    throw error;
  }
}

async function ensureSlideInfos(
  log?: Logger,
  options?: { forceRefresh?: boolean; presentationRef?: Partial<FactSetPresentationRef> | null }
): Promise<SlideInfo[]> {
  const forceRefresh = options?.forceRefresh ?? false;
  const presentationRef = normalizePresentationRef(options?.presentationRef);
  const bypassSharedCache = Boolean(presentationRef);
  if (!forceRefresh && !bypassSharedCache) {
    const cached = getCachedSlideInfos();
    if (cached) {
      logMessage(log, `Using cached slide list (${cached.length} slide(s)).`);
      return cached;
    }
    if (inFlightSlideInfos) {
      logMessage(log, "Awaiting in-flight slide list request...");
      return inFlightSlideInfos;
    }
  }

  const fetchPromise = (async () => {
    logMessage(log, "Fetching slide list intent...");
    const slideListResult = await sendIntentById(
      "getSlideList",
      {},
      SLIDE_LIST_INTENT_TIMEOUT_MS,
      {
        log,
        traceLabel: "slide-list",
        presentationRef,
      }
    );
    const slideInfos = extractSlideInfos(slideListResult);
    if (!bypassSharedCache) {
      setCachedSlideInfos(slideInfos);
    }
    const activeSlideIdFromList = extractActiveSlideIdFromSlideList(slideListResult);
    if (!bypassSharedCache) {
      setCachedActiveSlideId(activeSlideIdFromList);
    }
    logMessage(
      log,
      `Slide list received: count=${slideInfos.length}, active=${summarizeSlideIdForLog(activeSlideIdFromList)}`
    );
    logMessage(log, `Slide list sample: ${summarizeSlideInfosForLog(slideInfos)}`);
    return slideInfos;
  })();

  if (forceRefresh || bypassSharedCache) {
    return fetchPromise;
  }
  inFlightSlideInfos = fetchPromise.finally(() => {
    inFlightSlideInfos = null;
  });
  return inFlightSlideInfos;
}

/**
 * Raises `factset.GetHostContext` and returns the parsed `appType` /
 * `platformType`. The host-context intent is host-agnostic (it lives in the
 * common `factset.*` namespace) so it is safe to call from either a PowerPoint
 * or Excel host. Throws on timeout, transport errors, or a malformed response.
 */
export async function raiseHostContextIntent(
  timeoutMs = 8_000
): Promise<{ appType?: string; platformType?: string }> {
  const raw = await raiseIntent("factset.GetHostContext", { type: "factset.hostContext" }, timeoutMs);
  if (!raw || typeof raw !== "object") {
    return {};
  }
  const obj = raw as Record<string, unknown>;
  const hostContextCandidate =
    (obj.hostContext && typeof obj.hostContext === "object"
      ? (obj.hostContext as Record<string, unknown>)
      : undefined) ??
    (obj.context && typeof obj.context === "object"
      ? ((obj.context as Record<string, unknown>).hostContext as Record<string, unknown> | undefined)
      : undefined);
  const source = hostContextCandidate ?? obj;
  const appType = typeof source.appType === "string" ? source.appType : undefined;
  const platformType = typeof source.platformType === "string" ? source.platformType : undefined;
  return { appType, platformType };
}

export interface FactSetResolvedSlideBounds {
  slideWidth: number;
  slideHeight: number;
  source: string;
  resolvedAtMs: number;
}

async function resolveSlideBoundsWithMetadata(
  log?: Logger,
  options?: ResolveSlideBoundsOptions
): Promise<FactSetResolvedSlideBounds> {
  const bounds = await resolveSlideBoundsDimensions(log, options);
  return {
    slideWidth: bounds.slideWidth,
    slideHeight: bounds.slideHeight,
    source: lastSlideBoundsResolutionSource ?? "unknown",
    resolvedAtMs: intentBridgeCache.slideBoundsFetchedAt || nowMs(),
  };
}

export async function getResolvedSlideBoundsViaIntents(
  log?: Logger,
  options?: {
    preferredBounds?: SlideBounds;
    presentationRef?: Partial<FactSetPresentationRef> | null;
  }
): Promise<FactSetResolvedSlideBounds> {
  return resolveSlideBoundsWithMetadata(log, {
    preferredBounds: options?.preferredBounds,
    presentationRef: options?.presentationRef,
  });
}

export async function listSlidesViaIntents(
  log?: Logger,
  options?: { forceRefresh?: boolean; presentationRef?: Partial<FactSetPresentationRef> | null }
): Promise<FactSetSlideInfo[]> {
  const infos = await ensureSlideInfos(log, options);
  return infos.map((info) => ({
    slideId: info.slideId,
    index: info.index,
    ...(typeof info.slideNumber === "number" ? { slideNumber: info.slideNumber } : {}),
  }));
}

/**
 * Fetch the total slide count of the active presentation via
 * `factset.Presentation.GetSlideCount`. The host documents this intent as
 * lightweight and active-presentation-only (no `presentationRef` accepted),
 * so it's safe to poll alongside other cheap reads.
 *
 * Used by the chat extract path to size the slide window (see
 * `utils/contextWindow.computeContextSlideWindow`) without paying for the
 * heavier `GetSlideList` round-trip that walks every slide and serializes
 * per-slide tags.
 */
export async function getSlideCountViaIntents(log?: Logger): Promise<number> {
  const result = await sendIntentById(
    "getSlideCount",
    {},
    ACTIVE_CONTEXT_INTENT_TIMEOUT_MS,
    { log, traceLabel: "slide-count" }
  );
  const count = parseSlideCountResult(result);
  if (typeof count !== "number") {
    throw new Error("GetSlideCount returned no usable slide count.");
  }
  return count;
}

/**
 * Read slide tags via the cached `getSlideList` response (the FactSet host
 * surfaces a slide's persisted tags inside each `slidesInformation` entry).
 *
 * Returns the matching slide's tag map, or `null` if either the slide isn't
 * present in the list or the host build does not surface tag data at all.
 */
export async function getSlideTagsViaIntents(input: {
  slideId?: string;
  index?: number;
  log?: Logger;
  forceRefresh?: boolean;
}): Promise<Record<string, string> | null> {
  const infos = await ensureSlideInfos(input.log, { forceRefresh: input.forceRefresh });
  const targetSlideId = (input.slideId ?? "").trim();
  const targetIndex = typeof input.index === "number" ? input.index : undefined;
  for (const info of infos) {
    if (targetSlideId && info.slideId === targetSlideId) {
      return info.tags ?? null;
    }
    if (
      !targetSlideId &&
      typeof targetIndex === "number" &&
      info.index === targetIndex
    ) {
      return info.tags ?? null;
    }
  }
  return null;
}

export async function getSelectedShapesViaIntents(options?: {
  log?: Logger;
  timeoutMs?: number;
}): Promise<FactSetSelectedShapesResult> {
  const timeoutMs = options?.timeoutMs ?? 20_000;
  const result = await sendIntentById("getSelectedShapes", {}, timeoutMs, {
    log: options?.log,
    traceLabel: "selected-shapes",
  });
  return extractSelectedShapesResult(result);
}

async function resolveSlideRefFromSelectedShapesViaIntents(
  slideInfos: SlideInfo[],
  log?: Logger
): Promise<SlideRef | null> {
  try {
    const selectedShapes = await getSelectedShapesViaIntents({ log, timeoutMs: 12_000 });
    if (selectedShapes.shapes.length === 0) {
      return null;
    }
    const shapeWithSlide = selectedShapes.shapes.find((shape) => Boolean(shape.slideId));
    if (!shapeWithSlide?.slideId) {
      logMessage(
        log,
        "GetSelectedShapes returned shape(s) without slideId; cannot derive selected slide from shape selection."
      );
      return null;
    }
    const resolvedByShapeSlide = resolveSlideRefFromInfos(slideInfos, shapeWithSlide.slideId);
    if (resolvedByShapeSlide) {
      logMessage(
        log,
        `Resolved selected slide from GetSelectedShapes: ${summarizeSlideIdForLog(
          resolvedByShapeSlide.id
        )}@${resolvedByShapeSlide.index}`
      );
      return resolvedByShapeSlide;
    }
    logMessage(
      log,
      `GetSelectedShapes returned slideId=${summarizeSlideIdForLog(
        shapeWithSlide.slideId
      )}, but it was not found in current slide list.`
    );
    return null;
  } catch (error) {
    logMessage(
      log,
      `GetSelectedShapes fallback failed (continuing): ${toErrorMessage(error)}`
    );
    return null;
  }
}

// Fast ID -> index lookup from slide-list cache.
export async function resolveSlideIndexByIdViaIntents(
  slideId: string,
  log?: Logger,
  options?: { presentationRef?: Partial<FactSetPresentationRef> | null }
): Promise<number | null> {
  if (!slideId) return null;
  const infos = await ensureSlideInfos(log, {
    presentationRef: options?.presentationRef,
  });
  const match = infos.find((info) => info.slideId === slideId);
  if (match) return match.index;
  const refreshed = await ensureSlideInfos(log, {
    forceRefresh: true,
    presentationRef: options?.presentationRef,
  });
  const refreshedMatch = refreshed.find((info) => info.slideId === slideId);
  return refreshedMatch ? refreshedMatch.index : null;
}

export async function extractPresentationContentViaIntents(
  log?: Logger,
  options?: ExtractPresentationContentViaIntentsOptions
): Promise<PresentationContent> {
  const forceRefresh = options?.forceRefresh ?? false;
  const presentationRef = normalizePresentationRef(options?.presentationRef);
  const normalizedExtractorContext = normalizeRunOfficeExtractorContextOptions(options);
  const isScopedExtractorRequest = normalizedExtractorContext.hasFilters;
  const bypassSharedCache = Boolean(presentationRef);
  logMessage(
    log,
    `extractPresentationContentViaIntents(forceRefresh=${forceRefresh}, scoped=${isScopedExtractorRequest}, ` +
      `slideRanges=${normalizedExtractorContext.includeSlideRanges.length}, shapes=${normalizedExtractorContext.includeShapes.length})`
  );

  if ((forceRefresh || bypassSharedCache) && !isScopedExtractorRequest) {
    resetIntentBridgeCache({
      clearSlideInfos: true,
      clearActiveSlideId: true,
      clearSelectedSlideRef: true,
      clearPresentation: true,
      clearSlideBounds: true,
    });
  } else if (!isScopedExtractorRequest) {
    const cachedPresentation = getCachedPresentation();
    if (cachedPresentation) {
      logMessage(
        log,
        `Using cached extracted presentation (${cachedPresentation.slides.length} slide(s)).`
      );
      return clonePresentationContent(cachedPresentation);
    }
    if (inFlightPresentation) {
      logMessage(log, "Awaiting in-flight extractor request...");
      return clonePresentationContent(await inFlightPresentation);
    }
  }

  const fetchPromise = (async () => {
    const [activeSlideRefFromContext, slideInfos, extractorRaw] = await Promise.all([
      captureActiveSlideRefFromContext(log, { forceRefresh }),
      ensureSlideInfos(log, { forceRefresh, presentationRef }),
      sendIntentById("runOfficeExtractor", normalizedExtractorContext.context, 30_000, {
        log,
        traceLabel: isScopedExtractorRequest ? "extract-content-scoped" : "extract-content",
        presentationRef,
      }),
    ]);

    const extractorResult = await parseExtractorResultAsync(extractorRaw);
    if (!extractorResult) {
      throw new Error("RunOfficeExtractor returned an invalid payload.");
    }

    const preferredSlideIds = resolvePreferredSlideIdsForExtractor(
      slideInfos,
      normalizedExtractorContext
    );
    let presentation = toPresentationContentFromExtractor(extractorResult, preferredSlideIds, log);
    presentation = alignExtractorSlideIndicesWithSlideInfos(presentation, slideInfos, log);
    const resolvedBounds = await resolveSlideBoundsDimensions(log, {
      preferredBounds: options?.preferredBounds,
      activeSlideRefFromContext,
      extractorResult,
    });
    presentation.slideWidth = resolvedBounds.slideWidth;
    presentation.slideHeight = resolvedBounds.slideHeight;
    presentation.selectedSlideIndex = resolveSelectedSlideIndexFromContext(
      presentation,
      activeSlideRefFromContext,
      activeSlideRefFromContext?.id
    );
    logExtractedPresentationSnapshot(presentation, log);
    if (!isScopedExtractorRequest && !bypassSharedCache) {
      setCachedPresentation(presentation);
      const selectedSlide =
        presentation.slides.find((slide) => slide.index === presentation.selectedSlideIndex) ??
        presentation.slides[presentation.selectedSlideIndex];
      if (selectedSlide) {
        setCachedSelectedSlideRef({
          id: selectedSlide.id,
          index: selectedSlide.index,
          ...(typeof selectedSlide.slideNumber === "number"
            ? { slideNumber: selectedSlide.slideNumber }
            : {}),
          ...(activeSlideRefFromContext?.openPresentationId
            ? { openPresentationId: activeSlideRefFromContext.openPresentationId }
            : {}),
        });
      }
    }
    return presentation;
  })();

  if (forceRefresh || isScopedExtractorRequest || bypassSharedCache) {
    return fetchPromise;
  }
  inFlightPresentation = fetchPromise.finally(() => {
    inFlightPresentation = null;
  });
  const resolved = await inFlightPresentation;
  return clonePresentationContent(resolved);
}

async function getSlideImagesAsBase64ViaIntents(options: {
  slideIds?: string[];
  width: number;
  height: number;
  presentationRef?: FactSetPresentationRef | null;
  log?: Logger;
  timeoutMs?: number;
  traceLabel?: string;
}): Promise<{ images: FactSetSlideImageBatchItem[]; errors: unknown[] }> {
  const context: Record<string, unknown> = {
    width: Math.max(0, Math.round(options.width)),
    height: Math.max(0, Math.round(options.height)),
  };
  const normalizedSlideIds = (options.slideIds ?? [])
    .map((slideId) => String(slideId).trim())
    .filter((slideId): slideId is string => slideId.length > 0);
  if (normalizedSlideIds.length > 0) {
    context.slideIds = normalizedSlideIds;
  }
  const presentationRef = toIntentPresentationRef(options.presentationRef);
  if (presentationRef) {
    context.presentationRef = presentationRef;
  }
  const result = await sendIntentById("getSlideImages", context, options.timeoutMs ?? 20_000, {
    log: options.log,
    traceLabel: options.traceLabel ?? "slide-images",
  });
  return extractSlideImagesResult(result);
}

export async function exportSlideAsImageViaIntents(
  slideIndex?: number,
  options?: {
    height?: number;
    log?: Logger;
    presentationRef?: FactSetPresentationRef | null;
    targetSlideId?: string;
    forceRefreshSlideList?: boolean;
  }
): Promise<string> {
  const { width, height } = getImageDimensions(options?.height);
  let targetSlideId = pickFirstNonEmptySlideId(options?.targetSlideId);

  if (!targetSlideId && slideIndex !== undefined) {
    const slideInfos = await ensureSlideInfos(options?.log, {
      forceRefresh: options?.forceRefreshSlideList ?? false,
      presentationRef: options?.presentationRef,
    });
    const targetByExactIndex = slideInfos.find((info) => info.index === slideIndex);
    const targetByPosition = slideInfos[slideIndex];
    targetSlideId = targetByExactIndex?.slideId ?? targetByPosition?.slideId;
  }

  const presentationRef =
    normalizePresentationRef(options?.presentationRef) ??
    buildPresentationRefFromActiveContext(await captureActiveSlideRefFromContext(options?.log));
  const batchResult = await getSlideImagesAsBase64ViaIntents({
    slideIds: targetSlideId ? [targetSlideId] : undefined,
    width,
    height,
    presentationRef,
    log: options?.log,
    traceLabel: "slide-image",
  });
  const matchedImage = targetSlideId
    ? batchResult.images.find(
        (image) =>
          normalizeSlideIdentity(image.slideId ?? "") === normalizeSlideIdentity(targetSlideId ?? "")
      )
    : batchResult.images[0];
  return (matchedImage ?? batchResult.images[0])?.imageBase64 ?? "";
}

export async function deleteSlidesViaIntents(
  slideIds: string[],
  options?: {
    log?: Logger;
    timeoutMs?: number;
    presentationRef?: Partial<FactSetPresentationRef> | null;
  }
): Promise<boolean> {
  const candidateIds = Array.isArray(slideIds)
    ? slideIds
        .map((id) => (id == null ? "" : String(id).trim()))
        .filter((id): id is string => id.length > 0)
    : [];
  if (candidateIds.length === 0) {
    return false;
  }

  const timeoutMs = options?.timeoutMs ?? 20_000;
  logMessage(options?.log, `Deleting ${candidateIds.length} slide(s) via DeleteSlides intent...`);
  const result = await sendIntentById("deleteSlides", { slideIds: candidateIds }, timeoutMs, {
    log: options?.log,
    traceLabel: "delete-slides",
    presentationRef: options?.presentationRef,
  });
  const failureReason = getBatchFailureReason(result);
  if (failureReason) {
    throw new Error(failureReason);
  }

  resetIntentBridgeCache({
    clearSlideInfos: true,
    clearActiveSlideId: true,
    clearSelectedSlideRef: true,
    clearPresentation: true,
  });
  return true;
}

export async function exportPresentationAsBase64ViaIntents(options?: {
  slideIds?: string[];
  log?: Logger;
  presentationRef?: Partial<FactSetPresentationRef> | null;
}): Promise<string> {
  const normalizedSlideIds = (options?.slideIds ?? [])
    .map((slideId) => String(slideId).trim())
    .filter((slideId) => slideId.length > 0);

  const context: Record<string, unknown> = {};
  if (normalizedSlideIds.length > 0) {
    context.slideIds = normalizedSlideIds;
  }

  const result = await sendIntentById("exportPresentation", context, 30_000, {
    log: options?.log,
    traceLabel: "export-presentation",
    presentationRef: options?.presentationRef,
  });
  const presentationAsBase64 = extractExportPresentationBase64(result);
  if (!presentationAsBase64) {
    throw new Error("ExportPresentation returned an empty payload.");
  }
  return presentationAsBase64;
}

/**
 * Resolve PresLink-linked file paths for the requested shapes without fetching
 * workbook or presentation bytes.
 */
export async function getPreslinkFilePathsViaIntents(
  options: GetPreslinkFilePathsViaIntentsOptions
): Promise<GetPreslinkFilePathsResult> {
  const shapeRefs = normalizePreslinkShapeRefs(options.shapeRefs);
  const presentationRef = normalizePreslinkPresentationRef(options.presentationRef);
  const context: Record<string, unknown> = { shapeRefs };
  if (presentationRef.contextRef) {
    context.presentationRef = presentationRef.contextRef;
  }

  const result = await sendIntentById(
    "getPreslinkFilePaths",
    context,
    options.timeoutMs ?? 30_000,
    {
      log: options.log,
      traceLabel: "get-preslink-file-paths",
      presentationRef: presentationRef.sendRef,
    }
  );
  return extractGetPreslinkFilePathsResult(result);
}

/**
 * Fetch PresLink-linked workbook archives, and optionally the host presentation,
 * for the requested shapes.
 */
export async function fetchPreslinkFilesViaIntents(
  options: FetchPreslinkFilesViaIntentsOptions
): Promise<FetchPreslinkFilesResult> {
  const shapeRefs = normalizePreslinkShapeRefs(options.shapeRefs);
  const presentationRef = normalizePreslinkPresentationRef(options.presentationRef);
  const context: Record<string, unknown> = { shapeRefs };
  if (presentationRef.contextRef) {
    context.presentationRef = presentationRef.contextRef;
  }
  if (options.includePptFile) {
    context.options = { includePptFile: true };
  }

  const result = await sendIntentById(
    "fetchPreslinkFiles",
    context,
    options.timeoutMs ?? 60_000,
    {
      log: options.log,
      traceLabel: "fetch-preslink-files",
      presentationRef: presentationRef.sendRef,
    }
  );
  return extractFetchPreslinkFilesResult(result);
}

/**
 * Open a .pptx file as a new presentation from base64. Replaces the current
 * active presentation in the FactSet host.
 */
export async function openPresentationFromBase64ViaIntents(options: {
  pptxBase64: string;
  log?: Logger;
}): Promise<void> {
  const pptxBase64 = (options.pptxBase64 || "").trim();
  if (!pptxBase64) {
    throw new Error("openPresentationFromBase64 requires a non-empty base64 payload.");
  }

  await sendIntentById(
    "openPresentationFromBase64",
    { pptxPresentationBase64: pptxBase64 },
    30_000,
    { log: options.log, traceLabel: "open-presentation-from-base64" }
  );

  resetIntentBridgeCache({
    clearSlideInfos: true,
    clearPresentation: true,
    clearSlideBounds: true,
  });
}

/**
 * Materialize a base64-encoded file through the FactSet desktop host. The host,
 * rather than the Finster web server, owns the user's local filesystem.
 */
export async function writeFileFromBase64ViaIntents(options: {
  filePath: string;
  base64File: string;
  log?: Logger;
  timeoutMs?: number;
  onDebug?: (event: {
    appId: string;
    attempt: number;
    error?: string;
    intentName: string;
    status: "start" | "success" | "retryable_error";
  }) => void;
}): Promise<void> {
  const filePath = (options.filePath || "").trim();
  const base64File = (options.base64File || "").replace(/\s+/g, "");
  if (!filePath) {
    throw new Error("writeFileFromBase64 requires a non-empty filePath.");
  }
  if (!base64File) {
    throw new Error("writeFileFromBase64 requires a non-empty base64File payload.");
  }

  const context = { type: "factset.writeFileFromBase64", fullFileName: filePath, base64File, overwrite: true };
  const timeoutMs = options.timeoutMs ?? 60_000;
  const candidates: Array<{ intentName: string; appId: IntentAppId }> = [
    { intentName: "factset.WriteFileFromBase64", appId: DEFAULT_APP_ID },
    { intentName: "factset.writeFileFromBase64", appId: DEFAULT_APP_ID },
    { intentName: "factset.Presentation.WriteFileFromBase64", appId: POWERPOINT_INTENT_APP_ID },
    { intentName: "factset.writeFileFromBase64", appId: POWERPOINT_INTENT_APP_ID },
  ];
  const isRetryableRoutingError = (error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error);
    return /\b(Unknown intent|No app|not found|No handler)\b/i.test(message);
  };
  let lastRoutingError: unknown = null;

  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    if (!candidate) continue;
    options.onDebug?.({
      appId: candidate.appId.appId,
      attempt: index + 1,
      intentName: candidate.intentName,
      status: "start",
    });
    logMessage(
      options.log,
      `Intent(write-file-from-base64) REQ attempt=${index + 1}/${candidates.length}, ` +
        `name=${candidate.intentName}, appId=${candidate.appId.appId}, timeout=${timeoutMs}ms, ` +
        `context=${summarizeIntentRequestForLog("writeFileFromBase64", context)}`
    );

    try {
      const result = await raiseIntent(
        candidate.intentName,
        context,
        timeoutMs,
        candidate.appId
      );
      logMessage(
        options.log,
        `Intent(write-file-from-base64) RES attempt=${index + 1}/${candidates.length}, ` +
          `summary=${summarizeIntentResultForLog("writeFileFromBase64", result)}`
      );
      throwIfFactSetErrorResult(result, "Failed to write local file from base64");
      const failureReason = getBatchFailureReason(result);
      if (failureReason) {
        throw new Error(failureReason);
      }
      options.onDebug?.({
        appId: candidate.appId.appId,
        attempt: index + 1,
        intentName: candidate.intentName,
        status: "success",
      });
      return;
    } catch (error) {
      if (!isRetryableRoutingError(error)) {
        throw error;
      }
      lastRoutingError = error;
      const message = error instanceof Error ? error.message : String(error);
      logMessage(
        options.log,
        `Intent(write-file-from-base64) routing retry attempt=${index + 1}/${candidates.length}, ` +
          `error=${message}`
      );
      options.onDebug?.({
        appId: candidate.appId.appId,
        attempt: index + 1,
        error: message,
        intentName: candidate.intentName,
        status: "retryable_error",
      });
    }
  }
  throw lastRoutingError instanceof Error ? lastRoutingError : new Error(String(lastRoutingError));
}

/**
 * Insert a PPTX into an explicitly targeted presentation while preserving the
 * source theme, fonts, and line spacing. This Atlas intent is synchronous and
 * must not run concurrently with other PowerPoint automation.
 */
export async function insertSlidesFromBase64WithSourceFormattingViaIntents(options: {
  base64Slides: string;
  targetSlideId: string;
  presentationRef: Partial<FactSetPresentationRef>;
  log?: Logger;
  timeoutMs?: number;
  onDebug?: (event: {
    error?: string;
    insertedCount?: number;
    resultPreview?: string;
    status: "start" | "raw_result" | "success" | "failure_reason" | "error";
  }) => void;
}): Promise<FactSetInsertedSlideRef[]> {
  const base64Slides = (options.base64Slides || "").trim();
  const targetSlideId = (options.targetSlideId || "").trim();
  const presentationRef = toIntentPresentationRef(options.presentationRef);
  if (!base64Slides) {
    throw new Error(
      "InsertSlidesFromBase64WithSourceFormatting requires a non-empty base64 payload."
    );
  }
  if (!targetSlideId) {
    throw new Error(
      "InsertSlidesFromBase64WithSourceFormatting requires an explicit targetSlideId."
    );
  }
  if (!presentationRef) {
    throw new Error(
      "InsertSlidesFromBase64WithSourceFormatting requires an explicit presentationRef."
    );
  }

  options.onDebug?.({ status: "start" });
  let result: unknown;
  try {
    result = await sendIntentById(
      "insertSlidesFromBase64WithSourceFormatting",
      { base64Slides, targetSlideId },
      options.timeoutMs ?? 120_000,
      {
        log: options.log,
        traceLabel: "insert-slides-from-base64-with-source-formatting",
        presentationRef,
      }
    );
  } catch (error) {
    options.onDebug?.({
      status: "error",
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
  options.onDebug?.({
    status: "raw_result",
    resultPreview: toTruncatedSingleLineForLog(result, 1000),
  });
  throwIfFactSetErrorResult(
    result,
    "Failed to insert slides from base64 with source formatting"
  );
  const failureReason = getBatchFailureReason(result);
  if (failureReason) {
    options.onDebug?.({
      status: "failure_reason",
      error: failureReason,
      resultPreview: toTruncatedSingleLineForLog(result, 1000),
    });
    throw new Error(failureReason);
  }

  const insertedSlides = extractInsertedSlidesFromBase64Result(result);
  options.onDebug?.({
    status: "success",
    insertedCount: insertedSlides.length,
    resultPreview: toTruncatedSingleLineForLog(result, 1000),
  });
  resetIntentBridgeCache({
    clearSlideInfos: true,
    clearActiveSlideId: true,
    clearSelectedSlideRef: true,
    clearPresentation: true,
  });
  return insertedSlides;
}

export async function insertSlidesFromBase64ViaIntents(options: {
  base64Slides: string;
  targetSlideId?: string;
  keepSourceFormatting?: boolean;
  log?: Logger;
  presentationRef?: Partial<FactSetPresentationRef> | null;
}): Promise<FactSetInsertedSlideRef[]> {
  const base64Slides = (options.base64Slides || "").trim();
  if (!base64Slides) {
    throw new Error("InsertSlidesFromBase64 requires a non-empty base64 payload.");
  }

  const context: Record<string, unknown> = {
    base64Slides,
  };
  const normalizedTargetSlideId = (options.targetSlideId || "").trim();
  if (normalizedTargetSlideId) {
    context.targetSlideId = normalizedTargetSlideId;
  }
  if (options.keepSourceFormatting) {
    context.formattingOptions = "KeepSourceFormatting";
  }

  let preInsertSlideInfos: SlideInfo[] = [];
  try {
    preInsertSlideInfos = await ensureSlideInfos(options.log, {
      forceRefresh: true,
      presentationRef: options.presentationRef,
    });
  } catch (slideListError) {
    logMessage(
      options.log,
      `Could not capture pre-insert slide list for InsertSlidesFromBase64: ${toErrorMessage(slideListError)}`
    );
  }

  try {
    const result = await sendIntentById("insertSlidesFromBase64", context, 30_000, {
      log: options.log,
      traceLabel: "insert-slides-from-base64",
      presentationRef: options.presentationRef,
    });
    const insertedSlides = filterInsertedSlideRefsToNewSlides(
      extractInsertedSlidesFromBase64Result(result),
      preInsertSlideInfos
    );
    const inferredInsertedSlides = await inferInsertedSlidesFromCurrentDelta(
      preInsertSlideInfos,
      options.log,
      options.presentationRef
    );

    const resolvedInsertedSlides = dedupeInsertedSlideRefs([
      ...insertedSlides,
      ...inferredInsertedSlides,
    ]);
    if (resolvedInsertedSlides.length === 0) {
      throw new Error("InsertSlidesFromBase64 returned no inserted slide references.");
    }

    resetIntentBridgeCache({
      clearSlideInfos: true,
      clearActiveSlideId: true,
      clearSelectedSlideRef: true,
      clearPresentation: true,
    });
    return resolvedInsertedSlides;
  } catch (insertError) {
    const inferredInsertedAfterError = await inferInsertedSlidesFromCurrentDelta(
      preInsertSlideInfos,
      options.log,
      options.presentationRef
    );
    if (inferredInsertedAfterError.length > 0) {
      logMessage(
        options.log,
        "InsertSlidesFromBase64 reported an error but slide-list delta indicates insertion succeeded."
      );
      resetIntentBridgeCache({
        clearSlideInfos: true,
        clearActiveSlideId: true,
        clearSelectedSlideRef: true,
        clearPresentation: true,
      });
      return inferredInsertedAfterError;
    }

    if (normalizedTargetSlideId) {
      logMessage(
        options.log,
        `InsertSlidesFromBase64 with targetSlideId failed (${toErrorMessage(insertError)}); ` +
          "retrying without targetSlideId."
      );
      try {
        const retryResult = await sendIntentById(
          "insertSlidesFromBase64",
          { base64Slides },
          30_000,
          {
            log: options.log,
            traceLabel: "insert-slides-from-base64-retry",
            presentationRef: options.presentationRef,
          }
        );
        const retryInsertedSlides = filterInsertedSlideRefsToNewSlides(
          extractInsertedSlidesFromBase64Result(retryResult),
          preInsertSlideInfos
        );
        const retryInferredSlides = await inferInsertedSlidesFromCurrentDelta(
          preInsertSlideInfos,
          options.log,
          options.presentationRef
        );
        const resolvedRetryInsertedSlides = dedupeInsertedSlideRefs([
          ...retryInsertedSlides,
          ...retryInferredSlides,
        ]);
        if (resolvedRetryInsertedSlides.length === 0) {
          throw new Error("InsertSlidesFromBase64 retry returned no inserted slide references.");
        }
        resetIntentBridgeCache({
          clearSlideInfos: true,
          clearActiveSlideId: true,
          clearSelectedSlideRef: true,
          clearPresentation: true,
        });
        return resolvedRetryInsertedSlides;
      } catch (retryError) {
        const inferredInsertedAfterRetryError = await inferInsertedSlidesFromCurrentDelta(
          preInsertSlideInfos,
          options.log,
          options.presentationRef
        );
        if (inferredInsertedAfterRetryError.length > 0) {
          logMessage(
            options.log,
            "InsertSlidesFromBase64 retry reported an error but slide-list delta indicates insertion succeeded."
          );
          resetIntentBridgeCache({
            clearSlideInfos: true,
            clearActiveSlideId: true,
            clearSelectedSlideRef: true,
            clearPresentation: true,
          });
          return inferredInsertedAfterRetryError;
        }
        throw retryError;
      }
    }
    throw insertError;
  }
}

export async function addSlideTagsViaIntents(input: {
  slideId: string;
  tags: Record<string, string>;
  log?: Logger;
  timeoutMs?: number;
  presentationRef?: Partial<FactSetPresentationRef> | null;
}): Promise<boolean> {
  const slideId = (input.slideId || "").trim();
  if (!slideId) {
    throw new Error("AddSlideTags requires a non-empty slideId.");
  }

  const normalizedTags: Record<string, string> = {};
  for (const [rawKey, rawValue] of Object.entries(input.tags ?? {})) {
    const key = rawKey.trim();
    if (!key) {
      continue;
    }
    normalizedTags[key] = String(rawValue ?? "");
  }
  if (Object.keys(normalizedTags).length === 0) {
    throw new Error("AddSlideTags requires at least one non-empty tag key.");
  }

  const result = await sendIntentById(
    "addSlideTags",
    { slideId, tags: normalizedTags },
    input.timeoutMs ?? 20_000,
    {
      log: input.log,
      traceLabel: "add-slide-tags",
      presentationRef: input.presentationRef,
    }
  );
  const failureReason = getBatchFailureReason(result);
  if (failureReason) {
    throw new Error(failureReason);
  }
  return true;
}

export async function activatePresentationViaIntents(input: {
  presentationRef: Partial<FactSetPresentationRef>;
  log?: Logger;
  timeoutMs?: number;
}): Promise<boolean> {
  const normalizedRef = normalizePresentationRef(input.presentationRef);
  if (!normalizedRef) {
    throw new Error(
      "ActivatePresentation requires a presentationRef with an openPresentationId."
    );
  }

  const result = await sendIntentById("activatePresentation", {}, input.timeoutMs ?? 10_000, {
    log: input.log,
    traceLabel: "activate-presentation",
    presentationRef: normalizedRef,
  });
  const failureReason = getBatchFailureReason(result);
  if (failureReason) {
    throw new Error(failureReason);
  }
  // Active presentation changed — drop any cached active-slide / selection /
  // presentation data so the next read re-fetches against the new active file.
  resetIntentBridgeCache({
    clearSlideInfos: true,
    clearActiveSlideId: true,
    clearSelectedSlideRef: true,
    clearPresentation: true,
  });
  return true;
}

function extractAddSlideResult(raw: unknown): { slideId: string } {
  if (!raw || typeof raw !== "object") {
    throw new Error("AddSlide returned an empty result.");
  }
  const obj = raw as Record<string, unknown>;
  const slideId = pickFirstNonEmptySlideId(
    obj.addedSlideId,
    obj.addedSlideID,
    obj.slideId,
    obj.slideID,
    obj.id,
    obj.insertedSlideId,
    obj.insertedSlideID
  );
  if (!slideId) {
    throw new Error("AddSlide did not return a slideId.");
  }
  return { slideId };
}

export async function addSlideViaIntents(input?: {
  targetSlideId?: string;
  layoutName?: string;
  slideMasterName?: string;
  log?: Logger;
  timeoutMs?: number;
  presentationRef?: Partial<FactSetPresentationRef> | null;
}): Promise<{ slideId: string }> {
  const context: Record<string, unknown> = {};
  const targetSlideId = pickFirstNonEmptySlideId(input?.targetSlideId);
  if (targetSlideId) {
    context.targetSlideId = targetSlideId;
  }
  const layoutName = pickFirstNonEmptyString(input?.layoutName);
  if (layoutName) {
    context.layoutName = layoutName;
  }
  const slideMasterName = pickFirstNonEmptyString(input?.slideMasterName);
  if (slideMasterName) {
    context.slideMasterName = slideMasterName;
  }

  const result = await sendIntentById("addSlide", context, input?.timeoutMs ?? 20_000, {
    log: input?.log,
    traceLabel: "add-slide",
    presentationRef: input?.presentationRef,
  });
  const addedSlide = extractAddSlideResult(result);
  resetIntentBridgeCache({
    clearSlideInfos: true,
    clearActiveSlideId: true,
    clearSelectedSlideRef: true,
    clearPresentation: true,
  });
  return addedSlide;
}

export async function streamSlideThumbnailsViaIntents(
  onThumbnail: (slide: { index: number; slideNumber?: number; thumbnail: string }) => void,
  options?: { height?: number; maxSlides?: number; log?: Logger }
): Promise<{ totalSlides: number; emittedSlides: number }> {
  const presentationRef = await getActivePresentationRefViaIntents(options?.log);
  const slideInfos = await ensureSlideInfos(options?.log, {
    forceRefresh: true,
    presentationRef,
  });
  const orderedSlides = slideInfos.slice().sort((left, right) => left.index - right.index);
  const maxSlides =
    typeof options?.maxSlides === "number" ? Math.max(0, Math.trunc(options.maxSlides)) : undefined;
  const totalSlides =
    typeof maxSlides === "number" ? Math.min(maxSlides, orderedSlides.length) : orderedSlides.length;

  if (totalSlides === 0) {
    return { totalSlides: 0, emittedSlides: 0 };
  }

  const { width, height } = getImageDimensions(options?.height ?? 220);
  const targetSlides = orderedSlides.slice(0, totalSlides);
  let emittedSlides = 0;
  for (let start = 0; start < targetSlides.length; start += SLIDE_IMAGE_BATCH_SIZE) {
    const batch = targetSlides.slice(start, start + SLIDE_IMAGE_BATCH_SIZE);
    try {
      const batchResult = await getSlideImagesAsBase64ViaIntents({
        slideIds: batch.map((slide) => slide.slideId),
        width,
        height,
        presentationRef,
        log: options?.log,
        traceLabel: "thumbnail-batch",
      });
      if (batchResult.errors.length > 0) {
        logMessage(
          options?.log,
          `Slide thumbnail batch returned ${batchResult.errors.length} host error(s) for slides ${batch[0]?.index ?? 0}-${batch[batch.length - 1]?.index ?? 0}.`
        );
      }

      const imagesBySlideId = new Map<string, FactSetSlideImageBatchItem>();
      for (const image of batchResult.images) {
        if (!image.slideId) {
          continue;
        }
        imagesBySlideId.set(normalizeSlideIdentity(image.slideId), image);
      }

      for (let batchIndex = 0; batchIndex < batch.length; batchIndex += 1) {
        const slideInfo = batch[batchIndex];
        const matchedById = imagesBySlideId.get(normalizeSlideIdentity(slideInfo.slideId));
        const positionalFallback =
          batchResult.images.length === batch.length ? batchResult.images[batchIndex] : undefined;
        const image = matchedById ?? positionalFallback;
        const imageBase64 = image?.imageBase64 ?? "";
        if (!imageBase64 || imageBase64.length <= 100) {
          logMessage(
            options?.log,
            `Slide thumbnail batch returned empty image for index ${slideInfo.index} (slideId=${summarizeSlideIdForLog(slideInfo.slideId)}).`
          );
          continue;
        }
        onThumbnail({
          index: slideInfo.index,
          ...(typeof slideInfo.slideNumber === "number" ? { slideNumber: slideInfo.slideNumber } : {}),
          thumbnail: imageBase64,
        });
        emittedSlides += 1;
      }
    } catch (error) {
      logMessage(
        options?.log,
        `Slide thumbnail batch intent failed for slides ${batch[0]?.index ?? 0}-${batch[batch.length - 1]?.index ?? 0}: ${toErrorMessage(error)}`
      );
    }
  }

  return { totalSlides, emittedSlides };
}

export async function addImageFromBase64ViaIntents(input: {
  dataUri: string;
  name: string;
  insertOptions?: {
    left?: number;
    top?: number;
    width?: number;
    height?: number;
  };
}): Promise<void> {
  const dataUri = input.dataUri.trim();
  if (!dataUri) {
    throw new Error("AddImageFromBase64 requires a non-empty data URI.");
  }

  const name = input.name.trim();
  if (!name) {
    throw new Error("AddImageFromBase64 requires a non-empty image name.");
  }

  const context: Record<string, unknown> = { name, dataUri };
  const insertOptions = input.insertOptions;
  if (insertOptions) {
    const normalizedInsertOptions: Record<string, number> = {};
    const left = readFiniteNumber(insertOptions.left);
    const top = readFiniteNumber(insertOptions.top);
    const width = readPositiveNumber(insertOptions.width);
    const height = readPositiveNumber(insertOptions.height);
    if (typeof left === "number") normalizedInsertOptions.left = left;
    if (typeof top === "number") normalizedInsertOptions.top = top;
    if (typeof width === "number") normalizedInsertOptions.width = width;
    if (typeof height === "number") normalizedInsertOptions.height = height;
    if (Object.keys(normalizedInsertOptions).length > 0) {
      context.insertOptions = normalizedInsertOptions;
    }
  }

  await sendIntentById("addImageFromBase64", context, 20_000);
  resetIntentBridgeCache({ clearPresentation: true });
}

export async function getSelectedSlideRefViaIntents(
  log?: Logger,
  options?: { presentationRef?: Partial<FactSetPresentationRef> | null }
): Promise<SlideRef> {
  const presentationRef = normalizePresentationRef(options?.presentationRef);
  const bypassSharedCache = Boolean(presentationRef);

  if (!bypassSharedCache) {
    const cachedSlideRef = getCachedSelectedSlideRef();
    if (cachedSlideRef) {
      return cachedSlideRef;
    }
    if (inFlightSelectedSlideRef) {
      return inFlightSelectedSlideRef;
    }
  }

  const resolvePromise = (async () => {
    // Fast path: GetActivePresentationContext already returns both the
    // active slide's id and zero-based index for every host we ship to
    // today. Skipping the parallel `ensureSlideInfos` call here removes
    // one GetSlideList round-trip for callers that only need the active
    // slide. useTabs polling now consumes getActivePresentationContextViaIntents
    // directly, so this path remains for on-demand selected-slide lookups.
    const contextRef = await captureActiveSlideRefFromContext(log);
    const contextPresentationRef = buildPresentationRefFromActiveContext(contextRef);
    const requestedOpenPresentationId = presentationRef?.openPresentationId?.toLowerCase();
    const contextOpenPresentationId = contextPresentationRef?.openPresentationId?.toLowerCase();
    const contextMatchesRequestedPresentation =
      !requestedOpenPresentationId ||
      (typeof contextOpenPresentationId === "string" &&
        contextOpenPresentationId === requestedOpenPresentationId);
    const effectivePresentationRef = presentationRef ?? contextPresentationRef;
    // M1 fix: stamp the SlideRef with the openPresentationId we queried
    // against so callers (the useTabs slide-selection poll) can drop the
    // result when the user has switched files between fetch and resolve.
    // Falsy on Office.js / non-FactSet hosts — consumers must treat it
    // as best-effort.
    const stampedOpenPresentationId = effectivePresentationRef?.openPresentationId;
    const withOpenId = (ref: SlideRef): SlideRef =>
      stampedOpenPresentationId
        ? { ...ref, openPresentationId: stampedOpenPresentationId }
        : ref;
    const cacheIfAllowed = (ref: SlideRef): SlideRef => {
      if (!bypassSharedCache) {
        setCachedSelectedSlideRef(ref);
      }
      return ref;
    };

    if (!contextMatchesRequestedPresentation) {
      logMessage(
        log,
        "Selected slide context belongs to another presentation; resolving via scoped slide list."
      );
    }

    if (contextMatchesRequestedPresentation && contextRef?.id && typeof contextRef.index === "number") {
      const cachedSlideInfos = bypassSharedCache ? null : getCachedSlideInfos();
      const cachedInfo = cachedSlideInfos?.find(
        (slide) => normalizeSlideIdentity(slide.slideId) === normalizeSlideIdentity(contextRef.id)
      );
      const directResolved = withOpenId({
        id: contextRef.id,
        index: cachedInfo?.index ?? contextRef.index,
        ...(typeof cachedInfo?.slideNumber === "number"
          ? { slideNumber: cachedInfo.slideNumber }
          : typeof contextRef.slideNumber === "number"
            ? { slideNumber: contextRef.slideNumber }
            : {}),
      });
      return cacheIfAllowed(directResolved);
    }

    // Slow path: active context is incomplete (no id, or no index, or
    // both), or the caller explicitly scoped this lookup to a different
    // presentation. Fall back to the slide-list-driven resolution ladder.
    const slideInfos = await ensureSlideInfos(log, {
      presentationRef: effectivePresentationRef,
    });

    if (contextMatchesRequestedPresentation && contextRef?.id) {
      const resolvedByContextId = resolveSlideRefFromInfos(slideInfos, contextRef.id);
      if (resolvedByContextId) {
        return cacheIfAllowed(withOpenId(resolvedByContextId));
      }
    }

    if (contextMatchesRequestedPresentation && typeof contextRef?.index === "number") {
      const resolvedByContextIndex =
        slideInfos.find((slide) => slide.index === contextRef.index) ?? slideInfos[contextRef.index];
      if (resolvedByContextIndex) {
        const resolved = withOpenId({
          id: resolvedByContextIndex.slideId,
          index: resolvedByContextIndex.index,
          ...(typeof resolvedByContextIndex.slideNumber === "number"
            ? { slideNumber: resolvedByContextIndex.slideNumber }
            : {}),
        });
        return cacheIfAllowed(resolved);
      }
      const resolvedBySelectedShapes = await resolveSlideRefFromSelectedShapesViaIntents(
        slideInfos,
        log
      );
      if (resolvedBySelectedShapes) {
        return cacheIfAllowed(withOpenId(resolvedBySelectedShapes));
      }
      const indexOnly = withOpenId({
        id: contextRef.id ?? "",
        index: contextRef.index,
        ...(typeof contextRef.slideNumber === "number" ? { slideNumber: contextRef.slideNumber } : {}),
      });
      return cacheIfAllowed(indexOnly);
    }

    const resolvedBySelectedShapes = contextMatchesRequestedPresentation
      ? await resolveSlideRefFromSelectedShapesViaIntents(slideInfos, log)
      : null;
    if (resolvedBySelectedShapes) {
      return cacheIfAllowed(withOpenId(resolvedBySelectedShapes));
    }

    const fallback = slideInfos[0];
    if (fallback) {
      const resolved = withOpenId({
        id: fallback.slideId,
        index: fallback.index,
        ...(typeof fallback.slideNumber === "number" ? { slideNumber: fallback.slideNumber } : {}),
      });
      return cacheIfAllowed(resolved);
    }

    return withOpenId({ id: "", index: 0 });
  })();

  if (bypassSharedCache) {
    return resolvePromise;
  }

  inFlightSelectedSlideRef = resolvePromise.finally(() => {
    inFlightSelectedSlideRef = null;
  });
  return inFlightSelectedSlideRef;
}

// Set the host's selected slides. Used as a defensive workaround for hosts
// where ExportPresentation ignores `slideIds` and falls back to the active
// slide — by pinning the active selection to the source slide first, the
// export returns the right content regardless of which path the host takes.
export async function setSelectedSlidesViaIntents(input: {
  slideIds: string[];
  log?: Logger;
  timeoutMs?: number;
  presentationRef?: Partial<FactSetPresentationRef> | null;
}): Promise<boolean> {
  const candidateIds = (input.slideIds ?? [])
    .map((id) => (id == null ? "" : String(id).trim()))
    .filter((id): id is string => id.length > 0);
  if (candidateIds.length === 0) {
    throw new Error("SetSelectedSlides requires at least one slide ID.");
  }

  const result = await sendIntentById(
    "setSelectedSlides",
    { slideIds: candidateIds },
    input.timeoutMs ?? 10_000,
    {
      log: input.log,
      traceLabel: "set-selected-slides",
      presentationRef: input.presentationRef,
    }
  );
  const failureReason = getBatchFailureReason(result);
  if (failureReason) {
    throw new Error(failureReason);
  }

  // Selection changed under the hood; drop the cached active/selected refs so
  // the next read goes back to the host instead of returning stale state.
  resetIntentBridgeCache({
    clearActiveSlideId: true,
    clearSelectedSlideRef: true,
  });
  return true;
}

export async function handleBatchPptOperations(
  operations: PresentationModification,
  options?: {
    log?: Logger;
    timeoutMs?: number;
    hardTimeoutMs?: number;
    presentationRef?: Partial<FactSetPresentationRef> | null;
    allowPartialOperationResults?: boolean;
  }
): Promise<unknown> {
  const softTimeoutMs = Math.max(1_000, options?.timeoutMs ?? DEFAULT_BATCH_TIMEOUT_MS);
  const hardTimeoutMs = Math.max(
    softTimeoutMs,
    Math.max(1_000, options?.hardTimeoutMs ?? softTimeoutMs + DEFAULT_BATCH_TIMEOUT_GRACE_MS)
  );
  const slideOpsCount = operations.slides?.length ?? 0;
  const shapeOpsCount =
    operations.slides?.reduce((sum, slide) => sum + (slide.shapes?.length || 0), 0) ?? 0;
  logMessage(
    options?.log,
    `Performing batch operations: slides=${slideOpsCount}, shapes=${shapeOpsCount}, ` +
      `timeout=${softTimeoutMs}ms (hard=${hardTimeoutMs}ms)`
  );
  const intentDef = getIntentDef("performBatch");
  const parentOrigin = getBridgeParentOriginOrThrow();
  const respondAt = generateRespondAt();
  const startedAt = nowMs();
  const operationsJson = JSON.stringify(operations);
  let softTimeoutElapsed = false;
  const softTimeoutTimer = window.setTimeout(() => {
    softTimeoutElapsed = true;
    logMessage(
      options?.log,
      `Batch operations still running after ${softTimeoutMs}ms; ` +
        `continuing to wait up to ${hardTimeoutMs}ms before failing`
    );
  }, softTimeoutMs);
  const resultPromise = waitForIntentResponse(
    respondAt,
    intentDef.intentName,
    hardTimeoutMs,
    parentOrigin
  );

  postIntentMessage(
    respondAt,
    intentDef.intentName,
    withPresentationRef(
      "performBatch",
      {
        type: intentDef.contextType,
        json: operationsJson,
      },
      options?.presentationRef
    ),
    parentOrigin,
    POWERPOINT_INTENT_APP_ID
  );

  let result: unknown;
  try {
    result = await resultPromise;
  } catch (error) {
    const elapsedMs = nowMs() - startedAt;
    if (isIntentTimeoutError(error)) {
      logMessage(
        options?.log,
        `Batch operations timed out after ${elapsedMs}ms ` +
          `(soft=${softTimeoutMs}ms, hard=${hardTimeoutMs}ms)`
      );
    }
    throw error;
  } finally {
    window.clearTimeout(softTimeoutTimer);
  }

  const elapsedMs = nowMs() - startedAt;
  if (softTimeoutElapsed) {
    logMessage(
      options?.log,
      `Batch operations completed after soft timeout (elapsed=${elapsedMs}ms).`
    );
  }
  const failureReason = getBatchFailureReason(result);
  if (failureReason) {
    if (options?.allowPartialOperationResults && hasBatchOperationResults(result)) {
      logMessage(
        options?.log,
        `Batch operation returned partial per-operation results: ${failureReason}`
      );
      return result;
    }
    logMessage(options?.log, `Batch operation failure: ${failureReason}`);
    const diagnostics = extractBatchFailureDiagnostics(result, 4).filter(
      (diagnostic) => diagnostic !== failureReason
    );
    if (diagnostics.length > 0) {
      logMessage(options?.log, `Batch operation diagnostics: ${diagnostics.join(" | ")}`);
    }
    logMessage(options?.log, `Batch operation raw result: ${summarizeBatchResultForLog(result)}`);
    throw new Error(failureReason);
  }
  logMessage(options?.log, "Batch operations completed successfully.");
  return result;
}

export interface AllocateBlankSlidesInput {
  count: number;
  presentationRef?: Partial<FactSetPresentationRef> | null;
  // Optional planIndex -> baseSlideIndex map for duplicate-from-template mode.
  baseSlideIndexByPlanIndex?: Map<number, number>;
  // Optional planIndex -> custom slide tags merged onto default Finster tags.
  slideTagsByPlanIndex?: Map<number, Record<string, string>>;
}

export interface AllocateBlankSlidesResult {
  planIndexToSlideId: Map<number, string>;
  duplicatedPositions: Set<number>;
  // TODO(bucket-B-cleanup): switch this contract to `postSlides: FactSetSlideInfo[]`
  // and delete the synthetic-PresentationContent shim below. No external caller
  // currently reads any field on `postCreationContent` other than (transitively)
  // .slides[].id for the id-diff, which we already compute internally.
  postCreationContent: PresentationContent;
  allocated: number;
}

// Build a minimal PresentationContent stand-in from a slide-id list. Used by
// allocation helpers that historically returned the full extractor payload but
// only ever needed slide id/index for downstream id-diff logic. Shapes are
// intentionally empty and dimensions intentionally omitted — any caller that
// reads them on this synthesised return value should re-extract instead.
// TODO(bucket-B-cleanup): replace this with a `postSlides: FactSetSlideInfo[]`
// contract on the allocation helpers' return type.
export function synthesizePresentationContentFromSlideInfos(
  slideInfos: { slideId: string; index: number; slideNumber?: number }[]
): PresentationContent {
  return {
    slides: slideInfos.map((info) => ({
      id: info.slideId,
      index: info.index,
      ...(typeof info.slideNumber === "number" ? { slideNumber: info.slideNumber } : {}),
      shapes: [],
    })),
    selectedSlideIndex: 0,
  };
}

// Use AddSlide for true blank slides. Keep the duplicate-and-clear fallback only
// when callers explicitly request template duplication via baseSlideIndexByPlanIndex.
export async function allocateBlankSlidesViaIntents(
  input: AllocateBlankSlidesInput,
  log?: Logger
): Promise<AllocateBlankSlidesResult> {
  const count = Math.max(0, Math.floor(input.count));
  const presentationRef = normalizePresentationRef(input.presentationRef);
  if (count === 0) {
    // No allocation work performed; the caller only ever reads
    // `planIndexToSlideId` from the result, so synthesise an empty
    // PresentationContent shim instead of issuing a full extractor trip.
    const postSlides = await listSlidesViaIntents(log, { presentationRef });
    return {
      planIndexToSlideId: new Map(),
      duplicatedPositions: new Set(),
      postCreationContent: synthesizePresentationContentFromSlideInfos(postSlides),
      allocated: 0,
    };
  }

  const preInfos = await ensureSlideInfos(log, { forceRefresh: true, presentationRef });
  const baseMap = input.baseSlideIndexByPlanIndex;
  const hasAnyValidDuplicateRequest = Array.from({ length: count }, (_, planIndex) => {
    const baseIndex = baseMap?.get(planIndex);
    return typeof baseIndex === "number" && baseIndex >= 0 && baseIndex < preInfos.length;
  }).some(Boolean);

  if (!hasAnyValidDuplicateRequest) {
    const orderedPreInfos = preInfos.slice().sort((left, right) => left.index - right.index);
    let targetSlideId = orderedPreInfos[orderedPreInfos.length - 1]?.slideId;
    const planIndexToSlideId = new Map<number, string>();
    const duplicatedPositions = new Set<number>();

    logMessage(log, `Allocating ${count} blank slide(s) via AddSlide intent...`);
    await runWithinUndoEntry(async () => {
      for (let planIndex = 0; planIndex < count; planIndex += 1) {
        const created = await addSlideViaIntents({
          targetSlideId,
          log,
          layoutName: "Blank",
          presentationRef,
        });
        planIndexToSlideId.set(planIndex, created.slideId);
        targetSlideId = created.slideId;
      }
    }, log, presentationRef);

    const taggedSlides = Array.from(planIndexToSlideId.entries());
    for (let taggedSlideIndex = 0; taggedSlideIndex < taggedSlides.length; taggedSlideIndex += 1) {
      const [planIndex, slideId] = taggedSlides[taggedSlideIndex];
      const defaultTags: Record<string, string> = {
        "finster.source": "blank-slide-allocation",
        "finster.planIndex": String(planIndex),
        "finster.allocationMode": "blank",
        "finster.tagVersion": "1",
      };
      const customTags = input.slideTagsByPlanIndex?.get(planIndex) ?? {};
      const tags = { ...defaultTags, ...customTags };
      try {
        await addSlideTagsViaIntents({
          slideId,
          tags,
          log,
          presentationRef,
        });
      } catch (tagError) {
        logMessage(
          log,
          `AddSlideTags failed for allocated slide ${summarizeSlideIdForLog(slideId)} ` +
            `(planIndex=${planIndex}, continuing): ${toErrorMessage(tagError)}`
        );
      }
    }

    // Post-allocation only needs slide ids/indexes for downstream id-diff;
    // skip the full extractor trip and synthesise the legacy contract.
    const postSlides = await listSlidesViaIntents(log, { forceRefresh: true, presentationRef });
    return {
      planIndexToSlideId,
      duplicatedPositions,
      postCreationContent: synthesizePresentationContentFromSlideInfos(postSlides),
      allocated: planIndexToSlideId.size,
    };
  }

  const preIds = new Set(preInfos.map((info) => info.slideId));
  const defaultSourceInfo = preInfos[preInfos.length - 1];

  const duplicatedPositions = new Set<number>();

  const duplicateOps: SlideModification[] = [];
  for (let planIndex = 0; planIndex < count; planIndex++) {
    const baseIndex = baseMap?.get(planIndex);
    const hasValidBase =
      typeof baseIndex === "number" && baseIndex >= 0 && baseIndex < preInfos.length;
    const source = hasValidBase ? preInfos[baseIndex as number] : defaultSourceInfo;
    if (hasValidBase) duplicatedPositions.add(planIndex);
    duplicateOps.push({
      id: source.slideId,
      index: source.index,
      action: "duplicate",
      shapes: [],
    });
  }

  logMessage(log, `Allocating ${count} slide(s) via duplicate batch...`);
  await runWithinUndoEntry(async () => {
    await handleBatchPptOperations({ slides: duplicateOps }, { log, presentationRef });
  }, log, presentationRef);

  resetIntentBridgeCache({
    clearSlideInfos: true,
    clearActiveSlideId: true,
    clearSelectedSlideRef: true,
    clearPresentation: true,
  });

  // Post-duplicate batch first pass: id-diff via the cheap slide-list intent
  // (full extractor was overkill — duplicate-batch only needs IDs to map
  // plan indexes to new slide IDs). A second, scoped extractor trip below
  // pulls shape data for the blank-allocated slides only, when needed.
  const postSlides = await listSlidesViaIntents(log, { forceRefresh: true, presentationRef });
  const newSlides = postSlides.filter((slide) => !preIds.has(slide.slideId));
  if (newSlides.length < count) {
    logMessage(
      log,
      `Allocation under-produced: expected ${count}, observed ${newSlides.length} new slide(s).`
    );
  }

  const planIndexToSlideId = new Map<number, string>();
  for (let planIndex = 0; planIndex < count; planIndex++) {
    const created = newSlides[planIndex];
    if (created) {
      planIndexToSlideId.set(planIndex, created.slideId);
    }
  }

  const taggedSlides = Array.from(planIndexToSlideId.entries());
  for (let taggedSlideIndex = 0; taggedSlideIndex < taggedSlides.length; taggedSlideIndex += 1) {
    const [planIndex, slideId] = taggedSlides[taggedSlideIndex];
    const defaultTags: Record<string, string> = {
      "finster.source": "blank-slide-allocation",
      "finster.planIndex": String(planIndex),
      "finster.allocationMode": duplicatedPositions.has(planIndex) ? "duplicate" : "blank",
      "finster.tagVersion": "1",
    };
    const customTags = input.slideTagsByPlanIndex?.get(planIndex) ?? {};
    const tags = { ...defaultTags, ...customTags };
    try {
      await addSlideTagsViaIntents({
        slideId,
        tags,
        log,
        presentationRef,
      });
    } catch (tagError) {
      logMessage(
        log,
        `AddSlideTags failed for allocated slide ${summarizeSlideIdForLog(slideId)} ` +
          `(planIndex=${planIndex}, continuing): ${toErrorMessage(tagError)}`
      );
    }
  }

  // Identify the blank-allocated slides (non-duplicated positions) that
  // still need placeholder-shape removal. If none, skip the scoped extract
  // entirely. When some exist, pull shapes for *only* those slides instead
  // of re-extracting the whole deck.
  const blankAllocatedSlideIds: string[] = [];
  const blankAllocatedSlideRefs: Array<{ slideId: string; index: number }> = [];
  for (let planIndex = 0; planIndex < count; planIndex++) {
    if (duplicatedPositions.has(planIndex)) continue;
    const slideId = planIndexToSlideId.get(planIndex);
    if (!slideId) continue;
    blankAllocatedSlideIds.push(slideId);
    const match = newSlides.find((slide) => slide.slideId === slideId);
    if (match) {
      blankAllocatedSlideRefs.push({ slideId, index: match.index });
    }
  }

  const clearOps: SlideModification[] = [];
  if (blankAllocatedSlideIds.length > 0) {
    const scopedShapesContent = await extractPresentationContentViaIntents(log, {
      forceRefresh: true,
      presentationRef,
      includeSlideRanges: blankAllocatedSlideIds.map((id) => ({ id })),
    });
    const shapesBySlideId = new Map<
      string,
      { index: number; shapes: Array<{ id?: string }> }
    >();
    for (const slide of scopedShapesContent.slides) {
      shapesBySlideId.set(slide.id, { index: slide.index, shapes: slide.shapes || [] });
    }
    for (const ref of blankAllocatedSlideRefs) {
      const entry = shapesBySlideId.get(ref.slideId);
      if (!entry) continue;
      const deleteShapes = entry.shapes
        .filter((shape) => typeof shape.id === "string" && shape.id.length > 0)
        .map((shape) => ({ id: shape.id as string, action: "delete" as const }));
      if (deleteShapes.length === 0) continue;
      clearOps.push({
        id: ref.slideId,
        index: entry.index,
        action: "modify",
        shapes: deleteShapes,
      });
    }
  }

  if (clearOps.length > 0) {
    try {
      logMessage(
        log,
        `Clearing placeholder shapes on ${clearOps.length} blank slide(s)...`
      );
      await handleBatchPptOperations({ slides: clearOps }, { log, presentationRef });
      // Shape deletions require invalidating presentation cache.
      resetIntentBridgeCache({ clearPresentation: true });
    } catch (e) {
      logMessage(
        log,
        `Could not clear placeholder shapes (continuing): ${toErrorMessage(e)}`
      );
    }
  }

  return {
    planIndexToSlideId,
    duplicatedPositions,
    postCreationContent: synthesizePresentationContentFromSlideInfos(postSlides),
    allocated: planIndexToSlideId.size,
  };
}

export async function createBlankSlideAtEndViaIntents(
  log?: Logger,
  options?: { presentationRef?: Partial<FactSetPresentationRef> | null }
): Promise<string | null> {
  const result = await allocateBlankSlidesViaIntents(
    { count: 1, presentationRef: options?.presentationRef },
    log
  );
  return result.planIndexToSlideId.get(0) ?? null;
}

async function beginUndoEntry(
  log?: Logger,
  presentationRef?: Partial<FactSetPresentationRef> | null
): Promise<boolean> {
  try {
    await sendIntentById("beginUndo", {}, 10_000, {
      log,
      traceLabel: "undo-begin",
      presentationRef,
    });
    return true;
  } catch (undoError) {
    logMessage(log, `BeginUndo failed (continuing): ${toErrorMessage(undoError)}`);
    return false;
  }
}

async function endUndoEntry(
  log?: Logger,
  presentationRef?: Partial<FactSetPresentationRef> | null,
  undoEntryText: string = DEFAULT_UNDO_ENTRY_TEXT
): Promise<void> {
  const label = (undoEntryText || "").trim() || DEFAULT_UNDO_ENTRY_TEXT;
  try {
    await sendIntentById("endUndo", { undoEntryText: label }, 10_000, {
      log,
      traceLabel: "undo-end",
      presentationRef,
    });
  } catch (undoError) {
    logMessage(log, `EndUndo failed: ${toErrorMessage(undoError)}`);
  }
}

// Reentrancy counter for nested operations with an active undo entry.
let undoEntryDepth = 0;

async function runWithinUndoEntry(
  work: () => Promise<void>,
  log?: Logger,
  presentationRef?: Partial<FactSetPresentationRef> | null
): Promise<void> {
  if (undoEntryDepth > 0) {
    await work();
    return;
  }
  const undoStarted = await beginUndoEntry(log, presentationRef);
  if (undoStarted) {
    undoEntryDepth += 1;
  }
  try {
    await work();
  } finally {
    if (undoStarted) {
      undoEntryDepth = Math.max(0, undoEntryDepth - 1);
      await endUndoEntry(log, presentationRef);
    }
  }
}

// Opens a long-running undo scope; caller must close the returned function.
// `undoEntryText` is the label that will appear in PowerPoint's undo history
// when the scope closes. Defaults to "FactSet AI Chat Apply" for back-compat.
export async function beginUndoScopeViaIntents(
  log?: Logger,
  options?: {
    presentationRef?: Partial<FactSetPresentationRef> | null;
    undoEntryText?: string;
  }
): Promise<() => Promise<void>> {
  const undoStarted = await beginUndoEntry(log, options?.presentationRef);
  if (undoStarted) {
    undoEntryDepth += 1;
  }
  let closed = false;
  return async () => {
    if (closed) return;
    closed = true;
    if (undoStarted) {
      undoEntryDepth = Math.max(0, undoEntryDepth - 1);
      await endUndoEntry(log, options?.presentationRef, options?.undoEntryText);
    }
  };
}

function hasStructuralSlideMutations(modifications: PresentationModification): boolean {
  return (modifications.slides || []).some((slide) => {
    const action = slide.action ?? "modify";
    return action === "duplicate" || action === "delete";
  });
}

export interface IntentApplyModificationsOptions {
  preferredBounds?: SlideBounds;
  onPreparedBatch?: (operations: PresentationModification) => void | Promise<void>;
  presentationRef?: Partial<FactSetPresentationRef> | null;
}

export async function applyModificationsViaIntents(
  modifications: PresentationModification,
  log?: Logger,
  options?: IntentApplyModificationsOptions
): Promise<ApplyResultSummary> {
  const hydratedModifications = await hydrateImagePayloadsForModifications(modifications, log);
  const summary = createSummary(hydratedModifications);
  logMessage(
    log,
    `applyModificationsViaIntents start: requestedSlides=${summary.requested.slides}, ` +
      `requestedShapes=${summary.requested.shapes}, diagnostics=${(hydratedModifications.diagnostics || []).length}`
  );
  for (const diagnostic of hydratedModifications.diagnostics || []) {
    recordSkipped(summary, diagnostic);
  }

  if (!hydratedModifications.slides || hydratedModifications.slides.length === 0) {
    if ((hydratedModifications.diagnostics || []).length === 0) {
      recordSkipped(summary, {
        code: "NO_MODIFICATIONS_RETURNED",
        category: "no_op",
        message: "Model returned no slide operations to apply.",
      });
    }
    logMessage(log, "No slide operations returned for intent apply.");
    summary.classification = classifyApplyResult(summary);
    return summary;
  }

  const hasStructuralMutations = hasStructuralSlideMutations(hydratedModifications);
  logMessage(log, `Structural slide mutations present: ${hasStructuralMutations}`);
  const slideInfos = await ensureSlideInfos(log, {
    forceRefresh: hasStructuralMutations,
    presentationRef: options?.presentationRef,
  });
  logMessage(log, `Slide infos loaded for apply: ${slideInfos.length}`);
  const bounds = await resolveSlideBoundsDimensions(log, {
    preferredBounds: options?.preferredBounds,
  });
  logMessage(
    log,
    `Applying with bounds ${bounds.slideWidth}x${bounds.slideHeight}pt ` +
      `(source=${lastSlideBoundsResolutionSource ?? "unknown"})`
  );
  const preparedBatch = prepareBatchOperations(hydratedModifications, slideInfos, bounds, log);
  if (preparedBatch.issues.length > 0) {
    logMessage(log, `Batch preparation reported ${preparedBatch.issues.length} issue(s).`);
  }
  for (const issue of preparedBatch.issues) {
    recordSkipped(summary, issue);
  }
  if (options?.onPreparedBatch) {
    try {
      await options.onPreparedBatch(preparedBatch.operations);
    } catch (callbackError) {
      logMessage(log, `Prepared batch callback failed (continuing): ${toErrorMessage(callbackError)}`);
    }
  }

  if (preparedBatch.operations.slides.length === 0) {
    logMessage(log, "No valid operations remained after intent batch preparation.");
    summary.classification = classifyApplyResult(summary);
    return summary;
  }

  try {
    logMessage(
      log,
      `Executing prepared batch: slides=${preparedBatch.operations.slides.length}`
    );
    await runWithinUndoEntry(async () => {
      const batchResult = await handleBatchPptOperations(preparedBatch.operations, {
        log,
        presentationRef: options?.presentationRef,
        allowPartialOperationResults: true,
      });
      const accountedFromResult = markAppliedSummaryFromBatchResult(
        summary,
        batchResult,
        preparedBatch.operations.slides
      );
      if (!accountedFromResult) {
        markAppliedSummaryFromSlides(summary, preparedBatch.operations.slides);
      }
    }, log, options?.presentationRef);
    logMessage(log, "Applied batch operations.");
  } catch (applyError) {
    const applyErrorMessage = toErrorMessage(applyError);
    logMessage(log, `PerformBatchPptOperations failed: ${applyErrorMessage}`);
    recordError(summary, {
      code: "SHAPE_MODIFY_FAILED",
      category: "apply_error",
      message: `PerformBatchPptOperations failed: ${applyErrorMessage}`,
      action: "modify",
    });
  }

  resetIntentBridgeCache({
    clearPresentation: true,
    clearSelectedSlideRef: true,
    clearSlideInfos: hasStructuralMutations,
    clearActiveSlideId: hasStructuralMutations,
  });
  summary.classification = classifyApplyResult(summary);
  logMessage(
    log,
    `applyModificationsViaIntents complete: classification=${summary.classification}, ` +
      `appliedSlides=${summary.applied.slides}, appliedShapes=${summary.applied.shapes}, ` +
      `skipped=${summary.skipped.count}, errors=${summary.errors.count}`
  );
  return summary;
}

